import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Divider,
  Tab,
  Tabs,
  Typography,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import ViewQuiltRoundedIcon from "@mui/icons-material/ViewQuiltRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import ContactMailRoundedIcon from "@mui/icons-material/ContactMailRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import InstanceLeftSidebar from "../InstanceLeftSidebar";
import PageHeaderWithBreadcrumbs from "../../../components/PageHeaderWithBreadcrumbs";
import InstanceProducts from "../connect/products/InstanceProducts";
import PresentationTabs from "../../../components/PresentationTabs";
import useShowcaseProductPage, { rejectShowcaseReplacementSave } from "./promoshowcase/useShowcaseProductPage";
import ShowcaseProductPagination from "./promoshowcase/ShowcaseProductPagination";
import useShowcaseProductSync from "./promoshowcase/useShowcaseProductSync";
import ShowcaseProductSyncStatus from "./promoshowcase/ShowcaseProductSyncStatus";
import PromoShowcaseBuilderIntegrated from "./promoshowcase/PromoShowcaseBuilderIntegrated";
import PromoShowcaseProductPicker from "./promoshowcase/PromoShowcaseProductPicker";
import PromoShowcaseLaunchWizard from "./promoshowcase/PromoShowcaseLaunchWizard";
import PromoShowcaseAddonPreview from "./promoshowcase/PromoShowcaseAddonPreview";
import ShowcaseModeChrome from "./promoshowcase/ShowcaseModeChrome";
import { applyLegacyPageTemplateToLayout } from "./promoshowcase/foundation/legacyPageTemplateAdapter";
import { cloneBuilderValue, persistBuilderDraft, shouldHydrateSelectedTemplate, shouldUsePublishedTemplateState, upsertTemplate } from "./promoshowcase/builderDraftPersistence";
import { getPublishPreflightErrors, getPublishPreflightMessage, publishBuilderDocument } from "./promoshowcase/builderPublish";
import { persistSelectedTemplateId, readSelectedTemplateId, resolveTemplateSelection } from "./promoshowcase/templateSelectionPersistence";
import { buildPreviewProductGridHtml, buildPreviewTaxonomyItems } from "./promoshowcase/templateProductPreview";
import { toast } from "react-toastify";
import "../dashboard/PromoShowcasePage.css";

const defaultSettings = {
  instanceName: '',
  promoShowcaseEnabled: false,
  promoShowcaseOutputTarget: "wordpress",
  promoShowcaseUrl: "",
  promoShowcaseHosting: null,
  promoShowcaseConnected: false,
  promoShowcaseConnectedAt: null,
  promoShowcaseLastError: null,
  requestDesignerLabel: "Request designer",
  designerTeamUserIds: [],
  templateSyncState: { status: 'unknown', source: null, message: null, updatedAt: null, notes: [] },
  templateCapabilities: null,
  productMerchandising: { categories: [], brands: [], themes: [], suppliers: [] },
  activeTemplate: null,
  templateState: null,
  canonicalDocument: null,
  templateHistory: [],
};


const DEFAULT_WIDGET_STYLE_PROPS = {
  margin_top: 0,
  margin_right: '',
  margin_bottom: 14,
  margin_left: '',
  padding_top: 0,
  padding_right: 0,
  padding_bottom: 0,
  padding_left: 0,
  background_color: '#ffffff',

  // Legacy shorthand values remain for backward compatibility.
  border_width: 1,
  border_style: 'solid',
  border_color: '#e2e8f0',
  border_radius: 14,

  // Builder-authoritative per-side border controls.
  border_top_width: 1,
  border_right_width: 1,
  border_bottom_width: 1,
  border_left_width: 1,
  border_top_style: 'solid',
  border_right_style: 'solid',
  border_bottom_style: 'solid',
  border_left_style: 'solid',
  border_top_color: '#e2e8f0',
  border_right_color: '#e2e8f0',
  border_bottom_color: '#e2e8f0',
  border_left_color: '#e2e8f0',

  // Builder-authoritative per-corner radius controls.
  border_top_left_radius: 14,
  border_top_right_radius: 14,
  border_bottom_right_radius: 14,
  border_bottom_left_radius: 14,
};

const withDefaultWidgetStyle = (props = {}) => ({
  ...DEFAULT_WIDGET_STYLE_PROPS,
  ...(props && typeof props === 'object' ? props : {}),
});

const WIDGET_LIBRARY = [
  {
    key: 'hero',
    label: 'Banner',
    defaultProps: {
      title: 'Welcome to our store',
      subtitle: 'Discover branded products fast.',
      min_height: 460,
      width_mode: 'full_bleed',
      autoplay: true,
      autoplay_ms: 6500,
      slides: [
        {
          background_url: '',
          h1: 'Welcome to our store',
          paragraph: 'Discover branded products fast.',
          button_text: 'Shop now',
          button_url: '/shop',
        },
      ],
    },
  },
  { key: 'featured_categories', label: 'Featured Categories', defaultProps: { title: 'Featured categories', limit: 6 } },
  {
    key: 'category_grid',
    label: 'Category Grid',
    defaultProps: {
      heading: 'Browse categories',
      taxonomy: 'product_category',
      limit: 24,
      columns_desktop: 8,
      columns_tablet: 4,
      columns_mobile: 2,
      source: 'archive',
    },
  },
  {
    key: 'brand_grid',
    label: 'Brand Grid',
    defaultProps: {
      heading: 'Shop by brand',
      taxonomy: 'brand',
      limit: 24,
      columns_desktop: 8,
      columns_tablet: 4,
      columns_mobile: 2,
      source: 'archive',
    },
  },
  {
    key: 'theme_grid',
    label: 'Theme Grid',
    defaultProps: {
      heading: 'Shop by theme',
      taxonomy: 'theme',
      limit: 24,
      columns_desktop: 8,
      columns_tablet: 4,
      columns_mobile: 2,
      source: 'archive',
    },
  },
  {
    key: 'product_grid',
    label: 'Product Grid',
    defaultProps: {
      title: 'Popular products',
      page_size: 24,
      columns_desktop: 4,
      columns_tablet: 3,
      columns_mobile: 2,
      query_source: 'all',
      query_supplier: '',
      query_search: '',
      query_product_ids: '',
      query_sort: 'default',
    },
  },
  {
    key: 'product_carousel',
    label: 'Product Carousel',
    defaultProps: {
      title: 'Featured products',
      page_size: 12,
      columns_desktop: 4,
      columns_tablet: 3,
      columns_mobile: 1,
      query_source: 'all',
      query_supplier: '',
      query_search: '',
      query_product_ids: '',
      query_sort: 'default',
    },
  },
  {
    key: 'rich_text',
    label: 'Content + Media',
    defaultProps: {
      subheading: '',
      title: 'Section title',
      body: 'Add your message here.',
      cta_label: '',
      cta_url: '',
      image_url: '',
      image_alt: '',
      media_position: 'right',
      vertical_align: 'center',
      content_width: 50,
      gap: 48,
      image_radius: 18,
      image_shadow: 'medium',
      image_aspect: '4/3',
    },
  },
  { key: 'button_row', label: 'Button Row', defaultProps: { label: 'Shop now', url: '/shop' } },
  { key: 'contact_block', label: 'Contact Section', defaultProps: { cta: 'Need help? Contact us.' } },
  {
    key: 'cards_grid',
    label: 'Cards Grid',
    defaultProps: {
      subheading: 'What we do',
      title: 'Services built around you',
      description: '',
      variant: 'services',
      columns_desktop: 3,
      columns_tablet: 2,
      columns_mobile: 1,
      card_style: 'elevated',
      items: [
        { icon: '01', title: 'Strategy', body: 'Plan the right approach for your goals.', link_label: '', link_url: '' },
        { icon: '02', title: 'Execution', body: 'Turn the plan into polished, practical work.', link_label: '', link_url: '' },
        { icon: '03', title: 'Support', body: 'Keep improving with a team that stays involved.', link_label: '', link_url: '' },
      ],
    },
  },
  {
    key: 'stats',
    label: 'Stats',
    defaultProps: {
      subheading: '',
      title: 'Results at a glance',
      columns_desktop: 4,
      columns_tablet: 2,
      columns_mobile: 2,
      items: [
        { value: '10K+', label: 'Projects delivered' },
        { value: '98%', label: 'Client satisfaction' },
        { value: '24/7', label: 'Online access' },
        { value: '1', label: 'Connected platform' },
      ],
    },
  },
  {
    key: 'testimonials',
    label: 'Testimonials',
    defaultProps: {
      subheading: 'Customer stories',
      title: 'Trusted by teams like yours',
      columns_desktop: 3,
      columns_tablet: 2,
      columns_mobile: 1,
      items: [
        { quote: 'Add a customer quote that explains the real outcome.', name: 'Customer name', role: 'Company or role', image_url: '', rating: 5 },
        { quote: 'Use social proof to reinforce the value of the offer.', name: 'Customer name', role: 'Company or role', image_url: '', rating: 5 },
        { quote: 'Keep testimonials concise, specific and easy to scan.', name: 'Customer name', role: 'Company or role', image_url: '', rating: 5 },
      ],
    },
  },
  {
    key: 'faq',
    label: 'FAQ',
    defaultProps: {
      subheading: 'FAQ',
      title: 'Frequently asked questions',
      style: 'joined',
      allow_multiple: false,
      open_first: true,
      items: [
        { question: 'How does this work?', answer: 'Add a clear answer here.' },
        { question: 'Can we customize it?', answer: 'Yes. Keep the response practical and concise.' },
        { question: 'What happens next?', answer: 'Explain the next step or call to action.' },
      ],
    },
  },
  {
    key: 'logo_cloud',
    label: 'Logo Cloud',
    defaultProps: {
      subheading: '',
      title: 'Trusted by leading teams',
      columns_desktop: 5,
      columns_tablet: 3,
      columns_mobile: 2,
      grayscale: true,
      items: [
        { name: 'Brand one', image_url: '', link_url: '' },
        { name: 'Brand two', image_url: '', link_url: '' },
        { name: 'Brand three', image_url: '', link_url: '' },
        { name: 'Brand four', image_url: '', link_url: '' },
        { name: 'Brand five', image_url: '', link_url: '' },
      ],
    },
  },
  {
    key: 'cta',
    label: 'CTA Section',
    defaultProps: {
      subheading: '',
      title: 'Ready to get started?',
      body: 'Use a focused call to action to move visitors to the next step.',
      primary_label: 'Get started',
      primary_url: '/contact',
      secondary_label: '',
      secondary_url: '',
      align: 'center',
      style: 'panel',
    },
  },
  {
    key: 'gallery',
    label: 'Gallery / Lookbook',
    defaultProps: {
      subheading: 'Inspiration',
      title: 'See what is possible',
      description: '',
      layout: 'grid',
      columns_desktop: 3,
      columns_tablet: 2,
      columns_mobile: 1,
      gap: 16,
      image_radius: 16,
      lightbox: true,
      items: [
        { image_url: '', alt: 'Project example one', caption: 'Campaign merchandise', link_url: '' },
        { image_url: '', alt: 'Project example two', caption: 'Branded apparel', link_url: '' },
        { image_url: '', alt: 'Project example three', caption: 'Event experience', link_url: '' },
      ],
    },
  },
  {
    key: 'video',
    label: 'Video / Media',
    defaultProps: {
      subheading: '',
      title: 'Show the story in motion',
      body: '',
      video_url: '',
      poster_url: '',
      caption: '',
      media_position: 'right',
      content_width: 44,
      gap: 48,
      radius: 18,
      shadow: 'medium',
      autoplay: false,
      muted: false,
      controls: true,
    },
  },
  {
    key: 'team',
    label: 'Team / People',
    defaultProps: {
      subheading: 'Your team',
      title: 'Meet the people behind the work',
      description: '',
      columns_desktop: 3,
      columns_tablet: 2,
      columns_mobile: 1,
      card_style: 'minimal',
      items: [
        { name: 'Team member', role: 'Account Manager', bio: 'Add a short, useful introduction.', image_url: '', email: '', linkedin_url: '' },
        { name: 'Team member', role: 'Creative', bio: 'Add a short, useful introduction.', image_url: '', email: '', linkedin_url: '' },
        { name: 'Team member', role: 'Production', bio: 'Add a short, useful introduction.', image_url: '', email: '', linkedin_url: '' },
      ],
    },
  },
  {
    key: 'timeline',
    label: 'Timeline / Milestones',
    defaultProps: {
      subheading: 'How it comes together',
      title: 'A clear path from idea to delivery',
      description: '',
      style: 'vertical',
      items: [
        { label: '01', title: 'Discover', body: 'Define the audience, objective, budget and timeline.' },
        { label: '02', title: 'Create', body: 'Curate products and prepare the creative direction.' },
        { label: '03', title: 'Deliver', body: 'Coordinate production, fulfillment and final delivery.' },
      ],
    },
  },
  {
    key: 'comparison',
    label: 'Comparison Table',
    defaultProps: {
      subheading: 'Compare options',
      title: 'Choose the right fit',
      description: '',
      first_column_label: 'Feature',
      columns: [
        { key: 'option_a', label: 'Option A', highlight: false },
        { key: 'option_b', label: 'Option B', highlight: true },
        { key: 'option_c', label: 'Option C', highlight: false },
      ],
      rows: [
        { label: 'Best for', option_a: 'Simple needs', option_b: 'Most programs', option_c: 'Complex programs' },
        { label: 'Support', option_a: 'Standard', option_b: 'Priority', option_c: 'Dedicated' },
        { label: 'Flexibility', option_a: 'Good', option_b: 'High', option_c: 'Highest' },
      ],
      cta_label: '',
      cta_url: '',
    },
  },
  {
    key: 'form',
    label: 'Form',
    defaultProps: {
      form_key: '',
      title: 'Contact us',
      description: 'Send us a message and our team will follow up.',
      submit_label: 'Submit',
      success_message: 'Thanks — your response has been received.',
      email_from: 'info@elitecore.ca',
      email_from_name: 'Elite Core',
      email_to: '',
      email_cc: '',
      email_bcc: '',
      fields: [
        { key: 'name', label: 'Name', type: 'text', required: true },
        { key: 'email', label: 'Email', type: 'email', required: true },
        { key: 'message', label: 'Message', type: 'textarea', required: false },
      ],
    },
  },
];

const WIDGET_COMPONENT_ALIASES = {
  hero_split: 'hero',
  featured_categories: 'category_grid',
  brand_grid: 'category_grid',
  theme_grid: 'category_grid',
  catalog_toolbar: 'rich_text',
  category_hero: 'hero',
  promo_cards: 'rich_text',
};

const normalizeWidgetComponent = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return WIDGET_COMPONENT_ALIASES[raw] || raw;
};

// The Elements palette is the client-side source of truth for what can be
// authored. Publish preflight must never drift from it: if the builder lets a
// user add a widget, the client must recognize its normalized component.
const SUPPORTED_TEMPLATE_COMPONENTS = new Set([
  ...WIDGET_LIBRARY
    .map((widget) => normalizeWidgetComponent(widget?.key))
    .filter(Boolean),
  'catalog_archive',
]);

const QUICK_SECTIONS = [
  {
    id: 'promo-services',
    label: 'Promo Services',
    category: 'Services',
    description: 'A polished 3-column overview of the core services most promotional distributors offer.',
    widgets: [
      {
        component: 'cards_grid',
        props: {
          subheading: 'How we can help',
          title: 'Everything your brand needs, in one place',
          description: 'From the first idea to final delivery, bring your merchandise program together with one connected team.',
          variant: 'services',
          columns_desktop: 3,
          columns_tablet: 2,
          columns_mobile: 1,
          card_style: 'elevated',
          items: [
            { icon: '01', title: 'Creative & Design', body: 'Turn campaign ideas into brand-ready concepts, artwork and product direction.', link_label: '', link_url: '' },
            { icon: '02', title: 'Product Sourcing', body: 'Find the right promotional products, apparel and print for the audience, budget and timeline.', link_label: '', link_url: '' },
            { icon: '03', title: 'Fulfillment & Delivery', body: 'Coordinate decoration, kitting, inventory, shipping and distribution without the usual handoffs.', link_label: '', link_url: '' },
          ],
        },
      },
    ],
  },
  {
    id: 'promo-capabilities',
    label: 'Promo Capabilities',
    category: 'Services',
    description: 'A 4-card capability section for distributors with a broader offering.',
    widgets: [
      {
        component: 'cards_grid',
        props: {
          subheading: 'Capabilities',
          title: 'More than products',
          description: 'Build programs that connect strategy, merchandise, decoration and logistics.',
          variant: 'features',
          columns_desktop: 4,
          columns_tablet: 2,
          columns_mobile: 1,
          card_style: 'outlined',
          items: [
            { icon: '01', title: 'Branded Merchandise', body: 'Curated promotional products for campaigns, teams and customer programs.' },
            { icon: '02', title: 'Apparel & Uniforms', body: 'Wearable brand programs designed for comfort, consistency and repeat ordering.' },
            { icon: '03', title: 'Print & Packaging', body: 'Supporting print, packaging and presentation pieces that complete the experience.' },
            { icon: '04', title: 'Company Stores', body: 'Always-on storefronts for employee gear, client gifting and recurring programs.' },
          ],
        },
      },
    ],
  },
  {
    id: 'promo-process',
    label: 'How We Work',
    category: 'Process',
    description: 'A ready-made four-step promotional project process.',
    widgets: [
      {
        component: 'cards_grid',
        props: {
          subheading: 'Our process',
          title: 'From idea to delivery',
          description: 'A clear process keeps projects moving and gives everyone confidence in what happens next.',
          variant: 'process',
          columns_desktop: 4,
          columns_tablet: 2,
          columns_mobile: 1,
          card_style: 'minimal',
          items: [
            { icon: '01', title: 'Discover', body: 'We learn the audience, objective, quantity, budget and target date.' },
            { icon: '02', title: 'Create', body: 'We curate products, develop the creative direction and prepare the right options.' },
            { icon: '03', title: 'Produce', body: 'Artwork approvals, decoration and production are coordinated from one workflow.' },
            { icon: '04', title: 'Deliver', body: 'We ship, kit, distribute or launch the program exactly where it needs to go.' },
          ],
        },
      },
    ],
  },
  {
    id: 'promo-why-us',
    label: 'Why Choose Us',
    category: 'Trust',
    description: 'A modern value section focused on the things buyers care about most.',
    widgets: [
      {
        component: 'cards_grid',
        props: {
          subheading: 'Why work with us',
          title: 'Less friction. Better merchandise.',
          variant: 'benefits',
          columns_desktop: 3,
          columns_tablet: 2,
          columns_mobile: 1,
          card_style: 'filled',
          items: [
            { icon: '01', title: 'Curated, not overwhelming', body: 'Get focused recommendations instead of searching thousands of products without direction.' },
            { icon: '02', title: 'One connected workflow', body: 'Keep product, artwork, approvals, production and delivery moving together.' },
            { icon: '03', title: 'Built around your brand', body: 'Create merchandise people actually want to use, wear and remember.' },
          ],
        },
      },
    ],
  },
  {
    id: 'promo-company-store',
    label: 'Company Store',
    category: 'Programs',
    description: 'Content + media concept promoting employee or customer storefront programs.',
    widgets: [
      {
        component: 'rich_text',
        props: {
          subheading: 'Company stores',
          title: 'Your branded merchandise, always open',
          body: 'Give employees, customers or teams an easy place to order approved products while your program stays organized behind the scenes.',
          cta_label: 'Explore company stores',
          cta_url: '/contact',
          image_url: '',
          image_alt: 'Branded company store experience',
          media_position: 'right',
          content_width: 48,
          gap: 56,
          image_radius: 20,
          image_shadow: 'medium',
          image_aspect: '4/3',
        },
      },
    ],
  },
  {
    id: 'promo-event-merch',
    label: 'Event Merch',
    category: 'Programs',
    description: 'Ready-made campaign section for events, conferences and launches.',
    widgets: [
      {
        component: 'rich_text',
        props: {
          subheading: 'Events & campaigns',
          title: 'Merchandise that makes the moment feel bigger',
          body: 'Build an event collection around the audience and experience — from attendee kits and staff apparel to giveaways, signage and post-event follow-up.',
          cta_label: 'Plan your event merch',
          cta_url: '/contact',
          image_url: '',
          image_alt: 'Event merchandise and branded products',
          media_position: 'left',
          content_width: 50,
          gap: 52,
          image_radius: 18,
          image_shadow: 'soft',
          image_aspect: '16/9',
        },
      },
    ],
  },
  {
    id: 'promo-proof',
    label: 'Proof + Stats',
    category: 'Trust',
    description: 'Adds a stats strip followed by social proof for a stronger credibility section.',
    widgets: [
      {
        component: 'stats',
        props: {
          title: 'Built to keep programs moving',
          columns_desktop: 4,
          columns_tablet: 2,
          columns_mobile: 2,
          items: [
            { value: '1', label: 'Connected partner' },
            { value: '24/7', label: 'Online ordering' },
            { value: 'Fast', label: 'Curated sourcing' },
            { value: 'End-to-end', label: 'Project visibility' },
          ],
        },
      },
      {
        component: 'testimonials',
        props: {
          subheading: 'Customer experience',
          title: 'The kind of partner people come back to',
          columns_desktop: 3,
          columns_tablet: 2,
          columns_mobile: 1,
          items: [
            { quote: 'Add a specific customer story about service, speed or creativity.', name: 'Customer name', role: 'Company', image_url: '', rating: 5 },
            { quote: 'Use a quote that describes the experience of working with your team.', name: 'Customer name', role: 'Company', image_url: '', rating: 5 },
            { quote: 'Proof works best when it talks about a real result or solved problem.', name: 'Customer name', role: 'Company', image_url: '', rating: 5 },
          ],
        },
      },
    ],
  },
  {
    id: 'promo-quote-cta',
    label: 'Request a Quote CTA',
    category: 'Conversion',
    description: 'A strong promotional-project CTA ready to place near the bottom of a page.',
    widgets: [
      {
        component: 'cta',
        props: {
          subheading: 'Have a project in mind?',
          title: 'Tell us what you need. We’ll help shape the right solution.',
          body: 'Share your audience, quantity, budget and target date — even if the product is not figured out yet.',
          primary_label: 'Start a project',
          primary_url: '/contact',
          secondary_label: 'Browse products',
          secondary_url: '/shop',
          align: 'center',
          style: 'panel',
        },
      },
    ],
  },
  {
    id: 'promo-lookbook',
    label: 'Promo Inspiration Gallery',
    category: 'Visual',
    description: 'A ready-made visual section for past projects, campaign inspiration and decoration examples.',
    widgets: [
      {
        component: 'gallery',
        props: {
          subheading: 'Inspiration',
          title: 'Ideas worth stealing',
          description: 'Show real work, campaign concepts, apparel collections or event moments in a polished visual grid.',
          layout: 'grid',
          columns_desktop: 3,
          columns_tablet: 2,
          columns_mobile: 1,
          gap: 16,
          image_radius: 18,
          lightbox: true,
          items: [
            { image_url: '', alt: 'Promotional merchandise project', caption: 'Campaign kits', link_url: '' },
            { image_url: '', alt: 'Branded apparel project', caption: 'Team apparel', link_url: '' },
            { image_url: '', alt: 'Event merchandise project', caption: 'Event merchandise', link_url: '' },
          ],
        },
      },
    ],
  },
  {
    id: 'promo-team',
    label: 'Meet Your Promo Team',
    category: 'Trust',
    description: 'A people-first section for sales reps, account managers, creative and production contacts.',
    widgets: [
      {
        component: 'team',
        props: {
          subheading: 'Your team',
          title: 'Real people behind every project',
          description: 'Introduce the people customers will actually work with.',
          columns_desktop: 3,
          columns_tablet: 2,
          columns_mobile: 1,
          card_style: 'cards',
          items: [
            { name: 'Sales Rep', role: 'Account Manager', bio: 'Your main partner for ideas, planning and project coordination.', image_url: '', email: '', linkedin_url: '' },
            { name: 'Creative Lead', role: 'Design & Artwork', bio: 'Helps turn concepts into production-ready branded creative.', image_url: '', email: '', linkedin_url: '' },
            { name: 'Production Lead', role: 'Production & Fulfillment', bio: 'Keeps decoration, production and delivery moving.', image_url: '', email: '', linkedin_url: '' },
          ],
        },
      },
    ],
  },
  {
    id: 'promo-decoration-comparison',
    label: 'Decoration Comparison',
    category: 'Education',
    description: 'A comparison section for common decoration methods.',
    widgets: [
      {
        component: 'comparison',
        props: {
          subheading: 'Decoration guide',
          title: 'Which decoration method fits the project?',
          description: 'A simple starting point for explaining common decoration options.',
          first_column_label: 'Consideration',
          columns: [
            { key: 'embroidery', label: 'Embroidery', highlight: false },
            { key: 'screenprint', label: 'Screen Print', highlight: true },
            { key: 'dtf', label: 'DTF / Transfer', highlight: false },
          ],
          rows: [
            { label: 'Best for', embroidery: 'Polos, hats, jackets', screenprint: 'Tees, hoodies, volume runs', dtf: 'Detailed art, short runs' },
            { label: 'Look', embroidery: 'Premium / dimensional', screenprint: 'Classic / durable', dtf: 'Detailed / full colour' },
            { label: 'Typical quantity', embroidery: 'Low to high', screenprint: 'Medium to high', dtf: 'Low to medium' },
          ],
          cta_label: 'Ask us what fits your project',
          cta_url: '/contact',
        },
      },
    ],
  },
  {
    id: 'promo-faq',
    label: 'Promo FAQ',
    category: 'Support',
    description: 'Common questions for promotional products, artwork, timelines and ordering.',
    widgets: [
      {
        component: 'faq',
        props: {
          subheading: 'Common questions',
          title: 'Helpful before you get started',
          style: 'joined',
          allow_multiple: false,
          open_first: true,
          items: [
            { question: 'How far ahead should we start?', answer: 'Timelines vary by product and decoration method, but starting early gives you more product options and time for artwork, approvals and shipping.' },
            { question: 'Can you help us choose products?', answer: 'Yes. Share the audience, goal, quantity, budget and target date and we can curate focused recommendations.' },
            { question: 'Do you handle artwork and decoration?', answer: 'Yes. Artwork, proofing and decoration can be coordinated as part of the project.' },
            { question: 'Can you ship to multiple locations?', answer: 'Yes. Depending on the program, orders can be bulk shipped, split shipped, kitted or distributed to individual recipients.' },
          ],
        },
      },
    ],
  },
];

const SLOT_META = {
  home: { label: 'Home', icon: HomeRoundedIcon, tint: 'rgba(59,130,246,0.1)' },
  shop: { label: 'Shop', icon: StorefrontRoundedIcon, tint: 'rgba(16,185,129,0.12)' },
  catalog: { label: 'Catalog', icon: StorefrontRoundedIcon, tint: 'rgba(16,185,129,0.1)' },
  services: { label: 'Services', icon: AutoAwesomeRoundedIcon, tint: 'rgba(14,165,233,0.1)' },
  about: { label: 'About', icon: InfoRoundedIcon, tint: 'rgba(168,85,247,0.1)' },
  contact: { label: 'Contact', icon: ContactMailRoundedIcon, tint: 'rgba(245,158,11,0.12)' },
};

const WIDGET_META = {
  catalog_archive: { icon: StorefrontRoundedIcon, accent: '#059669' },
  hero: { icon: AutoAwesomeRoundedIcon, accent: '#7c3aed' },
  featured_categories: { icon: ViewQuiltRoundedIcon, accent: '#0f766e' },
  category_grid: { icon: ViewQuiltRoundedIcon, accent: '#0f766e' },
  brand_grid: { icon: ViewQuiltRoundedIcon, accent: '#0891b2' },
  theme_grid: { icon: ViewQuiltRoundedIcon, accent: '#a855f7' },
  product_grid: { icon: Inventory2RoundedIcon, accent: '#2563eb' },
  product_carousel: { icon: Inventory2RoundedIcon, accent: '#0f766e' },
  rich_text: { icon: ManageSearchRoundedIcon, accent: '#be123c' },
  button_row: { icon: OpenInNewRoundedIcon, accent: '#0f766e' },
  contact_block: { icon: ContactMailRoundedIcon, accent: '#b45309' },
  form: { icon: ContactMailRoundedIcon, accent: '#7c3aed' },
  cards_grid: { icon: ViewQuiltRoundedIcon, accent: '#2563eb' },
  stats: { icon: AutoAwesomeRoundedIcon, accent: '#0f766e' },
  testimonials: { icon: ContactMailRoundedIcon, accent: '#be123c' },
  faq: { icon: ManageSearchRoundedIcon, accent: '#7c3aed' },
  logo_cloud: { icon: ViewQuiltRoundedIcon, accent: '#475569' },
  cta: { icon: OpenInNewRoundedIcon, accent: '#b45309' },
  gallery: { icon: ViewQuiltRoundedIcon, accent: '#db2777' },
  video: { icon: AutoAwesomeRoundedIcon, accent: '#dc2626' },
  team: { icon: ContactMailRoundedIcon, accent: '#0369a1' },
  timeline: { icon: AutoAwesomeRoundedIcon, accent: '#7c3aed' },
  comparison: { icon: ViewQuiltRoundedIcon, accent: '#0f766e' },
};

const WIDGET_DETAILS = {
  catalog_archive: 'Live WooCommerce shop archive. This required system block keeps product queries, filters and pagination dynamic.',
  hero: 'Large visual banner with slides, headline, text, and a button.',
  featured_categories: 'Highlight a small set of popular categories.',
  category_grid: 'Show categories in a clean responsive grid.',
  brand_grid: 'Help shoppers browse products by brand.',
  theme_grid: 'Help shoppers browse by theme or collection.',
  product_grid: 'Show products from your connected catalogue.',
  product_carousel: 'Show products in a horizontally scrollable merchandising rail.',
  rich_text: 'Build a polished two-column content section with subheading, heading, copy, optional CTA and optional image.',
  button_row: 'Add one or more calls to action using the Showcase button contract.',
  contact_block: 'Give visitors a clear way to get help or contact your team.',
  form: 'Collect custom responses directly into Elite Core with a lightweight website form.',
  cards_grid: 'Create 2–4 column Services, Features, Benefits or Process sections from repeatable cards.',
  stats: 'Show concise KPI, proof or impact numbers in a responsive metric strip.',
  testimonials: 'Display customer proof with quotes, names, roles, photos and optional star ratings.',
  faq: 'Add an accessible expandable FAQ section for objections, support and SEO-friendly answers.',
  logo_cloud: 'Show customer, partner or supplier logos in a clean responsive trust section.',
  cta: 'Create a focused conversion section with headline, supporting copy and one or two actions.',
  gallery: 'Create a responsive visual gallery or lookbook with captions, links and optional lightbox viewing.',
  video: 'Pair content with YouTube, Vimeo or direct video media in a polished responsive section.',
  team: 'Introduce sales reps, account teams or staff with photos, roles, bios and contact links.',
  timeline: 'Show project steps, company history or milestones in a clear sequential layout.',
  comparison: 'Compare services, program levels or options in a responsive feature table.',
};

const WIDGET_TEMPLATES = {
  hero: [
    { key: 'launch', label: 'Launch Hero', props: { title: 'New Collection', subtitle: 'Fresh picks for your team.', min_height: 500 } },
    { key: 'promo', label: 'Promo Hero', props: { title: 'Limited Time Deal', subtitle: 'Save more with curated bundles.', min_height: 460 } },
  ],
  category_grid: [
    { key: 'browse8', label: 'Browse 8x4x2', props: { heading: 'Browse categories', columns_desktop: 8, columns_tablet: 4, columns_mobile: 2 } },
    { key: 'compact', label: 'Compact 6x3x2', props: { heading: 'Top categories', columns_desktop: 6, columns_tablet: 3, columns_mobile: 2 } },
  ],
  brand_grid: [
    { key: 'brandwall', label: 'Brand Wall', props: { heading: 'Popular brands', taxonomy: 'brand', columns_desktop: 8, columns_tablet: 4, columns_mobile: 2 } },
  ],
  theme_grid: [
    { key: 'seasonal', label: 'Seasonal Themes', props: { heading: 'Shop by theme', taxonomy: 'theme', columns_desktop: 8, columns_tablet: 4, columns_mobile: 2 } },
  ],
  product_grid: [
    { key: 'bestsellers', label: 'Best Sellers', props: { title: 'Best sellers', page_size: 16 } },
    { key: 'catalog', label: 'Full Catalog', props: { title: 'Catalog', page_size: 24 } },
  ],
  product_carousel: [
    { key: 'featured-rail', label: 'Featured Rail', props: { title: 'Featured products', page_size: 12, columns_desktop: 4, columns_tablet: 3, columns_mobile: 1 } },
    { key: 'compact-rail', label: 'Compact Rail', props: { title: 'More to explore', page_size: 16, columns_desktop: 5, columns_tablet: 3, columns_mobile: 1 } },
  ],
  rich_text: [
    { key: 'story-right', label: 'Story + Image', props: { subheading: 'Why choose us', title: 'Built around your team', body: 'Use this space to explain the value clearly and keep the visual beside the message.', media_position: 'right', content_width: 50, gap: 48, image_radius: 18, image_shadow: 'medium', image_aspect: '4/3' } },
    { key: 'story-left', label: 'Image + Story', props: { subheading: 'Our approach', title: 'A better way to get it done', body: 'Flip the visual to the left for an alternate page rhythm.', media_position: 'left', content_width: 50, gap: 48, image_radius: 18, image_shadow: 'soft', image_aspect: '4/3' } },
  ],
  cards_grid: [
    { key: 'services-3', label: '3 Services', props: { variant: 'services', columns_desktop: 3, card_style: 'elevated' } },
    { key: 'features-4', label: '4 Features', props: { variant: 'features', columns_desktop: 4, card_style: 'outlined' } },
    { key: 'process-3', label: '3 Step Process', props: { variant: 'process', columns_desktop: 3, card_style: 'minimal' } },
  ],
  stats: [
    { key: 'proof-4', label: '4 Metrics', props: { columns_desktop: 4 } },
  ],
  testimonials: [
    { key: 'social-proof-3', label: '3 Testimonials', props: { columns_desktop: 3 } },
  ],
  faq: [
    { key: 'faq-joined', label: 'Joined FAQ', props: { style: 'joined', open_first: true } },
    { key: 'faq-cards', label: 'FAQ Cards', props: { style: 'cards', open_first: false } },
  ],
  logo_cloud: [
    { key: 'logos-5', label: '5 Logos', props: { columns_desktop: 5, grayscale: true } },
  ],
  cta: [
    { key: 'cta-panel', label: 'CTA Panel', props: { style: 'panel', align: 'center' } },
    { key: 'cta-simple', label: 'Simple CTA', props: { style: 'minimal', align: 'left' } },
  ],
  gallery: [
    { key: 'lookbook-3', label: '3-Column Lookbook', props: { layout: 'grid', columns_desktop: 3, columns_tablet: 2, columns_mobile: 1, lightbox: true } },
    { key: 'lookbook-4', label: '4-Column Gallery', props: { layout: 'grid', columns_desktop: 4, columns_tablet: 2, columns_mobile: 2, lightbox: true } },
  ],
  video: [
    { key: 'video-right', label: 'Content + Video', props: { media_position: 'right', content_width: 44 } },
    { key: 'video-left', label: 'Video + Content', props: { media_position: 'left', content_width: 44 } },
  ],
  team: [
    { key: 'team-3', label: '3 People', props: { columns_desktop: 3, card_style: 'minimal' } },
    { key: 'team-cards', label: 'Team Cards', props: { columns_desktop: 4, card_style: 'cards' } },
  ],
  timeline: [
    { key: 'steps', label: 'Process Steps', props: { style: 'vertical' } },
    { key: 'milestones', label: 'Milestones', props: { style: 'cards' } },
  ],
  comparison: [
    { key: 'compare-3', label: '3-Way Comparison', props: {} },
  ],
  button_row: [
    { key: 'shop-now', label: 'Shop Now', props: { label: 'Shop now', url: '/shop' } },
    { key: 'contact-us', label: 'Contact Us', props: { label: 'Contact us', url: '/contact' } },
  ],
};

const getWidgetMeta = (component) => WIDGET_META[component] || { icon: ViewQuiltRoundedIcon, accent: '#475569' };
const getWidgetLabel = (component) => component === 'catalog_archive'
  ? 'Product Archive'
  : (WIDGET_LIBRARY.find((widget) => widget.key === component)?.label || component || 'Widget');

const createPromoShowcaseNodeId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return `psn_${window.crypto.randomUUID().replace(/-/g, '')}`;
  }
  return `psn_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

const DEFAULT_SHOWCASE_PAGES = [
  { slug: 'home', title: 'Home', slot: 'home', set_front_page: true, show_in_menu: true },
  { slug: 'shop', title: 'Shop', slot: 'shop', system: 'woocommerce_shop', set_front_page: false, show_in_menu: true },
  { slug: 'catalog', title: 'Catalog', slot: 'catalog', set_front_page: false, show_in_menu: false },
  { slug: 'services', title: 'Services', slot: 'services', set_front_page: false, show_in_menu: true },
  { slug: 'about', title: 'About', slot: 'about', set_front_page: false, show_in_menu: true },
  { slug: 'contact', title: 'Contact', slot: 'contact', set_front_page: false, show_in_menu: true },
];

const ensureSystemShowcasePages = (pages = []) => {
  const source = Array.isArray(pages) ? pages.map((page) => ({ ...(page || {}) })) : [];
  const shopIndex = source.findIndex((page) => page?.slot === 'shop' || page?.system === 'woocommerce_shop');
  const shopDefinition = { slug: 'shop', title: 'Shop', slot: 'shop', system: 'woocommerce_shop', set_front_page: false, show_in_menu: true };

  if (shopIndex >= 0) {
    source[shopIndex] = {
      ...shopDefinition,
      ...source[shopIndex],
      slug: 'shop',
      slot: 'shop',
      system: 'woocommerce_shop',
      set_front_page: false,
    };
    return source;
  }

  const legacyCatalogIndex = source.findIndex((page) =>
    page?.slot === 'catalog'
    && String(page?.slug || 'catalog') === 'catalog'
    && String(page?.title || 'Catalog') === 'Catalog'
  );
  if (legacyCatalogIndex >= 0 && source[legacyCatalogIndex]?.show_in_menu !== false) {
    source[legacyCatalogIndex] = { ...source[legacyCatalogIndex], show_in_menu: false };
  }

  const homeIndex = source.findIndex((page) => page?.slot === 'home');
  source.splice(homeIndex >= 0 ? homeIndex + 1 : 0, 0, shopDefinition);
  return source;
};

const DEFAULT_SITE_CHROME = {
  header: {
    enabled: true,
    content_width: 'contained',
    sticky: false,
    background_mode: 'solid',
    font_family: '',
    column_layout: 'balanced',
    logo_width_px: 180,
    logo_max_height_px: 48,
    show_announcement: true,
    show_brand_name: true,
    show_brand_name_with_logo: false,
    show_logo: true,
    show_account: true,
    show_product_search: false,
    show_cart: true,
    cart_display: 'label',
    cta_label: 'Shop now',
    cta_url: '/shop',
    blocks: ['brand','menu','actions'],
  },
  navigation: {
    mode: 'pages',
    font_size_px: 14,
    active_menu: 'primary',
    items: [],
    menus: [
      { key: 'primary', name: 'Primary Menu', items: [] },
      { key: 'footer', name: 'Footer Menu', items: [] },
    ],
  },
  footer: {
    enabled: true,
    content_width: 'contained',
    text: '',
    menu_key: 'footer',
    items: [],
    show_elite_core_watermark: true,
  },
};

const createEmptyLayoutSchema = () => ({
  version: 1,
  slots: Object.fromEntries(DEFAULT_SHOWCASE_PAGES.map((page) => [
    page.slot,
    page.slot === 'shop'
      ? [{
          id: 'ps-system-product-archive',
          component: 'catalog_archive',
          version: 1,
          props: { system_locked: true, title: 'Shop products' },
        }]
      : [],
  ])),
});

const normalizeLayoutSchema = (layout) => {
  const base = createEmptyLayoutSchema();
  const source = layout && typeof layout === 'object' ? layout : {};
  const slots = source.slots && typeof source.slots === 'object' ? source.slots : {};
  const slotKeys = Array.from(new Set([...Object.keys(base.slots), ...Object.keys(slots)]));

  slotKeys.forEach((slot) => {
    const sections = Array.isArray(slots[slot])
      ? slots[slot]
      : (slot === 'shop' ? base.slots.shop : []);
    base.slots[slot] = sections
      .filter((section) => section && typeof section === 'object')
      .map((section) => ({
        id: String(section.id || section.node_id || section.nodeId || '').trim() || createPromoShowcaseNodeId(),
        component: normalizeWidgetComponent(section.component),
        version: Number(section.version) || 1,
        props: (() => {
          const rawProps = section.props && typeof section.props === 'object'
            ? cloneBuilderValue(section.props)
            : {};
          const migratedProps = { ...rawProps };

          // Style contract v1 accidentally seeded left/right margin = 0, which
          // overrode the runtime's centered max-width column. Migrate that
          // generated state once; after v2, an explicitly entered 0 is retained.
          if (
            Number(migratedProps?.style_contract_version || 0) < 2 &&
            Number(migratedProps?.margin_left) === 0 &&
            Number(migratedProps?.margin_right) === 0
          ) {
            migratedProps.margin_left = '';
            migratedProps.margin_right = '';
          }

          if (Number(migratedProps?.style_contract_version || 0) < 3) {
            const legacyWidth = migratedProps?.border_width ?? DEFAULT_WIDGET_STYLE_PROPS.border_width;
            const legacyStyle = migratedProps?.border_style ?? DEFAULT_WIDGET_STYLE_PROPS.border_style;
            const legacyColor = migratedProps?.border_color ?? DEFAULT_WIDGET_STYLE_PROPS.border_color;
            const legacyRadius = migratedProps?.border_radius ?? DEFAULT_WIDGET_STYLE_PROPS.border_radius;

            ['top', 'right', 'bottom', 'left'].forEach((side) => {
              if (migratedProps[`border_${side}_width`] == null) migratedProps[`border_${side}_width`] = legacyWidth;
              if (migratedProps[`border_${side}_style`] == null) migratedProps[`border_${side}_style`] = legacyStyle;
              if (migratedProps[`border_${side}_color`] == null) migratedProps[`border_${side}_color`] = legacyColor;
            });

            [
              'border_top_left_radius',
              'border_top_right_radius',
              'border_bottom_right_radius',
              'border_bottom_left_radius',
            ].forEach((key) => {
              if (migratedProps[key] == null) migratedProps[key] = legacyRadius;
            });
          }

          return {
            ...withDefaultWidgetStyle(migratedProps),
            style_contract_version: 3,
          };
        })(),
      }))
      .filter((section) => section.component && SUPPORTED_TEMPLATE_COMPONENTS.has(section.component));

    if (slot === 'shop' && !base.slots[slot].some((section) => section.component === 'catalog_archive')) {
      base.slots[slot].push({
        id: 'ps-system-product-archive',
        component: 'catalog_archive',
        version: 1,
        props: withDefaultWidgetStyle({ system_locked: true, title: 'Shop products' }),
      });
    }
  });

  return base;
};


const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const sanitizeCssFontFamily = (value) => {
  const fallback = 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  const cleaned = String(value || fallback)
    .replace(/[;{}<>]/g, '')
    .replace(/[\r\n\t]+/g, ' ')
    .trim();
  return cleaned || fallback;
};

const SHOWCASE_GOOGLE_FONT_FAMILIES = {
  Inter: 'Inter',
  Manrope: 'Manrope',
  'DM Sans': 'DM+Sans',
  'Plus Jakarta Sans': 'Plus+Jakarta+Sans',
  Montserrat: 'Montserrat',
  Poppins: 'Poppins',
  Roboto: 'Roboto',
  'Open Sans': 'Open+Sans',
  Lato: 'Lato',
  'Nunito Sans': 'Nunito+Sans',
  Merriweather: 'Merriweather',
  Oswald: 'Oswald',
};

const getShowcaseGoogleFontHref = (...fontValues) => {
  const families = [];
  fontValues.flat(Infinity).forEach((value) => {
    const firstFamily = String(value || '')
      .split(',')[0]
      .trim()
      .replace(/^['"]|['"]$/g, '');
    const googleFamily = SHOWCASE_GOOGLE_FONT_FAMILIES[firstFamily];
    if (googleFamily && !families.includes(googleFamily)) families.push(googleFamily);
  });
  if (!families.length) return '';

  // Google Fonts CSS2 rejects requests that ask a family for weights it does
  // not provide. Showcase previously requested 400-900 for every family, which
  // made valid choices such as Manrope / Plus Jakarta Sans / Oswald unreliable
  // inside the srcDoc preview iframe. Every managed family supports regular +
  // bold, which covers the header/menu rendering while allowing the browser to
  // synthesize intermediate display weights where needed.
  const query = families.map((family) => `family=${family}:wght@400;700`).join('&');
  return `https://fonts.googleapis.com/css2?${query}&display=swap`;
};

const mergeCssBlocks = (...blocks) =>
  blocks
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join('\n\n');

const stripGlobalStylesheetFromCustomCss = ({ globalCss = '', customCss = '' }) => {
  const global = String(globalCss || '').trim();
  const custom = String(customCss || '').trim();
  if (!global) return custom;
  if (custom === global) return '';
  if (custom.startsWith(global)) return custom.slice(global.length).replace(/^\s+/, '');
  return custom;
};

const stripLegacyWidgetShellCss = (value = '') => {
  let css = String(value || '');

  // Older Showcase starters owned the generic widget shell in CSS. The builder
  // owns those properties now, so do not keep carrying this legacy block into
  // editable/published template CSS.
  css = css.replace(
    /\.promoshowcase-slot\s+\.ps-widget\s*,\s*\.promoshowcase-slot\s+\.ps\s*\{[^}]*?(?:border\s*:[^;]+;)?[^}]*?(?:border-radius\s*:[^;]+;)?[^}]*?(?:background\s*:[^;]+;)?[^}]*?(?:padding\s*:[^;]+;)?[^}]*?(?:margin-bottom\s*:[^;]+;)?[^}]*?\}/gi,
    ''
  );

  return css.replace(/\n{3,}/g, '\n\n').trim();
};

const buildSpacingStyle = (props = {}) => {
  const map = [
    ['margin_top', 'margin-top'],
    ['margin_right', 'margin-right'],
    ['margin_bottom', 'margin-bottom'],
    ['margin_left', 'margin-left'],
    ['padding_top', 'padding-top'],
    ['padding_right', 'padding-right'],
    ['padding_bottom', 'padding-bottom'],
    ['padding_left', 'padding-left'],
  ];
  const styles = map.map(([key, cssKey]) => {
    if (props?.[key] === '' || props?.[key] == null) return '';
    const value = Number(props[key]);
    return Number.isFinite(value) ? `${cssKey}:${value}px !important` : '';
  }).filter(Boolean);

  const backgroundColor = String(props?.background_color ?? '').trim();
  if (backgroundColor) styles.push(`background-color:${escapeHtml(backgroundColor)} !important`);

  const borderSides = ['top', 'right', 'bottom', 'left'];
  borderSides.forEach((side) => {
    const widthRaw = props?.[`border_${side}_width`] ?? props?.border_width;
    const styleRaw = props?.[`border_${side}_style`] ?? props?.border_style;
    const colorRaw = props?.[`border_${side}_color`] ?? props?.border_color;

    if (widthRaw !== '' && widthRaw != null) {
      const value = Number(widthRaw);
      if (Number.isFinite(value)) styles.push(`border-${side}-width:${Math.max(0, value)}px !important`);
    }
    const styleValue = String(styleRaw || '').trim();
    if (styleValue) styles.push(`border-${side}-style:${escapeHtml(styleValue)} !important`);
    const colorValue = String(colorRaw || '').trim();
    if (colorValue) styles.push(`border-${side}-color:${escapeHtml(colorValue)} !important`);
  });

  const radiusMap = [
    ['border_top_left_radius', 'border-top-left-radius'],
    ['border_top_right_radius', 'border-top-right-radius'],
    ['border_bottom_right_radius', 'border-bottom-right-radius'],
    ['border_bottom_left_radius', 'border-bottom-left-radius'],
  ];
  radiusMap.forEach(([key, cssKey]) => {
    const raw = props?.[key] ?? props?.border_radius;
    if (raw === '' || raw == null) return;
    const value = Number(raw);
    if (Number.isFinite(value)) styles.push(`${cssKey}:${Math.max(0, value)}px !important`);
  });

  return styles;
};

const WidgetStyleControls = ({ props = {}, onChange }) => {
  const [device, setDevice] = useState('desktop');
  const suffix = device === 'desktop' ? '' : `_${device}`;
  const keyFor = (key) => `${key}${suffix}`;
  const valueFor = (key) => {
    const responsiveKey = keyFor(key);
    if (device !== 'desktop') return props?.[responsiveKey] ?? '';
    return props?.[key] ?? DEFAULT_WIDGET_STYLE_PROPS[key] ?? '';
  };
  const numeric = (key) => (event) => {
    const raw = event.target.value;
    const value = raw === '' ? '' : Number(raw);
    onChange(keyFor(key), Number.isFinite(value) ? value : '');
  };
  const textChange = (key) => (event) => onChange(keyFor(key), event.target.value);

  return (
    <Box
      component="details"
      sx={{
        mt: 0.75,
        border: `1px solid ${BRAND.line}`,
        borderRadius: 1.5,
        background: 'rgba(255,255,255,0.88)',
        overflow: 'hidden',
        '&[open] > summary': { borderBottom: `1px solid ${BRAND.line}` },
      }}
    >
      <Box
        component="summary"
        sx={{
          px: 1,
          py: 0.85,
          cursor: 'pointer',
          listStyle: 'none',
          fontSize: 12,
          fontWeight: 800,
          color: BRAND.ink,
          '&::-webkit-details-marker': { display: 'none' },
        }}
      >
        Advanced
        <Typography component="span" variant="caption" sx={{ ml: 0.75, color: BRAND.slate }}>
          spacing, background, border & radius
        </Typography>
      </Box>

      <Box sx={{ p: 1, display: 'grid', gap: 1.25 }}>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {[
            ['desktop', 'Desktop'],
            ['tablet', 'Tablet'],
            ['mobile', 'Mobile'],
          ].map(([value, label]) => (
            <Button
              key={value}
              size="small"
              variant={device === value ? 'contained' : 'outlined'}
              onClick={() => setDevice(value)}
              sx={device === value ? styles.primaryButton : styles.outlineButton}
            >
              {label}
            </Button>
          ))}
        </Box>
        {device !== 'desktop' ? (
          <Typography variant="caption" sx={{ color: BRAND.slate }}>
            Blank values inherit the Desktop setting.
          </Typography>
        ) : null}
        <Divider />
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.65, color: BRAND.slate, fontWeight: 800 }}>
            Spacing
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' }, gap: 0.75 }}>
            <TextField size="small" type="number" label="Margin top" value={valueFor('margin_top')} onChange={numeric('margin_top')} />
            <TextField size="small" type="number" label="Margin right" value={valueFor('margin_right')} onChange={numeric('margin_right')} />
            <TextField size="small" type="number" label="Margin bottom" value={valueFor('margin_bottom')} onChange={numeric('margin_bottom')} />
            <TextField size="small" type="number" label="Margin left" value={valueFor('margin_left')} onChange={numeric('margin_left')} />

            <TextField size="small" type="number" label="Padding top" value={valueFor('padding_top')} inputProps={{ min: 0 }} onChange={numeric('padding_top')} />
            <TextField size="small" type="number" label="Padding right" value={valueFor('padding_right')} inputProps={{ min: 0 }} onChange={numeric('padding_right')} />
            <TextField size="small" type="number" label="Padding bottom" value={valueFor('padding_bottom')} inputProps={{ min: 0 }} onChange={numeric('padding_bottom')} />
            <TextField size="small" type="number" label="Padding left" value={valueFor('padding_left')} inputProps={{ min: 0 }} onChange={numeric('padding_left')} />
          </Box>
        </Box>

        <Divider />

        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.65, color: BRAND.slate, fontWeight: 800 }}>
            Background
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 360px)' }, gap: 0.75 }}>
            <TextField
              size="small"
              label="Background color"
              value={valueFor('background_color')}
              placeholder="transparent or #ffffff"
              onChange={textChange('background_color')}
            />
          </Box>
        </Box>

        <Divider />

        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.65, color: BRAND.slate, fontWeight: 800 }}>
            Border
          </Typography>
          <Box sx={{ display: 'grid', gap: 0.75 }}>
            {['top', 'right', 'bottom', 'left'].map((side) => (
              <Box
                key={side}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '110px 120px minmax(160px,1fr)' },
                  gap: 0.75,
                  alignItems: 'center',
                }}
              >
                <TextField
                  size="small"
                  type="number"
                  label={`${side.charAt(0).toUpperCase() + side.slice(1)} width`}
                  value={valueFor(`border_${side}_width`)}
                  inputProps={{ min: 0 }}
                  onChange={numeric(`border_${side}_width`)}
                />
                <TextField
                  select
                  size="small"
                  label="Style"
                  value={device === 'desktop' ? (valueFor(`border_${side}_style`) || 'solid') : valueFor(`border_${side}_style`)}
                  onChange={(e) => onChange(keyFor(`border_${side}_style`), e.target.value)}
                >
                  {device !== 'desktop' ? <MenuItem value="">Inherit desktop</MenuItem> : null}
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="solid">Solid</MenuItem>
                  <MenuItem value="dashed">Dashed</MenuItem>
                  <MenuItem value="dotted">Dotted</MenuItem>
                  <MenuItem value="double">Double</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  label="Color"
                  value={valueFor(`border_${side}_color`)}
                  placeholder="#e2e8f0"
                  onChange={(e) => onChange(keyFor(`border_${side}_color`), e.target.value)}
                />
              </Box>
            ))}
          </Box>
        </Box>

        <Divider />

        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.65, color: BRAND.slate, fontWeight: 800 }}>
            Corner radius
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' }, gap: 0.75 }}>
            <TextField size="small" type="number" label="Top left" value={valueFor('border_top_left_radius')} inputProps={{ min: 0 }} onChange={numeric('border_top_left_radius')} />
            <TextField size="small" type="number" label="Top right" value={valueFor('border_top_right_radius')} inputProps={{ min: 0 }} onChange={numeric('border_top_right_radius')} />
            <TextField size="small" type="number" label="Bottom right" value={valueFor('border_bottom_right_radius')} inputProps={{ min: 0 }} onChange={numeric('border_bottom_right_radius')} />
            <TextField size="small" type="number" label="Bottom left" value={valueFor('border_bottom_left_radius')} inputProps={{ min: 0 }} onChange={numeric('border_bottom_left_radius')} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const buildTemplateStylePreviewWidgetHtml = (widget = {}, idx = 0, products = [], merchandising = null, showPrices = true) => {
  const component = normalizeWidgetComponent(widget?.component || 'rich_text') || 'rich_text';
  const props = widget?.props && typeof widget.props === 'object' ? widget.props : {};
  const existingClass = String(props.class_name || props.className || '').trim();
  const className = [existingClass, `ps-widget ps-widget--${component}`].filter(Boolean).join(' ');
  const spacingStyles = buildSpacingStyle(props);
  const blockFontFamily = String(props?.font_family || '').trim();
  if (blockFontFamily) spacingStyles.push(`font-family:${blockFontFamily}`);

  if (component === 'catalog_archive') {
    const cards = buildPreviewProductGridHtml({
      props: { page_size: 8, columns_desktop: 4, columns_tablet: 3, columns_mobile: 2 },
      products,
      escapeHtml,
      showPrices,
    });
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)} ps-widget--catalog-archive" style="display:block;${spacingStyles.join(';')}">
      <div class="ps-catalog-archive-preview__bar">
        <div><strong>Product Archive</strong><span>Live WooCommerce shop</span></div>
        <div class="ps-catalog-archive-preview__tools"><span>Filters</span><span>Sort</span></div>
      </div>
      <div class="ps-catalog-archive-preview__layout">
        <aside class="ps-catalog-archive-preview__filters"><strong>Filter products</strong><span>Category</span><span>Brand</span><span>Theme</span><span>Price</span></aside>
        <div class="ps-catalog-archive-preview__products">${cards}</div>
      </div>
    </section>`;
  }

  if (component === 'hero') {
    const slides = Array.isArray(props?.slides) ? props.slides : [];
    const activeSlide = slides[0] || {};
    const title = activeSlide?.h1 || props?.title || 'Hero headline';
    const subtitle = activeSlide?.paragraph || props?.subtitle || 'Hero supporting copy';
    const cta = activeSlide?.button_text || props?.cta_text || '';
    const ctaUrl = activeSlide?.button_url || props?.cta_url || '#';
    const bgUrl = activeSlide?.background_url || props?.background_url || '';
    const widthMode = props?.width_mode === 'contained' ? 'contained' : 'full_bleed';
    const bgOpacityRaw = Number(activeSlide?.background_overlay_opacity ?? props?.background_overlay_opacity ?? 0.45);
    const bgOpacity = Number.isFinite(bgOpacityRaw) ? Math.max(0, Math.min(bgOpacityRaw, 0.95)) : 0.45;
    const textColor = String(activeSlide?.text_color || props?.text_color || (bgUrl ? '#ffffff' : '#0f172a')).trim();
    const headlineColor = String(activeSlide?.headline_text_color || textColor).trim();
    const supportingColor = String(activeSlide?.supporting_text_color || textColor).trim();
    const backgroundSize = ['cover', 'contain', 'auto'].includes(String(activeSlide?.background_size || '')) ? activeSlide.background_size : 'cover';
    const backgroundPosition = String(activeSlide?.background_position || 'center center').trim() || 'center center';
    const buttonSize = ['small', 'medium', 'large'].includes(String(activeSlide?.button_size || '')) ? activeSlide.button_size : 'medium';
    const slidePaddingStyles = [
      ['slide_padding_top', 'padding-top'],
      ['slide_padding_right', 'padding-right'],
      ['slide_padding_bottom', 'padding-bottom'],
      ['slide_padding_left', 'padding-left'],
    ].map(([key, cssKey]) => {
      if (activeSlide?.[key] === '' || activeSlide?.[key] == null) return '';
      const value = Number(activeSlide[key]);
      return Number.isFinite(value) ? `${cssKey}:${value}px !important` : '';
    }).filter(Boolean);
    const heroStyles = [
      `min-height:${Math.max(180, Math.min(1200, Number(props?.min_height || 460)))}px`,
      ...spacingStyles,
      ...slidePaddingStyles,
      bgUrl ? `background-image:linear-gradient(rgba(15,23,42,${bgOpacity}),rgba(15,23,42,${bgOpacity})),url('${escapeHtml(bgUrl)}')` : '',
      bgUrl ? `background-size:${escapeHtml(backgroundSize)} !important` : '',
      bgUrl ? `background-position:${escapeHtml(backgroundPosition)} !important` : '',
      bgUrl ? 'background-repeat:no-repeat !important' : '',
      textColor ? `color:${escapeHtml(textColor)}` : '',
    ].filter(Boolean).join(';');
    const heroStyle = heroStyles ? ` style="${heroStyles}"` : '';
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)} ps-widget--hero-${escapeHtml(widthMode)}"${heroStyle}><div class="ps-hero__grid"><div class="ps-hero__content"><h1 class="ps-hero__title" style="color:${escapeHtml(headlineColor)}">${escapeHtml(title)}</h1><p class="ps-hero__subtitle" style="color:${escapeHtml(supportingColor)}">${escapeHtml(subtitle)}</p>${cta ? `<a class="ps-button ps-button--${escapeHtml(buttonSize)} ps-hero__cta" href="${escapeHtml(ctaUrl)}" title="Links to ${escapeHtml(ctaUrl)}">${escapeHtml(cta)}</a>` : ''}</div></div></section>`;
  }

  if (component === 'product_grid' || component === 'product_carousel') {
    const title = props?.title || 'Products';
    const cards = buildPreviewProductGridHtml({ props, products, escapeHtml, showPrices });
    const productMarkup = component === 'product_carousel'
      ? cards.replace('class="ps-product-grid"', 'class="ps-product-grid ps-product-carousel-preview"')
      : cards;
    const textColor = String(props?.text_color || '').trim();
    const widgetStyle = ['display:block', ...spacingStyles, textColor ? `color:${escapeHtml(textColor)}` : ''].filter(Boolean).join(';');
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}" style="${widgetStyle}"><h3>${escapeHtml(title)}</h3>${productMarkup}</section>`;
  }

  if (component === 'category_grid') {
    const title = props?.heading || props?.title || 'Categories';
    const taxonomy = props?.taxonomy || 'product_category';
    const limit = Number(props?.limit || 8);
    const items = buildPreviewTaxonomyItems({ taxonomy, products, merchandising, limit });
    const fallbackLabels = taxonomy === 'brand'
      ? ['Popular brands', 'Premium brands', 'Everyday brands', 'New brands']
      : taxonomy === 'theme'
        ? ['Employee gifting', 'Events', 'Outdoors', 'Recognition']
        : ['Drinkware', 'Apparel', 'Bags', 'Technology', 'Awards', 'Office'];
    const sourceItems = items.length
      ? items
      : fallbackLabels.slice(0, Math.max(1, Math.min(limit || 6, fallbackLabels.length))).map((label) => ({ label, image: '', count: 0 }));
    const cards = sourceItems.map((item) => `<article class="ps-category-card">${item.image ? `<img class="ps-category-card__image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.label)}" loading="lazy" />` : '<div class="ps-category-card__image ps-category-card__image--placeholder"></div>'}<div class="ps-category-card__name">${escapeHtml(item.label)}</div>${item.count ? `<div class="ps-category-card__count">${item.count} product${item.count === 1 ? '' : 's'}</div>` : ''}</article>`).join('');
    const textColor = String(props?.text_color || '').trim();
    const widgetInlineStyles = [...spacingStyles, textColor ? `color:${escapeHtml(textColor)}` : ''].filter(Boolean).join(';');
    const widgetStyle = widgetInlineStyles ? ` style="${widgetInlineStyles}"` : '';
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}"${widgetStyle}><div class="ps-section-heading"><div><span class="ps-eyebrow">${escapeHtml(taxonomy === 'brand' ? 'Brands' : taxonomy === 'theme' ? 'Themes' : 'Collections')}</span><h3>${escapeHtml(title)}</h3></div></div><div class="ps-category-grid">${cards}</div></section>`;
  }

  if (component === 'button_row') {
    const buttons = Array.isArray(props?.buttons) && props.buttons.length
      ? props.buttons
      : [{ label: props?.label || 'Shop now', url: props?.url || '/shop' }];
    const buttonMarkup = buttons.map((button, buttonIndex) => `<a class="ps-button${buttonIndex ? ' ps-button--secondary' : ''}" href="${escapeHtml(button?.url || button?.href || '#')}">${escapeHtml(button?.label || button?.text || 'Button')}</a>`).join('');
    const textColor = String(props?.text_color || '').trim();
    const widgetInlineStyles = [...spacingStyles, textColor ? `color:${escapeHtml(textColor)}` : ''].filter(Boolean).join(';');
    const widgetStyle = widgetInlineStyles ? ` style="${widgetInlineStyles}"` : '';
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}"${widgetStyle}><div class="ps-button-row">${buttonMarkup}</div></section>`;
  }

  if (component === 'rich_text') {
    const subheading = props?.subheading || props?.eyebrow || '';
    const title = props?.title || 'Section title';
    const body = props?.body || 'Add your message here.';
    const imageUrl = props?.image_url || '';
    const mediaPosition = props?.media_position === 'left' ? 'left' : 'right';
    const contentWidth = Math.max(30, Math.min(70, Number(props?.content_width || 50)));
    const gap = Math.max(0, Math.min(160, Number(props?.gap || 48)));
    const radius = Math.max(0, Math.min(100, Number(props?.image_radius || 18)));
    const aspect = ['1/1','4/3','16/9','3/4'].includes(String(props?.image_aspect || '')) ? props.image_aspect : '4/3';
    const shadow = ['none','soft','medium','strong'].includes(String(props?.image_shadow || '')) ? props.image_shadow : 'medium';
    const content = `<div class="ps-content-media__content">${subheading ? `<div class="ps-content-media__subheading">${escapeHtml(subheading)}</div>` : ''}<h2 class="ps-rich-text__title">${escapeHtml(title)}</h2><div class="ps-rich-text__body"><p>${escapeHtml(body)}</p></div>${props?.cta_label && props?.cta_url ? `<a class="ps-button ps-content-media__cta" href="${escapeHtml(props.cta_url)}">${escapeHtml(props.cta_label)}</a>` : ''}</div>`;
    const media = imageUrl ? `<div class="ps-content-media__media ps-content-media__media--shadow-${escapeHtml(shadow)}" style="border-radius:${radius}px;aspect-ratio:${escapeHtml(aspect)}"><img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(props?.image_alt || '')}" /></div>` : '';
    const widgetInlineStyles = [...spacingStyles].filter(Boolean).join(';');
    const widgetStyle = widgetInlineStyles ? ` style="${widgetInlineStyles}"` : '';
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}"${widgetStyle}><div class="ps-rich-text${imageUrl ? ` ps-content-media ps-content-media--${mediaPosition}` : ''}" style="--ps-content-width:${contentWidth}%;--ps-content-gap:${gap}px;--ps-content-align:${escapeHtml(props?.vertical_align || 'center')}">${mediaPosition === 'left' ? media + content : content + media}</div></section>`;
  }

  if (component === 'cards_grid') {
    const items = Array.isArray(props?.items) ? props.items : [];
    const cards = items.map((item) => `<article class="ps-info-card"><div class="ps-info-card__icon">${escapeHtml(item?.icon || '')}</div><h3 class="ps-info-card__title">${escapeHtml(item?.title || '')}</h3><p>${escapeHtml(item?.body || '')}</p>${item?.link_label ? `<span class="ps-info-card__link">${escapeHtml(item.link_label)} →</span>` : ''}</article>`).join('');
    const style = `--ps-cols-desktop:${Number(props?.columns_desktop || 3)};--ps-cols-tablet:${Number(props?.columns_tablet || 2)};--ps-cols-mobile:${Number(props?.columns_mobile || 1)};`;
    const intro = `${(props?.subheading || props?.eyebrow) ? `<div class="ps-section-intro__subheading">${escapeHtml(props.subheading || props.eyebrow)}</div>` : ''}<h2 class="ps-section-intro__title">${escapeHtml(props?.title || 'Services built around you')}</h2>`;
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}" style="${[...spacingStyles].join(';')}"><div class="ps-modern-section"><div class="ps-section-intro">${intro}</div><div class="ps-cards-grid ps-cards-grid--${escapeHtml(props?.card_style || 'elevated')}" style="${style}">${cards}</div></div></section>`;
  }

  if (component === 'stats') {
    const items = Array.isArray(props?.items) ? props.items : [];
    const stats = items.map((item) => `<div class="ps-stat"><div class="ps-stat__value">${escapeHtml(item?.value || '')}</div><div class="ps-stat__label">${escapeHtml(item?.label || '')}</div></div>`).join('');
    const style = `--ps-cols-desktop:${Number(props?.columns_desktop || 4)};--ps-cols-tablet:${Number(props?.columns_tablet || 2)};--ps-cols-mobile:${Number(props?.columns_mobile || 2)};`;
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}" style="${[...spacingStyles].join(';')}"><div class="ps-modern-section"><div class="ps-section-intro"><h2 class="ps-section-intro__title">${escapeHtml(props?.title || 'Results at a glance')}</h2></div><div class="ps-stats-grid" style="${style}">${stats}</div></div></section>`;
  }

  if (component === 'testimonials') {
    const items = Array.isArray(props?.items) ? props.items : [];
    const cards = items.map((item) => `<figure class="ps-testimonial">${item?.rating ? `<div class="ps-testimonial__rating">${'★'.repeat(Math.max(0,Math.min(5,Number(item.rating)||0)))}</div>` : ''}<blockquote class="ps-testimonial__quote">“${escapeHtml(item?.quote || '')}”</blockquote><figcaption class="ps-testimonial__author">${item?.image_url ? `<img class="ps-testimonial__avatar" src="${escapeHtml(item.image_url)}" alt="" />` : ''}<div><div class="ps-testimonial__name">${escapeHtml(item?.name || '')}</div><div class="ps-testimonial__role">${escapeHtml(item?.role || '')}</div></div></figcaption></figure>`).join('');
    const style = `--ps-cols-desktop:${Number(props?.columns_desktop || 3)};--ps-cols-tablet:${Number(props?.columns_tablet || 2)};--ps-cols-mobile:${Number(props?.columns_mobile || 1)};`;
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}" style="${[...spacingStyles].join(';')}"><div class="ps-modern-section"><div class="ps-section-intro">${(props?.subheading || props?.eyebrow) ? `<div class="ps-section-intro__subheading">${escapeHtml(props.subheading || props.eyebrow)}</div>` : ''}<h2 class="ps-section-intro__title">${escapeHtml(props?.title || 'Trusted by teams like yours')}</h2></div><div class="ps-testimonials-grid" style="${style}">${cards}</div></div></section>`;
  }

  if (component === 'faq') {
    const items = Array.isArray(props?.items) ? props.items : [];
    const rows = items.map((item, itemIndex) => `<details class="ps-faq__item"${props?.open_first !== false && itemIndex === 0 ? ' open' : ''}><summary class="ps-faq__question"><span>${escapeHtml(item?.question || '')}</span><span class="ps-faq__icon">+</span></summary><div class="ps-faq__answer"><p>${escapeHtml(item?.answer || '')}</p></div></details>`).join('');
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}" style="${[...spacingStyles].join(';')}"><div class="ps-modern-section"><div class="ps-section-intro">${(props?.subheading || props?.eyebrow) ? `<div class="ps-section-intro__subheading">${escapeHtml(props.subheading || props.eyebrow)}</div>` : ''}<h2 class="ps-section-intro__title">${escapeHtml(props?.title || 'Frequently asked questions')}</h2></div><div class="ps-faq ps-faq--${escapeHtml(props?.style || 'joined')}">${rows}</div></div></section>`;
  }

  if (component === 'logo_cloud') {
    const items = Array.isArray(props?.items) ? props.items : [];
    const logos = items.map((item) => `<div class="ps-logo-item">${item?.image_url ? `<img src="${escapeHtml(item.image_url)}" alt="${escapeHtml(item?.name || '')}" />` : `<span>${escapeHtml(item?.name || 'Brand')}</span>`}</div>`).join('');
    const style = `--ps-cols-desktop:${Number(props?.columns_desktop || 5)};--ps-cols-tablet:${Number(props?.columns_tablet || 3)};--ps-cols-mobile:${Number(props?.columns_mobile || 2)};`;
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}" style="${[...spacingStyles].join(';')}"><div class="ps-modern-section ps-logo-section${props?.grayscale !== false ? ' is-grayscale' : ''}"><div class="ps-section-intro"><h2 class="ps-section-intro__title">${escapeHtml(props?.title || 'Trusted by leading teams')}</h2></div><div class="ps-logo-grid" style="${style}">${logos}</div></div></section>`;
  }

  if (component === 'cta') {
    const actions = `${props?.primary_label ? `<a class="ps-button">${escapeHtml(props.primary_label)}</a>` : ''}${props?.secondary_label ? `<a class="ps-button ps-button--secondary">${escapeHtml(props.secondary_label)}</a>` : ''}`;
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}" style="${[...spacingStyles].join(';')}"><div class="ps-cta ps-cta--${escapeHtml(props?.style || 'panel')} ps-cta--${escapeHtml(props?.align || 'center')}">${(props?.subheading || props?.eyebrow) ? `<div class="ps-cta__subheading">${escapeHtml(props.subheading || props.eyebrow)}</div>` : ''}<h2 class="ps-cta__title">${escapeHtml(props?.title || 'Ready to get started?')}</h2><div class="ps-cta__body"><p>${escapeHtml(props?.body || '')}</p></div><div class="ps-cta__actions">${actions}</div></div></section>`;
  }

  if (component === 'gallery') {
    const items = Array.isArray(props?.items) ? props.items.filter((item) => item?.image_url) : [];
    const cells = items.map((item) => `<figure class="ps-gallery__item" style="border-radius:${Math.max(0,Number(props?.image_radius || 16))}px"><img src="${escapeHtml(item.image_url)}" alt="${escapeHtml(item?.alt || '')}" />${item?.caption ? `<figcaption class="ps-gallery__caption">${escapeHtml(item.caption)}</figcaption>` : ''}</figure>`).join('');
    const style = `--ps-cols-desktop:${Number(props?.columns_desktop || 3)};--ps-cols-tablet:${Number(props?.columns_tablet || 2)};--ps-cols-mobile:${Number(props?.columns_mobile || 1)};--ps-gallery-gap:${Number(props?.gap || 16)}px;`;
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}" style="${[...spacingStyles].join(';')}"><div class="ps-modern-section"><div class="ps-section-intro">${props?.subheading ? `<div class="ps-section-intro__subheading">${escapeHtml(props.subheading)}</div>` : ''}<h2 class="ps-section-intro__title">${escapeHtml(props?.title || 'See what is possible')}</h2></div><div class="ps-gallery ps-gallery--${escapeHtml(props?.layout || 'grid')}" style="${style}">${cells || '<div class="ps-video__placeholder">Add gallery images</div>'}</div></div></section>`;
  }

  if (component === 'video') {
    const url = props?.video_url || '';
    const media = url ? `<div class="ps-video__placeholder">Video: ${escapeHtml(url)}</div>` : '<div class="ps-video__placeholder">Add a YouTube, Vimeo, MP4 or WebM URL</div>';
    const content = `<div class="ps-video__content">${props?.subheading ? `<div class="ps-section-intro__subheading">${escapeHtml(props.subheading)}</div>` : ''}<h2 class="ps-section-intro__title">${escapeHtml(props?.title || 'Show the story in motion')}</h2>${props?.body ? `<div class="ps-section-intro__description"><p>${escapeHtml(props.body)}</p></div>` : ''}</div>`;
    const mediaBox = `<figure class="ps-video__media" style="border-radius:${Math.max(0,Number(props?.radius || 18))}px">${media}${props?.caption ? `<figcaption class="ps-video__caption">${escapeHtml(props.caption)}</figcaption>` : ''}</figure>`;
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}" style="${[...spacingStyles].join(';')}"><div class="ps-video ps-video--${escapeHtml(props?.media_position || 'right')}" style="--ps-content-width:${Number(props?.content_width || 44)}%;--ps-content-gap:${Number(props?.gap || 48)}px">${props?.media_position === 'left' ? mediaBox + content : content + mediaBox}</div></section>`;
  }

  if (component === 'team') {
    const items = Array.isArray(props?.items) ? props.items : [];
    const cards = items.map((item) => `<article class="ps-team-card">${item?.image_url ? `<img class="ps-team-card__image" src="${escapeHtml(item.image_url)}" alt="${escapeHtml(item?.name || '')}" />` : ''}<div class="ps-team-card__content"><h3 class="ps-team-card__name">${escapeHtml(item?.name || '')}</h3><div class="ps-team-card__role">${escapeHtml(item?.role || '')}</div><p>${escapeHtml(item?.bio || '')}</p></div></article>`).join('');
    const style = `--ps-cols-desktop:${Number(props?.columns_desktop || 3)};--ps-cols-tablet:${Number(props?.columns_tablet || 2)};--ps-cols-mobile:${Number(props?.columns_mobile || 1)};`;
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}" style="${[...spacingStyles].join(';')}"><div class="ps-modern-section"><div class="ps-section-intro">${props?.subheading ? `<div class="ps-section-intro__subheading">${escapeHtml(props.subheading)}</div>` : ''}<h2 class="ps-section-intro__title">${escapeHtml(props?.title || 'Meet the people behind the work')}</h2></div><div class="ps-team-grid ps-team-grid--${escapeHtml(props?.card_style || 'minimal')}" style="${style}">${cards}</div></div></section>`;
  }

  if (component === 'timeline') {
    const items = Array.isArray(props?.items) ? props.items : [];
    const rows = items.map((item) => `<article class="ps-timeline__item"><div class="ps-timeline__marker">${escapeHtml(item?.label || '')}</div><div class="ps-timeline__content"><h3 class="ps-timeline__title">${escapeHtml(item?.title || '')}</h3><div class="ps-timeline__body"><p>${escapeHtml(item?.body || '')}</p></div></div></article>`).join('');
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}" style="${[...spacingStyles].join(';')}"><div class="ps-modern-section"><div class="ps-section-intro">${props?.subheading ? `<div class="ps-section-intro__subheading">${escapeHtml(props.subheading)}</div>` : ''}<h2 class="ps-section-intro__title">${escapeHtml(props?.title || 'A clear path from idea to delivery')}</h2></div><div class="ps-timeline ps-timeline--${escapeHtml(props?.style || 'vertical')}">${rows}</div></div></section>`;
  }

  if (component === 'comparison') {
    const columns = Array.isArray(props?.columns) ? props.columns : [];
    const rows = Array.isArray(props?.rows) ? props.rows : [];
    const head = columns.map((col) => `<th class="${col?.highlight ? 'is-highlight' : ''}">${escapeHtml(col?.label || '')}</th>`).join('');
    const body = rows.map((row) => `<tr><th>${escapeHtml(row?.label || '')}</th>${columns.map((col) => `<td class="${col?.highlight ? 'is-highlight' : ''}">${escapeHtml(row?.[col.key] || '')}</td>`).join('')}</tr>`).join('');
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}" style="${[...spacingStyles].join(';')}"><div class="ps-modern-section"><div class="ps-section-intro">${props?.subheading ? `<div class="ps-section-intro__subheading">${escapeHtml(props.subheading)}</div>` : ''}<h2 class="ps-section-intro__title">${escapeHtml(props?.title || 'Choose the right fit')}</h2></div><div class="ps-comparison-wrap"><table class="ps-comparison"><thead><tr><th>${escapeHtml(props?.first_column_label || 'Feature')}</th>${head}</tr></thead><tbody>${body}</tbody></table></div></div></section>`;
  }

  if (component === 'form') {
    const title = props?.title || 'Contact us';
    const description = props?.description || '';
    const fields = Array.isArray(props?.fields) && props.fields.length ? props.fields : [];
    const fieldMarkup = fields.map((field) => {
      const label = field?.label || field?.key || 'Field';
      const type = field?.type || 'text';
      const input = type === 'textarea'
        ? '<textarea rows="3" disabled></textarea>'
        : type === 'select'
          ? '<select disabled><option>Select…</option></select>'
          : type === 'checkbox'
            ? '<input type="checkbox" disabled />'
            : `<input type="${escapeHtml(type === 'tel' ? 'tel' : type === 'email' ? 'email' : 'text')}" disabled />`;
      return `<label class="ps-form__field"><span class="ps-form__label">${escapeHtml(label)}${field?.required ? ' *' : ''}</span>${input}</label>`;
    }).join('');
    const widgetInlineStyles = [...spacingStyles].filter(Boolean).join(';');
    const widgetStyle = widgetInlineStyles ? ` style="${widgetInlineStyles}"` : '';
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}"${widgetStyle}><div class="ps-form-wrap"><h3 class="ps-form__title">${escapeHtml(title)}</h3>${description ? `<p class="ps-form__description">${escapeHtml(description)}</p>` : ''}<div class="ps-form">${fieldMarkup}<button class="ps-button" disabled>${escapeHtml(props?.submit_label || 'Submit')}</button></div></div></section>`;
  }

  if (component === 'contact_block') {
    const title = props?.title || 'Need help?';
    const cta = props?.cta || 'Our team is here to help you find the right products.';
    const email = props?.email || 'hello@example.com';
    const phone = props?.phone || '1 (800) 555-0199';
    const textColor = String(props?.text_color || '').trim();
    const widgetInlineStyles = [...spacingStyles, textColor ? `color:${escapeHtml(textColor)}` : ''].filter(Boolean).join(';');
    const widgetStyle = widgetInlineStyles ? ` style="${widgetInlineStyles}"` : '';
    return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}"${widgetStyle}><div class="ps-contact-block"><span class="ps-eyebrow">Contact</span><h3 class="ps-contact-block__title">${escapeHtml(title)}</h3><p>${escapeHtml(cta)}</p><div class="ps-contact-block__links"><span>${escapeHtml(email)}</span><span>${escapeHtml(phone)}</span></div></div></section>`;
  }

  const textColor = String(props?.text_color || '').trim();
  const widgetInlineStyles = [...spacingStyles, textColor ? `color:${escapeHtml(textColor)}` : ''].filter(Boolean).join(';');
  const widgetStyle = widgetInlineStyles ? ` style="${widgetInlineStyles}"` : '';
  return `<section data-ps-node="${escapeHtml(widget?.id || idx)}" class="${escapeHtml(className)}"${widgetStyle}><h3>${escapeHtml(props?.title || 'Rich Text')}</h3><p>${escapeHtml(props?.body || 'Add your message here.')}</p></section>`;
};

const buildResponsivePreviewCss = (widgets = []) => {
  const buildRules = (props = {}, suffix = '') => {
    const rules = [];
    const spacing = [
      ['margin_top','margin-top'],['margin_right','margin-right'],['margin_bottom','margin-bottom'],['margin_left','margin-left'],
      ['padding_top','padding-top'],['padding_right','padding-right'],['padding_bottom','padding-bottom'],['padding_left','padding-left'],
    ];
    spacing.forEach(([key, cssKey]) => {
      const raw = props?.[`${key}${suffix}`];
      if (raw === '' || raw == null) return;
      const value = Number(raw);
      if (Number.isFinite(value)) rules.push(`${cssKey}:${cssKey.startsWith('padding-') ? Math.max(0,value) : value}px!important`);
    });
    const bg = String(props?.[`background_color${suffix}`] || '').trim();
    if (bg) rules.push(`background-color:${bg}!important`);
    ['top','right','bottom','left'].forEach((side) => {
      const width = props?.[`border_${side}_width${suffix}`];
      if (width !== '' && width != null && Number.isFinite(Number(width))) rules.push(`border-${side}-width:${Math.max(0,Number(width))}px!important`);
      const style = String(props?.[`border_${side}_style${suffix}`] || '').trim();
      if (style) rules.push(`border-${side}-style:${style}!important`);
      const color = String(props?.[`border_${side}_color${suffix}`] || '').trim();
      if (color) rules.push(`border-${side}-color:${color}!important`);
    });
    [
      ['border_top_left_radius','border-top-left-radius'],
      ['border_top_right_radius','border-top-right-radius'],
      ['border_bottom_right_radius','border-bottom-right-radius'],
      ['border_bottom_left_radius','border-bottom-left-radius'],
    ].forEach(([key, cssKey]) => {
      const raw = props?.[`${key}${suffix}`];
      if (raw === '' || raw == null || !Number.isFinite(Number(raw))) return;
      rules.push(`${cssKey}:${Math.max(0,Number(raw))}px!important`);
    });
    return rules;
  };

  let tablet = '';
  let mobile = '';
  (widgets || []).forEach((widget, idx) => {
    const id = String(widget?.id || idx).replace(/[^a-zA-Z0-9_-]/g, '');
    if (!id) return;
    const selector = `.ps-widget[data-ps-node="${id}"]`;
    const tabletRules = buildRules(widget?.props || {}, '_tablet');
    const mobileRules = buildRules(widget?.props || {}, '_mobile');
    if (tabletRules.length) tablet += `${selector}{${tabletRules.join(';')}}`;
    if (mobileRules.length) mobile += `${selector}{${mobileRules.join(';')}}`;
  });
  return `${tablet ? `@media(max-width:900px){${tablet}}` : ''}${mobile ? `@media(max-width:600px){${mobile}}` : ''}`;
};

const buildTemplateStylePreviewDoc = ({ slot = 'home', widgets = [], products = [], merchandising = null, storeSettings = {}, templateCss = '', customCss = '', brandName = '', primaryColor = '#B91C1C', secondaryColor = '#111827', announcementText = '', logoUrl = '', fontFamily = 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', pages = DEFAULT_SHOWCASE_PAGES, siteChrome = DEFAULT_SITE_CHROME }) => {
  const body = (widgets || []).map((widget, idx) => buildTemplateStylePreviewWidgetHtml(
    widget, idx, products, merchandising, storeSettings?.show_prices !== false
  )).join('')
    || '<section class="ps-widget ps-empty">No widgets in this slot yet.</section>';
  const responsiveAdvancedCss = buildResponsivePreviewCss(widgets);
  const headerLogoWidth = Math.max(40, Math.min(240, Number(siteChrome?.header?.logo_width_px || 180)));
  const headerLogoMaxHeight = Math.max(24, Math.min(150, Number(siteChrome?.header?.logo_max_height_px || 48)));
  const headerTransparentOverlay = siteChrome?.header?.background_mode === 'transparent';
  const headerSticky = !headerTransparentOverlay && siteChrome?.header?.sticky === true;
  const menuFontSize = Math.max(10, Math.min(24, Number(siteChrome?.navigation?.font_size_px || 14)));
  const headerFontFamily = String(siteChrome?.header?.font_family || '').trim();
  const headerColumnLayout = ['balanced','brand_emphasis','menu_emphasis','equal'].includes(siteChrome?.header?.column_layout)
    ? siteChrome.header.column_layout
    : 'balanced';
  const previewNavItems = (() => {
    const customItems = Array.isArray(siteChrome?.navigation?.items) ? siteChrome.navigation.items : [];
    return customItems.length
      ? customItems
      : (Array.isArray(pages) ? pages : []).filter((page) => page?.show_in_menu !== false).map((page) => ({
          label: page?.title || page?.slot,
          url: '/' + String(page?.slug || page?.slot || '').replace(/^\/+/, ''),
        }));
  })();
  const previewHeaderBlocks = Array.isArray(siteChrome?.header?.blocks) && siteChrome.header.blocks.length
    ? siteChrome.header.blocks
    : ['brand','menu','actions'];
  const googleFontsHref = getShowcaseGoogleFontHref(
    fontFamily,
    headerFontFamily,
    (widgets || []).map((widget) => widget?.props?.font_family || '')
  );

  const previewHeaderBlocksHtml = previewHeaderBlocks.map((block) => {
    if (block === 'brand') {
      return `<div class="ps-preview-brand">${logoUrl && siteChrome?.header?.show_logo !== false ? `<img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(brandName || '')}" />` : ''}${siteChrome?.header?.show_brand_name === false || (logoUrl && siteChrome?.header?.show_brand_name_with_logo !== true) ? '' : `<span>${escapeHtml(brandName || '')}</span>`}</div>`;
    }
    if (block === 'menu') {
      return `<nav class="ps-preview-nav">${previewNavItems.map((item) => `<span>${escapeHtml(item?.label || '')}</span>`).join('')}</nav>`;
    }
    if (block === 'actions') {
      const productSearch = siteChrome?.header?.show_product_search === true
        ? '<span class="ps-preview-product-search" aria-label="Product search"><span class="ps-preview-product-search__icon" aria-hidden="true">⌕</span><span class="ps-preview-product-search__placeholder">Search products…</span></span>'
        : '';
      return `<div class="ps-preview-actions">${siteChrome?.header?.show_account === false ? '' : '<span>Account</span>'}${productSearch}${siteChrome?.header?.show_cart === false ? '' : (siteChrome?.header?.cart_display === 'icon' ? '<span class="ps-preview-cart ps-preview-cart--icon" aria-label="Cart"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 4h2l2.3 10.1a2 2 0 0 0 2 1.6h7.9a2 2 0 0 0 1.9-1.4L21 8H7"></path><circle cx="10" cy="20" r="1"></circle><circle cx="18" cy="20" r="1"></circle></svg><span class="ps-preview-cart__count">0</span></span>' : '<span class="ps-preview-cart">Cart<span class="ps-preview-cart__count">0</span></span>')}</div>`;
    }
    if (block === 'search') {
      return '<div class="ps-preview-header-search" aria-label="Search">⌕</div>';
    }
    if (block === 'cta') {
      const label = String(siteChrome?.header?.cta_label || 'Shop now').trim() || 'Shop now';
      return `<a class="ps-preview-header-cta" href="${escapeHtml(siteChrome?.header?.cta_url || '/shop')}">${escapeHtml(label)}</a>`;
    }
    return '';
  }).join('');

  const previewScript = "(function(){"
    + "const source='ps-template-style-preview';"
    + "const root=document.body;"
    + "const pick=function(el){"
    + "if(!el)return '';"
    + "const block=el.closest('[data-ps-node]');"
    + "if(!block)return '';"
    + "const classes=(block.className||'').trim().split(' ').filter(Boolean);"
    + "const specific=classes.find(function(c){return c.indexOf('ps-widget--')===0;})||classes.find(function(c){return c.indexOf('ps-')===0;})||classes[0]||'';"
    + "return specific?'.promoshowcase-slot .'+specific:'.promoshowcase-slot';"
    + "};"
    + "const nodeId=function(el){const block=el&&el.closest?el.closest('[data-ps-node]'):null;return block?String(block.getAttribute('data-ps-node')||''):'';};"
    + "root.addEventListener('mouseover',function(e){"
    + "const selector=pick(e.target);if(!selector)return;"
    + "window.parent.postMessage({source:source,type:'hover',selector:selector},'*');"
    + "});"
    + "root.addEventListener('click',function(e){"
    + "if(e.target&&e.target.closest&&e.target.closest('[data-ps-inline-edit],[contenteditable=\\\"true\\\"]'))return;"
    + "const selector=pick(e.target);if(!selector)return;"
    + "e.preventDefault();e.stopPropagation();"
    + "window.parent.postMessage({source:source,type:'select',selector:selector,nodeId:nodeId(e.target)},'*');"
    + "},true);"
    + "})();";

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  ${googleFontsHref ? `<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link rel="stylesheet" data-ps-google-fonts="true" href="${googleFontsHref}">` : ''}
  <style>
    html,body{width:100%;max-width:100%;margin:0;overflow-x:clip}
    html{box-sizing:border-box}
    *,*::before,*::after{box-sizing:inherit}
    body{font-family:${sanitizeCssFontFamily(fontFamily)};background:#f5f7fb;color:#0f172a}
    img,video,svg,canvas{max-width:100%;height:auto}
    .promoshowcase-slot{width:100%;max-width:100%;min-width:0;overflow-x:clip;--ps-primary:${escapeHtml(primaryColor || '#B91C1C')};--ps-secondary:${escapeHtml(secondaryColor || '#111827')};--ps-line:rgba(15,23,42,.09);--ps-muted:#64748b}
    .ps-preview-announcement{width:100%;max-width:100%;background:var(--ps-primary);color:#fff;padding:7px clamp(12px,2.2vw,20px);text-align:center;font-size:12px;font-weight:700}
    .ps-preview-header{width:100%;max-width:100%;min-height:64px;background:#fff;border-bottom:1px solid var(--ps-line);position:relative;z-index:30;overflow:hidden;font-family:var(--ps-header-font,inherit)}
    .ps-preview-header.is-sticky{position:sticky!important;top:0!important;z-index:1000!important}
    .ps-preview-header.is-transparent-overlay{position:absolute!important;top:0!important;left:0!important;right:0!important;width:100%!important;max-width:none!important;z-index:1000!important;background:transparent!important;border-bottom-color:transparent!important;overflow:visible}
    .ps-preview-header.is-transparent-overlay .ps-preview-header__inner{background:transparent!important}
    .ps-preview-header.is-transparent-overlay .ps-preview-nav,.ps-preview-header.is-transparent-overlay .ps-preview-actions,.ps-preview-header.is-transparent-overlay .ps-preview-brand,.ps-preview-header.is-transparent-overlay .ps-preview-header-search{color:#fff!important;text-shadow:0 1px 10px rgba(0,0,0,.35)}
    .ps-preview-header.is-transparent-overlay .ps-preview-cart--icon{color:#fff}
    .ps-preview-header__inner{width:100%;max-width:100%;min-width:0;min-height:64px;display:flex;align-items:center;gap:clamp(8px,1.5vw,18px);padding:0 clamp(12px,2.5vw,24px);font-family:inherit}
    .ps-preview-brand{flex:0 0 auto}.ps-preview-nav{flex:1 1 auto}.ps-preview-actions,.ps-preview-header-search,.ps-preview-header-cta{flex:0 0 auto}
    .ps-preview-header__inner[data-column-layout="brand_emphasis"] .ps-preview-brand{min-width:220px}
    .ps-preview-header__inner[data-column-layout="menu_emphasis"] .ps-preview-nav{flex-grow:2}
    .ps-preview-header__inner[data-column-layout="equal"]>*{flex:1 1 0}
    .ps-preview-header-search{display:grid;place-items:center;width:34px;height:34px;border-radius:8px;font-size:22px;font-weight:800}
    .ps-preview-header-cta{display:inline-flex;align-items:center;justify-content:center;padding:9px 14px;border-radius:9px;background:var(--ps-primary);color:#fff;text-decoration:none;font-size:12px;font-weight:800}
    .ps-preview-chrome-contained{width:100%;max-width:1180px;margin-inline:auto}
    .ps-preview-chrome-full{width:100%;max-width:100%;margin:0}
    .ps-preview-brand{display:flex;align-items:center;gap:10px;min-width:0;flex:0 1 auto;font-weight:850}
    .ps-preview-brand img{display:block;width:min(${headerLogoWidth}px,35vw);max-width:100%;max-height:${headerLogoMaxHeight}px;height:auto;object-fit:contain}
    .ps-preview-nav{display:flex;align-items:center;justify-content:center;gap:clamp(8px,1.5vw,18px);min-width:0;flex:1 1 auto;color:#475569;font-size:${menuFontSize}px;font-weight:700;white-space:normal;flex-wrap:wrap}
    .ps-preview-actions{display:flex;align-items:center;justify-content:flex-end;gap:10px;min-width:0;flex:0 1 auto;font-size:12px;font-weight:700;white-space:nowrap}
    .ps-preview-product-search{display:inline-flex;align-items:center;gap:7px;width:min(220px,24vw);min-width:150px;height:36px;padding:0 10px;border:1px solid var(--ps-line);border-radius:9px;background:#fff;color:#64748b;white-space:nowrap;overflow:hidden}
    .ps-preview-product-search__icon{flex:0 0 auto;font-size:16px;line-height:1}
    .ps-preview-product-search__placeholder{overflow:hidden;text-overflow:ellipsis}
    .ps-preview-cart{display:inline-flex;align-items:center;gap:6px}
    .ps-preview-cart--icon{position:relative;width:34px;height:34px;display:inline-grid;place-items:center;border-radius:8px}
    .ps-preview-cart--icon svg{width:20px;height:20px;display:block;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}
    .ps-preview-cart__count{display:inline-grid;place-items:center;min-width:18px;height:18px;padding:0 5px;border-radius:999px;background:var(--ps-primary);color:#fff;font-size:10px;line-height:1}
    .ps-preview-cart--icon .ps-preview-cart__count{position:absolute;top:-4px;right:-5px}
    .ps-preview-shell{position:relative;width:100%;max-width:1180px;min-width:0;margin:0 auto;padding:0 clamp(12px,2.5vw,24px) 48px}
    .ps-shell__body,.ps-widget,.ps-modern-section{width:100%;max-width:100%;min-width:0}
    .ps-shell__details{
      position:absolute;
      top:8px;
      left:8px;
      z-index:1200;
      margin:0;
      padding:6px 9px;
      border:1px solid rgba(148,163,184,.28);
      border-radius:999px;
      background:rgba(15,23,42,.88);
      box-shadow:0 6px 18px rgba(15,23,42,.16);
      color:#fff;
      font-size:10px;
      font-weight:750;
      text-transform:uppercase;
      letter-spacing:.1em;
      opacity:0;
      transform:translateY(-3px);
      transition:opacity .14s ease,transform .14s ease;
      pointer-events:auto;
      cursor:default;
    }
    .ps-shell__details:hover,.ps-shell__details:focus-visible{opacity:.92;transform:translateY(0);outline:none}
    @media(max-width:760px){
      .ps-preview-header__inner{min-height:auto;padding-top:10px;padding-bottom:10px;flex-wrap:wrap}
      .ps-preview-nav{order:10;flex:1 0 100%;justify-content:flex-start;overflow:hidden}
    }
    @media(max-width:460px){
      .ps-preview-nav{gap:10px;font-size:12px}
      .ps-preview-actions{gap:7px;font-size:11px}
      .ps-preview-product-search{width:150px;min-width:120px}
    }
    .ps-widget{margin:0 0 26px}.ps-widget h1,.ps-widget h2,.ps-widget h3{margin-top:0}
    .ps-button{padding:10px 15px;border-radius:9px;border:0;background:var(--ps-primary);color:#fff;cursor:pointer;text-decoration:none;display:inline-flex;width:auto;max-width:max-content;align-self:flex-start;font-weight:750}.ps-button--small{padding:7px 11px;font-size:13px}.ps-button--medium{padding:10px 15px;font-size:14px}.ps-button--large{padding:13px 20px;font-size:16px}.ps-button--secondary{background:#fff;color:#0f172a;border:1px solid var(--ps-line)}
    .ps-button-row{display:flex;gap:10px;flex-wrap:wrap}
    .ps-widget--hero{min-height:300px;display:grid;align-content:stretch;gap:0;padding:0;border-radius:18px;background:linear-gradient(135deg,#0f172a,#334155);overflow:hidden;box-shadow:0 18px 45px rgba(15,23,42,.12);width:100%;max-width:none}
    .ps-widget--hero .ps-hero__grid{width:100%;min-height:inherit;display:grid;align-content:end;padding:34px}
    .ps-widget--hero .ps-hero__content{width:min(100%,760px)}
    .ps-widget--hero h1{font-size:clamp(34px,5vw,62px);line-height:.98;letter-spacing:-.045em;max-width:760px}.ps-widget--hero p{max-width:650px;font-size:16px;line-height:1.6}
    .ps-widget--hero-contained{width:100%;max-width:100%}
    .ps-widget--hero-full_bleed{width:100vw;max-width:none;margin-left:calc(50% - 50vw);margin-right:calc(50% - 50vw);border-radius:0}
    .ps-widget--hero-full_bleed .ps-hero__grid{width:min(100% - 32px,1180px);margin-inline:auto;padding-left:0;padding-right:0}
    @media(max-width:600px){.ps-widget--hero .ps-hero__grid{padding:26px 20px}.ps-widget--hero-full_bleed .ps-hero__grid{width:calc(100% - 24px);padding-left:0;padding-right:0}}
    .ps-section-heading{display:flex;justify-content:space-between;align-items:end;margin-bottom:12px}.ps-section-heading h3{margin:4px 0 0;font-size:22px}.ps-eyebrow{display:block;color:var(--ps-primary);font-size:10px;font-weight:850;text-transform:uppercase;letter-spacing:.14em}
    .ps-category-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:12px}
    .ps-category-card{overflow:hidden;border:1px solid var(--ps-line);border-radius:14px;background:#fff;box-shadow:0 8px 20px rgba(15,23,42,.04)}.ps-category-card__image{display:block;width:100%;aspect-ratio:1.28/1;object-fit:cover;background:linear-gradient(135deg,#eef2ff,#f8fafc)}.ps-category-card__image--placeholder{background:linear-gradient(135deg,color-mix(in srgb,var(--ps-primary) 10%,#fff),#f8fafc)}.ps-category-card__name{padding:11px 12px 3px;font-weight:800}.ps-category-card__count{padding:0 12px 12px;color:var(--ps-muted);font-size:11px}
    ${responsiveAdvancedCss}
    .ps-widget--catalog-archive{width:100%;max-width:1180px;margin-inline:auto;padding:22px 0}
    .ps-catalog-archive-preview__bar{display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:16px}
    .ps-catalog-archive-preview__bar>div:first-child{display:grid;gap:2px}
    .ps-catalog-archive-preview__bar span{color:#64748b;font-size:12px}
    .ps-catalog-archive-preview__tools{display:flex;gap:8px}
    .ps-catalog-archive-preview__tools span{padding:7px 10px;border:1px solid var(--ps-line);border-radius:8px;background:#fff;color:#334155}
    .ps-catalog-archive-preview__layout{display:grid;grid-template-columns:minmax(170px,220px) minmax(0,1fr);gap:20px}
    .ps-catalog-archive-preview__filters{display:grid;align-content:start;gap:8px;padding:14px;border:1px solid var(--ps-line);border-radius:12px;background:#f8fafc}
    .ps-catalog-archive-preview__filters span{padding:8px 0;border-bottom:1px solid var(--ps-line);font-size:12px;color:#475569}
    .ps-catalog-archive-preview__products{min-width:0}
    @media(max-width:760px){.ps-catalog-archive-preview__layout{grid-template-columns:1fr}.ps-catalog-archive-preview__filters{display:none}}
    .ps-product-grid{display:grid;grid-template-columns:repeat(var(--ps-product-cols-desktop),minmax(0,1fr));gap:12px;width:100%}
    @media(max-width:900px){.ps-product-grid{grid-template-columns:repeat(var(--ps-product-cols-tablet),minmax(0,1fr))}}
    @media(max-width:600px){.ps-product-grid{grid-template-columns:repeat(var(--ps-product-cols-mobile),minmax(0,1fr))}}
    .ps-card{padding:12px;border:1px solid var(--ps-line);border-radius:12px;background:#fff}
    .ps-product-card{overflow:hidden;border:1px solid var(--ps-line);border-radius:14px;background:#fff;box-shadow:0 8px 20px rgba(15,23,42,.04);transition:transform .18s ease,box-shadow .18s ease}.ps-product-card:hover{transform:translateY(-2px);box-shadow:0 14px 28px rgba(15,23,42,.09)}
    .ps-product-card__image{display:block;width:100%;aspect-ratio:1/1;object-fit:contain;background:#fff}
    .ps-product-card__name{padding:10px 10px 2px;font-weight:700;line-height:1.3}
    .ps-product-card__supplier,.ps-product-card__price{padding:2px 10px 10px;color:#64748b;font-size:12px}
    /* Match source-backed live WordPress product widgets. */
    .ps-product-card{display:flex;flex-direction:column}
    .ps-product-card__media{position:relative;display:grid;place-items:center;aspect-ratio:1;margin:11px 11px 0;background:#f6f8fb;border-radius:12px;overflow:hidden}
    .ps-product-card__media .ps-product-card__image{width:100%;height:100%;aspect-ratio:auto;padding:12px;object-fit:contain;background:none}
    .ps-product-card__image-empty{font-size:12px;color:#64748b}.ps-product-card__photos{position:absolute;bottom:9px;right:9px;padding:4px 8px;border-radius:16px;background:#fff;font-size:11px;font-weight:750}
    .ps-product-card__details{display:flex;flex:1;flex-direction:column;padding:14px;min-width:0}
    .ps-product-card__source{color:#64748b;font-size:11px;font-weight:680;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .ps-product-card__details .ps-product-card__name{padding:0;margin:6px 0;color:#142033;font-size:14px;line-height:1.35;font-weight:800}
    .ps-product-card__description{display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;overflow:hidden;color:#64748b;font-size:12px;line-height:1.4}
    .ps-product-card__facts{display:flex;flex-wrap:wrap;gap:5px;margin:4px 0 12px}
    .ps-product-card__facts span{padding:5px 8px;background:#f6f8fb;border:1px solid var(--ps-line);border-radius:8px;font-size:11px}
    .ps-product-card__details .ps-product-card__price{display:flex;flex-direction:column;align-items:flex-start;gap:3px;margin-top:auto;padding:10px 0 0;border-top:1px solid var(--ps-line)}
    .ps-product-card__price-prefix,.ps-product-card__price small{color:#64748b;font-size:10px}
    .ps-product-card__price strong{color:var(--ps-primary);font-size:17px;font-weight:820}
    .ps-product-card__colors{display:flex;align-items:center;flex-wrap:wrap;gap:5px;margin-top:10px}
    .ps-product-card__swatch{width:16px;height:16px;border:1px solid #cbd5e1;border-radius:50%;background:var(--ps-swatch)}
    .ps-product-card__colors-rest{font-size:11px;color:#64748b}
    .ps-product-card__action{display:flex;justify-content:space-between;align-items:center;margin-top:12px;padding:10px 12px;border-radius:8px;background:var(--ps-primary);color:#fff;font-size:12px;font-weight:790}
    .ps-product-grid__empty{grid-column:1/-1;padding:18px;border:1px dashed rgba(0,0,0,.18);border-radius:10px;color:#64748b;text-align:center}
    .ps-product-carousel-preview{display:flex;overflow:hidden}.ps-product-carousel-preview .ps-product-card{min-width:calc((100% - 30px)/4)}
    .ps-contact-block{padding:26px;border:1px solid var(--ps-line);border-radius:16px;background:linear-gradient(135deg,#fff,#f8fafc)}.ps-contact-block__links{display:flex;gap:16px;flex-wrap:wrap;color:var(--ps-primary);font-weight:750;font-size:13px}
    .ps-gallery{display:grid;grid-template-columns:repeat(var(--ps-cols-desktop),minmax(0,1fr));gap:var(--ps-gallery-gap,16px)}.ps-gallery__item{position:relative;margin:0;overflow:hidden;background:#f8fafc}.ps-gallery__item img{display:block;width:100%;aspect-ratio:4/3;object-fit:cover}.ps-gallery__caption{position:absolute;left:8px;right:8px;bottom:8px;padding:7px 9px;border-radius:9px;background:rgba(15,23,42,.72);color:#fff;font-size:12px}.ps-video{display:grid;grid-template-columns:minmax(0,var(--ps-content-width,44%)) minmax(0,1fr);gap:var(--ps-content-gap,48px);align-items:center}.ps-video--left{grid-template-columns:minmax(0,1fr) minmax(0,var(--ps-content-width,44%))}.ps-video__placeholder{display:grid;place-items:center;aspect-ratio:16/9;padding:22px;background:#0f172a;color:#cbd5e1;text-align:center}.ps-video__media{margin:0;overflow:hidden}.ps-video__caption{padding:8px 10px;background:#fff;color:var(--ps-muted);font-size:12px}.ps-team-grid{display:grid;grid-template-columns:repeat(var(--ps-cols-desktop),minmax(0,1fr));gap:18px}.ps-team-card{overflow:hidden;border-radius:16px;background:#fff}.ps-team-grid--cards .ps-team-card{border:1px solid var(--ps-line)}.ps-team-card__image{width:100%;aspect-ratio:4/3;object-fit:cover}.ps-team-card__content{padding:15px}.ps-team-card__name{margin:0}.ps-team-card__role{color:var(--ps-primary);font-weight:800}.ps-timeline__item{display:grid;grid-template-columns:auto 1fr;gap:16px;padding-bottom:22px}.ps-timeline__marker{display:grid;place-items:center;min-width:42px;height:42px;padding:0 8px;border-radius:999px;background:var(--ps-primary);color:#fff;font-weight:900}.ps-timeline--cards{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px}.ps-timeline--cards .ps-timeline__item{display:block;padding:18px;border:1px solid var(--ps-line);border-radius:14px}.ps-comparison-wrap{overflow:auto;border:1px solid var(--ps-line);border-radius:14px}.ps-comparison{width:100%;min-width:620px;border-collapse:collapse;background:#fff}.ps-comparison th,.ps-comparison td{padding:12px;border:1px solid var(--ps-line);text-align:left}.ps-comparison .is-highlight{background:color-mix(in srgb,var(--ps-primary) 7%,#fff)}
        .ps-section-intro{max-width:760px;margin:0 0 24px}.ps-section-intro__subheading,.ps-cta__subheading{color:var(--ps-primary);font-size:11px;font-weight:850;text-transform:uppercase;letter-spacing:.12em;margin-bottom:8px}.ps-section-intro__title,.ps-cta__title{font-size:clamp(28px,4vw,48px);line-height:1.04;letter-spacing:-.04em;margin:0}.ps-cards-grid,.ps-stats-grid,.ps-testimonials-grid,.ps-logo-grid{display:grid;grid-template-columns:repeat(var(--ps-cols-desktop),minmax(0,1fr));gap:16px}.ps-info-card,.ps-testimonial{padding:22px;border:1px solid var(--ps-line);border-radius:16px;background:#fff}.ps-cards-grid--elevated .ps-info-card,.ps-testimonial{box-shadow:0 12px 28px rgba(15,23,42,.07)}.ps-info-card__icon{display:inline-grid;place-items:center;min-width:40px;height:40px;padding:0 9px;border-radius:12px;background:color-mix(in srgb,var(--ps-primary) 10%,white);color:var(--ps-primary);font-size:12px;font-weight:900}.ps-info-card__title{margin:14px 0 8px}.ps-info-card p,.ps-testimonial__role,.ps-stat__label{color:var(--ps-muted)}.ps-info-card__link{display:inline-block;margin-top:12px;color:var(--ps-primary);font-weight:800}.ps-stat{padding:20px 14px;border-top:1px solid var(--ps-line)}.ps-stat__value{font-size:clamp(30px,5vw,58px);font-weight:900;letter-spacing:-.05em}.ps-testimonial{margin:0}.ps-testimonial__rating{color:#f59e0b}.ps-testimonial__quote{margin:12px 0;line-height:1.65}.ps-testimonial__author{display:flex;align-items:center;gap:10px}.ps-testimonial__avatar{width:40px;height:40px;border-radius:50%;object-fit:cover}.ps-testimonial__name{font-weight:850}.ps-faq--joined{border:1px solid var(--ps-line);border-radius:14px;overflow:hidden}.ps-faq__item{border-bottom:1px solid var(--ps-line)}.ps-faq__question{display:flex;justify-content:space-between;gap:18px;padding:17px;cursor:pointer;font-weight:850}.ps-faq__answer{padding:0 17px 17px;color:var(--ps-muted)}.ps-logo-item{min-height:84px;display:grid;place-items:center;padding:16px;border:1px solid var(--ps-line);border-radius:12px;background:#fff}.ps-logo-item img{max-width:100%;max-height:44px}.ps-logo-section.is-grayscale img{filter:grayscale(1);opacity:.72}.ps-cta{padding:clamp(30px,6vw,64px);border-radius:22px}.ps-cta--panel{background:linear-gradient(135deg,color-mix(in srgb,var(--ps-primary) 12%,#fff),#fff);border:1px solid color-mix(in srgb,var(--ps-primary) 20%,transparent)}.ps-cta--center{text-align:center}.ps-cta__actions{display:flex;gap:10px;justify-content:inherit;flex-wrap:wrap;margin-top:20px}.ps-cta--center .ps-cta__actions{justify-content:center}
    @media(max-width:900px){.ps-cards-grid,.ps-stats-grid,.ps-testimonials-grid,.ps-logo-grid,.ps-gallery,.ps-team-grid{grid-template-columns:repeat(var(--ps-cols-tablet),minmax(0,1fr))}.ps-video,.ps-video--left{grid-template-columns:1fr}.ps-timeline--cards{grid-template-columns:1fr 1fr}}
    @media(max-width:600px){.ps-cards-grid,.ps-stats-grid,.ps-testimonials-grid,.ps-logo-grid,.ps-gallery,.ps-team-grid{grid-template-columns:repeat(var(--ps-cols-mobile),minmax(0,1fr))}.ps-timeline--cards{grid-template-columns:1fr}}
    .ps-preview-footer{background:#0f172a;color:#fff;padding:30px 24px}.ps-preview-footer__inner{max-width:1180px;margin:0 auto;display:flex;justify-content:space-between;gap:20px}.ps-preview-footer__muted{color:rgba(255,255,255,.58);font-size:12px}.ps-preview-footer__watermark{margin-top:8px;color:rgba(255,255,255,.52);font-size:11px}.ps-preview-footer__watermark a{color:rgba(255,255,255,.78);font-weight:750;text-decoration:none}.ps-preview-footer__watermark a:hover{text-decoration:underline}
    [data-ps-node]{position:relative;cursor:pointer}
    [data-ps-node]:hover{outline:2px dashed var(--ps-primary);outline-offset:2px}
    ${templateCss || ''}
    ${customCss || ''}
    /* Header typography is an explicit site-chrome override. Keep this after
       template/global/custom CSS so Site Font remains the fallback, never the
       winner, when a Header Font Family is selected. */
    .ps-preview-header[style*="--ps-header-font"],
    .ps-preview-header[style*="--ps-header-font"] *,
    .ps-preview-header[style*="--ps-header-font"] button,
    .ps-preview-header[style*="--ps-header-font"] input,
    .ps-preview-header[style*="--ps-header-font"] select,
    .ps-preview-header[style*="--ps-header-font"] textarea{
      font-family:var(--ps-header-font)!important;
    }
    /* Builder preview layout guarantees: keep authored sections inside the
       iframe viewport. Components that truly require horizontal scrolling
       should own it locally (for example .ps-comparison-wrap). */
    .ps-product-grid,.ps-category-grid,.ps-cards-grid,.ps-stats-grid,.ps-testimonials-grid,.ps-logo-grid,.ps-gallery,.ps-team-grid,.ps-video,.ps-timeline,.ps-content-media{min-width:0;max-width:100%}
    .ps-product-card,.ps-category-card,.ps-info-card,.ps-testimonial,.ps-team-card{min-width:0;max-width:100%}
    .ps-comparison-wrap{max-width:100%;overflow-x:auto;overscroll-behavior-x:contain}
    /* Builder preview layout guarantees: block wrappers stay full-row and
       responsive column controls always drive the actual product grid. */
    .ps-widget--product_grid,.ps-widget--product_carousel{
      display:block !important;
      width:100% !important;
      max-width:none !important;
    }
    .ps-widget--product_grid > h3,.ps-widget--product_carousel > h3{
      display:block !important;
      width:100% !important;
    }
    .ps-widget--product_grid .ps-product-grid,.ps-widget--product_carousel .ps-product-grid{
      display:grid !important;
      width:100% !important;
      max-width:none !important;
      grid-template-columns:repeat(var(--ps-product-cols-desktop,4),minmax(0,1fr)) !important;
      grid-auto-flow:row !important;
      align-items:stretch !important;
      gap:14px !important;
    }
    .ps-widget--product_grid .ps-product-card,.ps-widget--product_carousel .ps-product-card{
      width:100% !important;
      max-width:none !important;
      min-width:0 !important;
      margin:0 !important;
    }
    @media(max-width:900px){
      .ps-widget--product_grid .ps-product-grid,.ps-widget--product_carousel .ps-product-grid{
        grid-template-columns:repeat(var(--ps-product-cols-tablet,3),minmax(0,1fr)) !important;
      }
    }
    @media(max-width:600px){
      .ps-widget--product_grid .ps-product-grid,.ps-widget--product_carousel .ps-product-grid{
        grid-template-columns:repeat(var(--ps-product-cols-mobile,2),minmax(0,1fr)) !important;
      }
    }
  </style>
</head>
<body>
  <div class="promoshowcase-slot promoshowcase-slot-${escapeHtml(slot)}" data-slot="${escapeHtml(slot)}" style="--ps-primary:${escapeHtml(primaryColor || '#B91C1C')};--ps-secondary:${escapeHtml(secondaryColor || '#111827')};">
    ${announcementText && siteChrome?.header?.show_announcement !== false && siteChrome?.header?.enabled !== false ? `<div class="ps-preview-announcement"><div class="${siteChrome?.header?.content_width === 'full' ? 'ps-preview-chrome-full' : 'ps-preview-chrome-contained'}">${escapeHtml(announcementText)}</div></div>` : ''}
    ${siteChrome?.header?.enabled === false ? '' : `<header class="ps-preview-header${headerSticky ? ' is-sticky' : ''}${headerTransparentOverlay ? ' is-transparent-overlay' : ''}"${headerFontFamily ? ` style="--ps-header-font:${escapeHtml(sanitizeCssFontFamily(headerFontFamily))}"` : ''}><div class="${siteChrome?.header?.content_width === 'full' ? 'ps-preview-chrome-full' : 'ps-preview-chrome-contained'} ps-preview-header__inner" data-column-layout="${escapeHtml(headerColumnLayout)}">${previewHeaderBlocksHtml}</div></header>`}
    <main class="ps-preview-shell">
      <div class="ps-shell__details" tabindex="0" title="Current preview page">Previewing ${escapeHtml(slot)} page</div>
      <div class="ps-shell__body">${body}</div>
    </main>
    ${siteChrome?.footer?.enabled === false ? '' : `<footer class="ps-preview-footer"><div class="${siteChrome?.footer?.content_width === 'full' ? 'ps-preview-chrome-full' : 'ps-preview-chrome-contained'} ps-preview-footer__inner"><div><strong>${escapeHtml(brandName || '')}</strong><div class="ps-preview-footer__muted">${escapeHtml(siteChrome?.footer?.text || (brandName ? `© ${new Date().getFullYear()} ${brandName}` : ''))}</div>${siteChrome?.footer?.show_elite_core_watermark === false ? '' : '<div class="ps-preview-footer__watermark">Powered by <a href="https://elitecore.ca" target="_blank" rel="noopener noreferrer">Elite Core</a></div>'}</div><div class="ps-preview-footer__muted">${(Array.isArray(siteChrome?.footer?.items) && siteChrome.footer.items.length ? siteChrome.footer.items : (Array.isArray(pages) ? pages.filter((page) => page?.show_in_menu !== false).map((page) => ({ label: page?.title || page?.slot })) : [])).map((item) => escapeHtml(item?.label || '')).filter(Boolean).join(' · ')}</div></div></footer>`}
  </div>
  <script>${previewScript}</script>
</body>
</html>`;
};


const PRODUCT_ADDONS = [
  {
    title: "S&S Activewear",
    description: "Bring in S&S catalog data for on-demand promo merchandising.",
    group: "catalogues",
  },
  {
    title: "Stormtech",
    description: "Sync Stormtech catalog content to highlight premium apparel.",
    group: "catalogues",
  },
  {
    title: "ASI Products",
    description: "Connect to the ASI product feed for expansive catalog coverage.",
    group: "products",
  },
  {
    title: "SAGE Products",
    description: "SAGE product import source is staged for launch.",
    group: "products",
    comingSoon: true,
  },
  {
    title: "Custom Products",
    description: "Showcase your curated in-house product collection.",
    group: "products",
  },
];

const DISTRIBUTOR_ADDONS = [
  {
    key: "smart-merchandising",
    title: "Smart Merchandising",
    description:
      "Use PromoWorld-derived categories, themes, brands, suppliers, colors, and sizes as a reusable merchandising layer for collections, filters, and site experiences.",
    available: true,
  },
  {
    title: "Jersey & Apparel Builder",
    description:
      "Interactive builder for uniforms, teamwear, and branded apparel with live personalization.",
  },
  {
    title: "AI Client Store Generator",
    description:
      "Instantly create branded client stores with auto-generated layouts, collections, and publish-ready output.",
  },
  {
    title: "Campaign & Drop Manager",
    description:
      "Time-based promotional campaigns with limited drops, pre-orders, and automated open/close schedules.",
  },
  {
    title: "Advanced Product Personalization",
    description:
      "Text, image, and logo uploads per item with conditional personalization and live previews.",
  },
  {
    title: "Client Approval Workflows",
    description:
      "Multi-level proof approvals with history tied to orders and purchase orders.",
  },
  {
    title: "Program-Based Pricing Engine",
    description:
      "Contract pricing, volume tiers, and department rules with built-in margin protection.",
  },
  {
    title: "Lead Capture & Conversion Tools",
    description:
      "Branded lead forms and campaign landing pages that flow directly into Core ERP.",
  },
  {
    title: "Client Portal & Re-Order Hub",
    description:
      "Client-only dashboards with one-click reorders and centralized approvals and invoices.",
  },
  {
    title: "AI Sales & Merchandising Assistant",
    description:
      "AI-recommended products, smart bundles, and automated upsell suggestions inside stores.",
  },
];


const clampUrl = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (!/^https?:\/\//i.test(raw)) return `https://${raw}`;
  return raw;
};

// ✅ Elite Core (red / white / orange) palette
const BRAND = {
  red: "#C3171D",
  orange: "#FF6A3D",
  white: "#FFFFFF",
  ink: "#111827",
  slate: "#475569",
  line: "#E5E7EB",
  soft: "#F8FAFC",
  card: "rgba(255,255,255,0.86)",
};

const styles = {
  pageWrap: { p: 0, width: "100%",
},

  heroPanel: {
    position: "relative",
    borderRadius: 0,
    width: "100%",
    overflow: "hidden",
    border: `1px solid ${BRAND.line}`,
    background: `
      radial-gradient(900px 420px at 10% 5%, rgba(195,23,29,0.10), transparent 55%),
      radial-gradient(900px 420px at 90% 0%, rgba(255,106,61,0.10), transparent 55%),
      linear-gradient(180deg, ${BRAND.soft}, #ffffff)
    `,
    boxShadow: "0 16px 40px rgba(17,24,39,0.08)",
  },

  animatedGrid: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    opacity: 0.35,
    backgroundImage: `
      linear-gradient(to right, rgba(17,24,39,0.04) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(17,24,39,0.03) 1px, transparent 1px)
    `,
    backgroundSize: "52px 52px",
    maskImage:
      "radial-gradient(700px 320px at 50% 0%, black 55%, transparent 100%)",
    animation: "gridFloat 10s ease-in-out infinite",
  },

  heroHeaderRow: {
    display: "flex",
    position: "relative",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    px: { xs: 2, md: 3 },
    pt: { xs: 2, md: 3 },
    pb: 1,
  },

  heroTitle: {
    fontWeight: 900,
    letterSpacing: 0.2,
    color: BRAND.ink,
  },

  heroSub: {
    color: BRAND.slate,
    fontSize: 13,
  },

  heroBody: {
    px: { xs: 2, md: 3 },
    pb: { xs: 2, md: 3 },
    display: "grid",
    gap: 2,
  },

  // ✅ Tabs: gray/white with RED active indicator
  tabBar: {
    mt: 1,
    fontSize: 27,
    borderBottom: `1px solid ${BRAND.line}`,
    "& .MuiTabs-indicator": {
      height: 3,
      borderRadius: 0,
      background: BRAND.red,
    },
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 800,
      minHeight: 44,
      color: "#6B7280",
    },
    "& .Mui-selected": {
      color: BRAND.ink,
    },
  },

  glassCard: {
    borderRadius: 0,
    border: `1px solid ${BRAND.line}`,
    background: `
      radial-gradient(650px 300px at 12% 0%, rgba(195,23,29,0.08), transparent 58%),
      radial-gradient(650px 300px at 88% 0%, rgba(255,106,61,0.08), transparent 58%),
      ${BRAND.card}
    `,
    boxShadow: "0 14px 34px rgba(17,24,39,0.08)",
    backdropFilter: "blur(12px)",
  },

  moduleTile: {
    position: "relative",
    borderRadius: 0,
    border: `1px solid ${BRAND.line}`,
    p: 2,
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 10px 24px rgba(17,24,39,0.06)",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      inset: -2,
      background:
        "radial-gradient(260px 120px at 16% 0%, rgba(195,23,29,0.10), transparent 60%), radial-gradient(260px 120px at 84% 0%, rgba(255,106,61,0.10), transparent 60%)",
      opacity: 1,
      pointerEvents: "none",
    },
  },

  moduleHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    position: "relative",
    zIndex: 1,
  },

  moduleTitle: {
    fontWeight: 900,
    color: BRAND.ink,
  },

  moduleDesc: {
    position: "relative",
    zIndex: 1,
    color: BRAND.slate,
    lineHeight: 1.4,
    fontSize: 13,
  },

  comingSoonPill: {
    fontSize: 11,
    padding: "3px 10px",
    borderRadius: 0,
    background:
      "linear-gradient(90deg, rgba(195,23,29,0.12), rgba(255,106,61,0.10))",
    border: "1px solid rgba(195,23,29,0.18)",
    color: BRAND.red,
    textTransform: "uppercase",
    letterSpacing: 0.7,
    fontWeight: 900,
    position: "relative",
    zIndex: 1,
  },

  previewFrameWrap: {
    borderRadius: 0,
    border: `1px solid ${BRAND.line}`,
    overflow: "hidden",
    background: "#fff",
    position: "relative",
    minHeight: 320,
    boxShadow: "0 12px 28px rgba(17,24,39,0.06)",
  },

  previewTopBar: {
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    px: 1.5,
    background: "linear-gradient(180deg, #ffffff, #fafafa)",
    borderBottom: `1px solid ${BRAND.line}`,
  },

  dots: { display: "flex", gap: 0.8, alignItems: "center" },

  dot: (c) => ({
    width: 10,
    height: 10,
    borderRadius: 0,
    background: c,
    opacity: 0.95,
    boxShadow: "0 0 0 3px rgba(17,24,39,0.03)",
  }),

  tinyUrl: {
    fontSize: 12,
    color: "#4B5563",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  primaryButton: {
    borderRadius: 4,
    fontWeight: 900,
    textTransform: "none",
    background: `linear-gradient(90deg, ${BRAND.red}, ${BRAND.orange})`,
    boxShadow: "0 10px 22px rgba(195,23,29,0.18)",
  },

  outlineButton: {
    borderRadius: 4,
    fontWeight: 900,
    textTransform: "none",
    borderColor: "rgba(195,23,29,0.25)",
    color: BRAND.red,
    background: "rgba(255,255,255,0.85)",
    "&:hover": { background: "rgba(195,23,29,0.05)" },
  },

  iconButton: {
    borderRadius: 4,
    border: `1px solid ${BRAND.line}`,
    color: BRAND.red,
    background: "rgba(255,255,255,0.85)",
    "&:hover": { background: "rgba(195,23,29,0.06)" },
  },

  statusChipDisabled: {
    background: "#F3F4F6",
    color: "#6B7280",
    border: `1px solid ${BRAND.line}`,
    fontWeight: 800,
  },

  statusChipConnected: {
    background: "rgba(34,197,94,0.12)",
    color: "#065F46",
    border: "1px solid rgba(34,197,94,0.25)",
    fontWeight: 800,
  },

  statusChipNotConnected: {
    background: "rgba(245,158,11,0.12)",
    color: "#92400E",
    border: "1px solid rgba(245,158,11,0.25)",
    fontWeight: 800,
  },

  // Preview mode toggle (screenshot vs live)
  previewModeRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    flexWrap: "wrap",
    px: 1.25,
    py: 1,
    borderBottom: `1px solid ${BRAND.line}`,
    background: "linear-gradient(180deg, #ffffff, #fafafa)",
  },

  toggleGroup: {
    background: "rgba(255,255,255,0.92)",
    border: `1px solid ${BRAND.line}`,
    borderRadius: 10,
    overflow: "hidden",
    "& .MuiToggleButton-root": {
      textTransform: "none",
      fontWeight: 900,
      padding: "6px 10px",
      border: 0,
      color: "#6B7280",
    },
    "& .MuiToggleButton-root.Mui-selected": {
      background: "rgba(195,23,29,0.08)",
      color: BRAND.ink,
    },
    "& .MuiToggleButton-root:hover": {
      background: "rgba(195,23,29,0.05)",
    },
  },

  screenshotImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  screenshotPlaceholder: {
    height: { xs: 360, md: 420 },
    display: "grid",
    placeItems: "center",
    background:
      "radial-gradient(700px 260px at 50% 0%, rgba(195,23,29,0.10), transparent 60%), #ffffff",
  },
};

export default function PromoShowcasePage() {
  const { instanceUniqueId } = useParams();
  const history = useHistory();
  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const accessToken = localStorage.getItem("accessToken");
  const headers = useMemo(() => ({ headers: { accessToken } }), [accessToken]);
  const currentUserRole = useMemo(() => {
    const fallbackRole = String(localStorage.getItem('userRole') || '').toLowerCase();
    const token = String(accessToken || '');
    if (!token || !token.includes('.')) return fallbackRole;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return String(payload?.role || payload?.user?.role || fallbackRole || '').toLowerCase();
    } catch (error) {
      return fallbackRole;
    }
  }, [accessToken]);
  const canViewPageEditorPayload = useMemo(
    () => ['administrator', 'admin', 'owner'].includes(currentUserRole),
    [currentUserRole]
  );

  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [provisioningShowcase, setProvisioningShowcase] = useState(false);
  const [customDomainDraft, setCustomDomainDraft] = useState("");
  const [customDomainSaving, setCustomDomainSaving] = useState(false);
  const [customDomainVerifying, setCustomDomainVerifying] = useState(false);
  const [launchWizardOpen, setLaunchWizardOpen] = useState(false);
  const [onboardingSiteName, setOnboardingSiteName] = useState("");
  const [onboardingSiteSlug, setOnboardingSiteSlug] = useState("");
  const [launchingShowcase, setLaunchingShowcase] = useState(false);
  const [launchVerification, setLaunchVerification] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [builderFocusMode, setBuilderFocusMode] = useState(false);
  const [builderRequestedSidebarView, setBuilderRequestedSidebarView] = useState('');
  const [builderRequestedSidebarViewTick, setBuilderRequestedSidebarViewTick] = useState(0);
  const productHelpStorageKey = useMemo(
    () => `promoshowcase:products-help:hidden:${instanceUniqueId || 'default'}`,
    [instanceUniqueId]
  );
  const [productHelpHidden, setProductHelpHidden] = useState(() => {
    try {
      return localStorage.getItem(`promoshowcase:products-help:hidden:${instanceUniqueId || 'default'}`) === '1';
    } catch (_error) {
      return false;
    }
  });
  // This route is the standalone Showcase product surface. It always enters
  // Showcase mode on a fresh load, while still allowing an in-session exit
  // back to normal Elite Core chrome without navigating away.
  const [showcaseMode, setShowcaseMode] = useState(true);
  const [showcaseIntroVisible, setShowcaseIntroVisible] = useState(true);

  const [productSourceFilter, setProductSourceFilter] = useState("promoworld");
  const [storeProductSummary, setStoreProductSummary] = useState({
    loading: false,
    storeName: "PromoShowcase",
    importedCount: 0,
    isSynced: null,
    products: [],
    syncMeta: null,
    selections: [],
    selectionCount: 0,
    catalogueCount: 0,
    syncSession: null,
  });
  const [designConfig, setDesignConfig] = useState({
    primaryColor: "",
    secondaryColor: "",
    heroTitle: "",
    heroSubtitle: "",
    logoUrl: "",
    globalStylesheet: "",
  });
  const [addonsConfig, setAddonsConfig] = useState({ enabled: {}, settings: {} });
  const [savingAddons, setSavingAddons] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogPublishing, setBlogPublishing] = useState(false);
  const [blogForm, setBlogForm] = useState({ title: "", excerpt: "", content: "", status: "draft" });
  const [blogDrafts, setBlogDrafts] = useState([]);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [sitePages, setSitePages] = useState([]);
  const [sitePagesLoading, setSitePagesLoading] = useState(false);
  const [showcaseOrders, setShowcaseOrders] = useState([]);
  const [showcaseOrdersLoading, setShowcaseOrdersLoading] = useState(false);
  const [showcaseOrdersRefreshing, setShowcaseOrdersRefreshing] = useState(false);
  const [creatingProjectOrderId, setCreatingProjectOrderId] = useState(null);
  const [formSubmissions, setFormSubmissions] = useState([]);
  const [formSubmissionsLoading, setFormSubmissionsLoading] = useState(false);
  const [sitePagesRefreshing, setSitePagesRefreshing] = useState(false);
  const [sitePagesQuery, setSitePagesQuery] = useState('');
  const [siteUsers, setSiteUsers] = useState([]);
  const [siteUsersLoading, setSiteUsersLoading] = useState(false);
  const [siteUsersRefreshing, setSiteUsersRefreshing] = useState(false);
  const [siteUsersQuery, setSiteUsersQuery] = useState('');
  const [hasLoadedPages, setHasLoadedPages] = useState(false);
  const [hasLoadedUsers, setHasLoadedUsers] = useState(false);
  const [pageEditorOpen, setPageEditorOpen] = useState(false);
  const [pageEditorSyncing, setPageEditorSyncing] = useState(false);
  const [pageEditorViewMode, setPageEditorViewMode] = useState('builder');
  const [editingPage, setEditingPage] = useState(null);
  const [pageEditorSlot, setPageEditorSlot] = useState('home');
  const [pageEditorLayout, setPageEditorLayout] = useState(createEmptyLayoutSchema());
  const [pageWidgetDraftType, setPageWidgetDraftType] = useState('hero');
  const [creatingSiteUser, setCreatingSiteUser] = useState(false);
  const [newSiteUser, setNewSiteUser] = useState({ email: "", username: "", displayName: "", role: "customer", password: "" });
  const [designerTeamUsers, setDesignerTeamUsers] = useState([]);
  const [requestDesignerOpen, setRequestDesignerOpen] = useState(false);
  const [requestingDesigner, setRequestingDesigner] = useState(false);
  const [showProductPopup, setShowProductPopup] = useState(false);
  const [showPromoWorldPicker, setShowPromoWorldPicker] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesSaving, setTemplatesSaving] = useState(false);
  const [templatesApplying, setTemplatesApplying] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateForm, setTemplateForm] = useState({
    brandName: '',
    primaryColor: '#B91C1C',
    secondaryColor: '#111111',
    announcementText: '',
    logoUrl: '',
    fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    siteIdentity: {
      site_title: '',
      tagline: '',
      meta_description: '',
      social_image_url: '',
      site_icon_url: '',
      contact: { email: '', phone: '', address: '' },
    },
    featuredCategories: '',
    templateCss: '',
    customCss: '',
    pages: DEFAULT_SHOWCASE_PAGES,
    siteChrome: DEFAULT_SITE_CHROME,
    storeSettings: {
      ecommerce_enabled: true,
      show_prices: true,
      guest_checkout: true,
      taxes_enabled: true,
      shipping_enabled: true,
      currency: 'CAD',
      payment_methods: {
        request_quote: true,
        stripe: false,
      },
      request_quote: {
        title: 'Request Quote/Estimate',
        description: 'Request a quote for your order.',
        checkout_button_text: 'Submit Request',
      },
      orders: {
        paid_order_action: 'create_project',
        quote_order_action: 'manual',
      },
      catalog_filters: {
        enabled: true,
        category: true,
        theme: true,
        brand: true,
        supplier: true,
        color: true,
        size: true,
        price: true,
        order: ['category', 'theme', 'brand', 'supplier', 'color', 'size', 'price'],
      },
    },
  });
  const globalStylesheet = useMemo(() => String(designConfig?.globalStylesheet || '').trim(), [designConfig?.globalStylesheet]);
  const combinedCustomCss = useMemo(
    () => mergeCssBlocks(globalStylesheet, templateForm.customCss),
    [globalStylesheet, templateForm.customCss]
  );
  const [layoutBuilder, setLayoutBuilder] = useState(createEmptyLayoutSchema());
  const [widgetDraft, setWidgetDraft] = useState({ slot: 'home', type: 'hero' });
  const [previewSlot, setPreviewSlot] = useState('home');
  const [selectedBuilderSlot, setSelectedBuilderSlot] = useState('home');
  const [selectedBuilderWidgetIndex, setSelectedBuilderWidgetIndex] = useState(null);
  const [builderCanvasMode, setBuilderCanvasMode] = useState('preview');
  const [builderInspectorTab, setBuilderInspectorTab] = useState('structure');
  const [templateCssEditorTab, setTemplateCssEditorTab] = useState('custom');
  const [showSelectorContract, setShowSelectorContract] = useState(false);
  const [debouncedTemplateCss, setDebouncedTemplateCss] = useState('');
  const [debouncedCustomCss, setDebouncedCustomCss] = useState('');
  const [previewHoveredSelector, setPreviewHoveredSelector] = useState('');
  const [previewSelectedSelector, setPreviewSelectedSelector] = useState('');
  const [showSelectorOverlay, setShowSelectorOverlay] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      const stored = window.localStorage.getItem('promoshowcase:selector-overlay');
      return stored == null ? true : stored !== 'false';
    } catch (_error) {
      return true;
    }
  });
  const [previewRendererMode, setPreviewRendererMode] = useState('local');
  const [wordpressPreviewHtml, setWordpressPreviewHtml] = useState('');
  const [wordpressPreviewLoading, setWordpressPreviewLoading] = useState(false);
  const [wordpressPreviewError, setWordpressPreviewError] = useState('');
  const [templateStatus, setTemplateStatus] = useState('draft');
  const [templateFormErrors, setTemplateFormErrors] = useState([]);
  const [lastApplyResult, setLastApplyResult] = useState(null);
  const [layoutDirty, setLayoutDirty] = useState(false);
  const lastLayoutImportRef = useRef(null);
  const [canUndoLayoutImport, setCanUndoLayoutImport] = useState(false);
  const lastHydratedTemplateIdRef = useRef('');
  const forceTemplateHydrationRef = useRef(false);
  const publishValidationRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowcaseIntroVisible(false), 1100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('promoshowcase:selector-overlay', showSelectorOverlay ? 'true' : 'false');
    } catch (_error) {
      // Preview preference persistence is best-effort only.
    }
  }, [showSelectorOverlay]);

  const handleOpenMyProducts = useCallback(() => {
    if (!settings.promoShowcaseEnabled) return;
    setShowProductPopup(true);
  }, [settings.promoShowcaseEnabled]);

  // ✅ NEW: preview mode
  // "screenshot" = WordPress mshots image
  // "live" = iframe preview
  const [previewMode, setPreviewMode] = useState("screenshot");

  const applyShowcaseProductPage = useCallback((data) => {
    const summary = data.summary || {};
    setStoreProductSummary({
      loading: false, storeName: "PromoShowcase", products: data.products || [],
      importedCount: Number(summary.importedCount || 0),
      isSynced: typeof summary.isSynced === "boolean" ? summary.isSynced : null,
      syncMeta: { lastSyncedAt: summary.lastSyncedAt || null },
      syncSession: summary.syncSession || null,
      selections: data.selections || [], selectionCount: Number(summary.selectionCount || 0),
      catalogueCount: Number(summary.catalogueCount || 0),
    });
  }, []);
  const productBrowse = useShowcaseProductPage({ server: SERVER, instanceUniqueId, headers, onLoaded: applyShowcaseProductPage });
  const loadPromoShowcaseProductSummary = productBrowse.reload;
  const applySyncSession = useCallback(session => {
    setStoreProductSummary(previous => ({ ...previous, syncSession: session, isSynced: session?.status === 'complete' }));
  }, []);
  const productSync = useShowcaseProductSync({ server: SERVER, instanceUniqueId, headers,
    session: storeProductSummary.syncSession, onSession: applySyncSession, readSyncSession: productBrowse.readSyncSession });
  const { run: runProductSync, startNew: startNewProductSync, active: syncingProducts, invalidateMembership: invalidateProductMembership } = productSync;
  const obsoleteSyncSessionId = productSync.session?.id;
  const mutateProductPage = productBrowse.mutate;
  const acknowledgeMembershipChange = useCallback(() => {
    invalidateProductMembership(obsoleteSyncSessionId);
    setStoreProductSummary(previous => ({ ...previous, isSynced: false }));
  }, [invalidateProductMembership, obsoleteSyncSessionId]);
  const mutateShowcaseProduct = useCallback((type, key, product) =>
    mutateProductPage(type, key, product, acknowledgeMembershipChange), [mutateProductPage, acknowledgeMembershipChange]);
  const showcaseActions = useMemo(() => ({
    mutate: mutateShowcaseProduct, onEditorStateChange: productBrowse.setEditorOpen,
    busy: productBrowse.loading || productBrowse.mutating,
  }), [mutateShowcaseProduct, productBrowse.setEditorOpen, productBrowse.loading, productBrowse.mutating]);
  const addShowcaseProduct = useCallback(product => mutateShowcaseProduct('add', null, product), [mutateShowcaseProduct]);
  const removeShowcaseProduct = useCallback(product => mutateShowcaseProduct('remove',
    product.showcaseIdentity?.key || `${String(product.Source || 'promoworld').toLowerCase()}:${product.ProductId || product.Id || product.id}`), [mutateShowcaseProduct]);

  const handleSyncProductsToShowcase = useCallback(async () => {
    if (!settings.promoShowcaseEnabled) {
      toast.error("Enable PromoShowcase before syncing products.");
      return;
    }

    try {
      const finalData = productSync.session?.status === 'cancelled' ? await startNewProductSync() : await runProductSync();
      if (!finalData) return;
      const session = finalData?.session || {};
      if (finalData?.partial) {
        toast.warn(
          `Sync completed with ${session.failedCount || 0} failed product${Number(session.failedCount || 0) === 1 ? '' : 's'}.`
        );
      } else {
        const archiveText = Number(finalData?.archivedCount || 0) > 0
          ? ` ${finalData.archivedCount} stale product${Number(finalData.archivedCount) === 1 ? '' : 's'} archived.`
          : '';
        toast.success(
          `Synced ${session.syncedCount || 0} website product${Number(session.syncedCount || 0) === 1 ? '' : 's'} to WordPress.${archiveText}`
        );
      }

      await loadPromoShowcaseProductSummary();
    } catch (error) {
      toast.error(error.message);
    }
  }, [loadPromoShowcaseProductSummary, settings.promoShowcaseEnabled, runProductSync, startNewProductSync, productSync.session?.status]);

  const closeProductListPopup = useCallback(() => {
    setShowProductPopup(false);
  }, []);

  const selectedTemplate = useMemo(
    () => (templates || []).find((tpl) => String(tpl.id) === String(selectedTemplateId)) || null,
    [templates, selectedTemplateId]
  );

  const starterTemplates = useMemo(
    () => (templates || []).filter((tpl) => String(tpl?.visibility || 'public').toLowerCase() !== 'private'),
    [templates]
  );

  const savedTemplates = useMemo(
    () => (templates || [])
      .filter((tpl) => String(tpl?.visibility || '').toLowerCase() === 'private')
      .sort((a, b) => {
        const aStamp = Number(String(a?.id || '').split('_').pop()) || 0;
        const bStamp = Number(String(b?.id || '').split('_').pop()) || 0;
        return bStamp - aStamp;
      }),
    [templates]
  );

  const addShowcasePage = useCallback(() => {
    const existing = Array.isArray(templateForm.pages) ? templateForm.pages : DEFAULT_SHOWCASE_PAGES;
    const base = 'page';
    let number = existing.length + 1;
    let slot = `${base}-${number}`;
    while (existing.some((page) => page?.slot === slot)) {
      number += 1;
      slot = `${base}-${number}`;
    }
    const page = {
      slug: slot,
      title: `Page ${number}`,
      slot,
      set_front_page: false,
      show_in_menu: true,
    };
    setTemplateForm((prev) => ({ ...prev, pages: [...(Array.isArray(prev.pages) ? prev.pages : []), page] }));
    setLayoutBuilder((prev) => {
      const next = normalizeLayoutSchema(prev);
      if (!next.slots[slot]) next.slots[slot] = [];
      return next;
    });
    setSelectedBuilderSlot(slot);
    setSelectedBuilderWidgetIndex(null);
    setLayoutDirty(true);
  }, [templateForm.pages]);

  const updateShowcasePage = useCallback((slot, patch) => {
    setTemplateForm((prev) => {
      const pages = (Array.isArray(prev.pages) ? prev.pages : DEFAULT_SHOWCASE_PAGES).map((page) => {
        if (page?.slot !== slot) {
          if (patch?.set_front_page) return { ...page, set_front_page: false };
          return page;
        }
        if (slot === 'shop') {
          return {
            ...page,
            ...(patch || {}),
            slug: 'shop',
            slot: 'shop',
            system: 'woocommerce_shop',
            set_front_page: false,
          };
        }
        return { ...page, ...(patch || {}) };
      });
      return { ...prev, pages };
    });
    setLayoutDirty(true);
  }, []);

  const removeShowcasePage = useCallback((slot) => {
    if (slot === 'home') return toast.error('Home cannot be removed. Set another page as the front page first if needed.');
    if (slot === 'shop') return toast.error('Shop is a WooCommerce system page and cannot be removed.');
    setTemplateForm((prev) => ({
      ...prev,
      pages: (Array.isArray(prev.pages) ? prev.pages : []).filter((page) => page?.slot !== slot),
    }));
    setLayoutBuilder((prev) => {
      const next = normalizeLayoutSchema(prev);
      delete next.slots[slot];
      return next;
    });
    if (selectedBuilderSlot === slot) setSelectedBuilderSlot('home');
    setSelectedBuilderWidgetIndex(null);
    setLayoutDirty(true);
  }, [selectedBuilderSlot]);

  const builderSlots = useMemo(() => {
    const pageSlots = (Array.isArray(templateForm.pages) ? templateForm.pages : [])
      .map((page) => String(page?.slot || '').trim())
      .filter(Boolean);
    return Array.from(new Set([...pageSlots, ...Object.keys(layoutBuilder?.slots || {})]));
  }, [layoutBuilder?.slots, templateForm.pages]);
  const currentBuilderWidgets = useMemo(
    () => (Array.isArray(layoutBuilder?.slots?.[selectedBuilderSlot]) ? layoutBuilder.slots[selectedBuilderSlot] : []),
    [layoutBuilder?.slots, selectedBuilderSlot]
  );
  const selectedBuilderWidget = selectedBuilderWidgetIndex != null ? currentBuilderWidgets[selectedBuilderWidgetIndex] || null : null;
  const selectedBuilderPage = (Array.isArray(templateForm.pages) ? templateForm.pages : []).find((page) => page?.slot === selectedBuilderSlot);
  const selectedBuilderSlotMeta = SLOT_META[selectedBuilderSlot] || {
    label: selectedBuilderPage?.title || selectedBuilderSlot,
    icon: ViewQuiltRoundedIcon,
    tint: 'rgba(148,163,184,.12)',
  };
  const selectedBuilderWidgetMeta = getWidgetMeta(selectedBuilderWidget?.component);
  const selectedBuilderWidgetPresets = useMemo(
    () => (selectedBuilderWidget ? (WIDGET_TEMPLATES[selectedBuilderWidget.component] || []) : []),
    [selectedBuilderWidget]
  );

  useEffect(() => {
    setPreviewSlot(selectedBuilderSlot);
    setWidgetDraft((prev) => (prev.slot === selectedBuilderSlot ? prev : { ...prev, slot: selectedBuilderSlot }));
  }, [selectedBuilderSlot]);

  useEffect(() => {
    if (selectedBuilderWidgetIndex == null) return;
    if (selectedBuilderWidgetIndex >= currentBuilderWidgets.length) {
      setSelectedBuilderWidgetIndex(currentBuilderWidgets.length ? currentBuilderWidgets.length - 1 : null);
    }
  }, [currentBuilderWidgets.length, selectedBuilderWidgetIndex]);

  useEffect(() => {
    if (selectedBuilderWidget) {
      setBuilderInspectorTab('widget');
    }
  }, [selectedBuilderWidget]);

  const selectTemplateAndHydrate = useCallback((templateId) => {
    forceTemplateHydrationRef.current = true;
    lastLayoutImportRef.current = null;
    setCanUndoLayoutImport(false);
    setLayoutDirty(false);
    setSelectedTemplateId(templateId);
    persistSelectedTemplateId(instanceUniqueId, templateId);
  }, [instanceUniqueId]);

  useEffect(() => {
    if (!layoutDirty) return undefined;
    const warnBeforeLeaving = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [layoutDirty]);

  const addWidgetToSlot = useCallback(() => {
    const slot = widgetDraft.slot || 'home';
    setLayoutDirty(true);
    const widget = WIDGET_LIBRARY.find((item) => item.key === widgetDraft.type);
    if (!widget) return;

    setLayoutBuilder((prev) => {
      const next = normalizeLayoutSchema(prev);
      const nodeId = createPromoShowcaseNodeId();
      const props = withDefaultWidgetStyle(widget.defaultProps || {});
      if (widget.key === 'form' && !props.form_key) props.form_key = nodeId;
      next.slots[slot] = [...(next.slots[slot] || []), { id: nodeId, component: widget.key, version: 1, props }];
      return next;
    });
  }, [widgetDraft]);

  const insertQuickSection = useCallback((sectionId, slotOverride = null) => {
    const section = QUICK_SECTIONS.find((item) => item.id === sectionId);
    if (!section) return;
    const slot = slotOverride || selectedBuilderSlot || widgetDraft.slot || 'home';
    setLayoutDirty(true);
    setLayoutBuilder((prev) => {
      const next = normalizeLayoutSchema(prev);
      const additions = (section.widgets || []).map((definition) => {
        const nodeId = createPromoShowcaseNodeId();
        const props = withDefaultWidgetStyle(definition.props || {});
        if (definition.component === 'form' && !props.form_key) props.form_key = nodeId;
        return { id: nodeId, component: definition.component, version: 1, props };
      });
      next.slots[slot] = [...(next.slots[slot] || []), ...additions];
      return next;
    });
    setSelectedBuilderWidgetIndex(null);
    setBuilderCanvasMode('preview');
    toast.success(`Added ${section.label} to ${slot}.`);
  }, [selectedBuilderSlot, widgetDraft.slot]);

  const importPageTemplateToCurrentSlot = useCallback((templateId, mode = 'append') => {
    try {
      let imported = null;
      setLayoutDirty(true);
      setLayoutBuilder((prev) => {
        const current = normalizeLayoutSchema(prev);
        lastLayoutImportRef.current = current;
        setCanUndoLayoutImport(true);
        const result = applyLegacyPageTemplateToLayout({
          layout: current,
          slot: selectedBuilderSlot,
          templateId,
          mode,
        });
        imported = result.converted;
        return normalizeLayoutSchema(result.layout);
      });
      setSelectedBuilderWidgetIndex(null);
      setBuilderCanvasMode('preview');
      toast.success(`${mode === 'replace' ? 'Replaced' : 'Added'} ${imported?.templateName || 'page layout'} ${mode === 'replace' ? 'on' : 'to'} ${selectedBuilderSlotMeta.label}. You can undo this change.`);
      return imported;
    } catch (error) {
      console.error('Failed to add PromoShowcase page layout', error);
      toast.error(error?.message || 'Could not add this page layout.');
      return null;
    }
  }, [selectedBuilderSlot, selectedBuilderSlotMeta.label]);

  const undoLastLayoutImport = useCallback(() => {
    if (!lastLayoutImportRef.current) return;
    setLayoutBuilder(normalizeLayoutSchema(lastLayoutImportRef.current));
    lastLayoutImportRef.current = null;
    setCanUndoLayoutImport(false);
    setSelectedBuilderWidgetIndex(null);
    setBuilderCanvasMode('preview');
    setLayoutDirty(true);
    toast.success('Restored the page to how it looked before the layout was added.');
  }, []);

  const removeWidgetFromSlot = useCallback((slot, index) => {
    setLayoutBuilder((prev) => {
      const next = normalizeLayoutSchema(prev);
      const widget = (next.slots[slot] || [])[index];
      if (widget?.component === 'catalog_archive' || widget?.props?.system_locked === true) {
        toast.error('The Shop Product Archive is required and cannot be removed.');
        return prev;
      }
      setLayoutDirty(true);
      next.slots[slot] = (next.slots[slot] || []).filter((_, idx) => idx !== index);
      return next;
    });
  }, [setLayoutDirty]);

  const moveWidgetInSlot = useCallback((slot, index, direction) => {
    setLayoutDirty(true);
    setLayoutBuilder((prev) => {
      const next = normalizeLayoutSchema(prev);
      const arr = [...(next.slots[slot] || [])];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= arr.length) return next;
      const temp = arr[index];
      arr[index] = arr[target];
      arr[target] = temp;
      next.slots[slot] = arr;
      return next;
    });
  }, [setLayoutDirty]);

  const reorderWidgetInSlot = useCallback((slot, fromIndex, toIndex) => {
    setLayoutDirty(true);
    setLayoutBuilder((prev) => {
      const next = normalizeLayoutSchema(prev);
      const arr = [...(next.slots[slot] || [])];
      if (
        fromIndex < 0
        || fromIndex >= arr.length
        || toIndex < 0
        || toIndex >= arr.length
        || fromIndex === toIndex
      ) return next;
      const [moved] = arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, moved);
      next.slots[slot] = arr;
      return next;
    });
    setSelectedBuilderWidgetIndex((current) => {
      if (current == null) return current;
      if (current === fromIndex) return toIndex;
      if (fromIndex < toIndex && current > fromIndex && current <= toIndex) return current - 1;
      if (fromIndex > toIndex && current >= toIndex && current < fromIndex) return current + 1;
      return current;
    });
  }, [setLayoutDirty]);

  const updateWidgetProps = useCallback((slot, index, key, value) => {
    setLayoutDirty(true);
    setLayoutBuilder((prev) => {
      const next = normalizeLayoutSchema(prev);
      next.slots[slot] = (next.slots[slot] || []).map((widget, idx) =>
        idx === index
          ? { ...widget, props: { ...(widget.props || {}), [key]: value } }
          : widget
      );
      return next;
    });
  }, [setLayoutDirty]);

  const addHeroSlide = useCallback((slot, index) => {
    setLayoutDirty(true);
    setLayoutBuilder((prev) => {
      const next = normalizeLayoutSchema(prev);
      next.slots[slot] = (next.slots[slot] || []).map((widget, idx) => {
        if (idx !== index) return widget;
        const slides = Array.isArray(widget?.props?.slides) ? widget.props.slides : [];
        if (slides.length >= 10) return widget;
        return {
          ...widget,
          props: {
            ...(widget.props || {}),
            slides: [...slides, { background_url: '', h1: '', paragraph: '', button_text: '', button_url: '' }],
          },
        };
      });
      return next;
    });
  }, [setLayoutDirty]);

  const removeHeroSlide = useCallback((slot, index, slideIndex) => {
    setLayoutDirty(true);
    setLayoutBuilder((prev) => {
      const next = normalizeLayoutSchema(prev);
      next.slots[slot] = (next.slots[slot] || []).map((widget, idx) => {
        if (idx !== index) return widget;
        const slides = Array.isArray(widget?.props?.slides) ? widget.props.slides : [];
        return {
          ...widget,
          props: {
            ...(widget.props || {}),
            slides: slides.filter((_, i) => i !== slideIndex),
          },
        };
      });
      return next;
    });
  }, [setLayoutDirty]);

  const updateHeroSlide = useCallback((slot, index, slideIndex, key, value) => {
    setLayoutDirty(true);
    setLayoutBuilder((prev) => {
      const next = normalizeLayoutSchema(prev);
      next.slots[slot] = (next.slots[slot] || []).map((widget, idx) => {
        if (idx !== index) return widget;
        const slides = Array.isArray(widget?.props?.slides) ? widget.props.slides : [];
        const updated = slides.map((slide, i) => (i === slideIndex ? { ...(slide || {}), [key]: value } : slide));
        return {
          ...widget,
          props: {
            ...(widget.props || {}),
            slides: updated,
          },
        };
      });
      return next;
    });
  }, [setLayoutDirty]);

  const addPageWidget = useCallback(() => {
    const widget = WIDGET_LIBRARY.find((item) => item.key === pageWidgetDraftType);
    if (!widget) return;
    setPageEditorLayout((prev) => {
      const next = normalizeLayoutSchema(prev);
      next.slots[pageEditorSlot] = [...(next.slots[pageEditorSlot] || []), { id: createPromoShowcaseNodeId(), component: widget.key, version: 1, props: { ...(widget.defaultProps || {}) } }];
      return next;
    });
  }, [pageEditorSlot, pageWidgetDraftType]);

  const updatePageWidgetProps = useCallback((index, key, value) => {
    setPageEditorLayout((prev) => {
      const next = normalizeLayoutSchema(prev);
      next.slots[pageEditorSlot] = (next.slots[pageEditorSlot] || []).map((widget, idx) =>
        idx === index ? { ...widget, props: { ...(widget.props || {}), [key]: value } } : widget
      );
      return next;
    });
  }, [pageEditorSlot]);

  const removePageWidget = useCallback((index) => {
    setPageEditorLayout((prev) => {
      const next = normalizeLayoutSchema(prev);
      const widget = (next.slots[pageEditorSlot] || [])[index];
      if (widget?.component === 'catalog_archive' || widget?.props?.system_locked === true) {
        toast.error('The Shop Product Archive is required and cannot be removed.');
        return prev;
      }
      next.slots[pageEditorSlot] = (next.slots[pageEditorSlot] || []).filter((_, idx) => idx !== index);
      return next;
    });
  }, [pageEditorSlot]);

  const movePageWidget = useCallback((index, direction) => {
    setPageEditorLayout((prev) => {
      const next = normalizeLayoutSchema(prev);
      const arr = [...(next.slots[pageEditorSlot] || [])];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= arr.length) return next;
      const temp = arr[index];
      arr[index] = arr[target];
      arr[target] = temp;
      next.slots[pageEditorSlot] = arr;
      return next;
    });
  }, [pageEditorSlot]);

  const applyPageWidgetTemplatePreset = useCallback((index, templateProps = {}) => {
    setPageEditorLayout((prev) => {
      const next = normalizeLayoutSchema(prev);
      next.slots[pageEditorSlot] = (next.slots[pageEditorSlot] || []).map((widget, idx) =>
        idx === index
          ? { ...widget, props: { ...(widget.props || {}), ...(templateProps || {}) } }
          : widget
      );
      return next;
    });
  }, [pageEditorSlot]);

  const addPageHeroSlide = useCallback((index) => {
    setPageEditorLayout((prev) => {
      const next = normalizeLayoutSchema(prev);
      next.slots[pageEditorSlot] = (next.slots[pageEditorSlot] || []).map((widget, idx) => {
        if (idx !== index) return widget;
        const slides = Array.isArray(widget?.props?.slides) ? widget.props.slides : [];
        if (slides.length >= 10) return widget;
        return {
          ...widget,
          props: {
            ...(widget.props || {}),
            slides: [
              ...slides,
              {
                background_url: '',
                h1: '',
                paragraph: '',
                button_text: '',
                button_url: '',
              },
            ],
          },
        };
      });
      return next;
    });
  }, [pageEditorSlot]);

  const removePageHeroSlide = useCallback((index, slideIndex) => {
    setPageEditorLayout((prev) => {
      const next = normalizeLayoutSchema(prev);
      next.slots[pageEditorSlot] = (next.slots[pageEditorSlot] || []).map((widget, idx) => {
        if (idx !== index) return widget;
        const slides = Array.isArray(widget?.props?.slides) ? widget.props.slides : [];
        return {
          ...widget,
          props: {
            ...(widget.props || {}),
            slides: slides.filter((_, i) => i !== slideIndex),
          },
        };
      });
      return next;
    });
  }, [pageEditorSlot]);

  const updatePageHeroSlide = useCallback((index, slideIndex, key, value) => {
    setPageEditorLayout((prev) => {
      const next = normalizeLayoutSchema(prev);
      next.slots[pageEditorSlot] = (next.slots[pageEditorSlot] || []).map((widget, idx) => {
        if (idx !== index) return widget;
        const slides = Array.isArray(widget?.props?.slides) ? widget.props.slides : [];
        const updated = slides.map((slide, i) => (i === slideIndex ? { ...(slide || {}), [key]: value } : slide));
        return {
          ...widget,
          props: {
            ...(widget.props || {}),
            slides: updated,
          },
        };
      });
      return next;
    });
  }, [pageEditorSlot]);

  const loadSettings = useCallback(async () => {
    await loadSettingsImpl();
  }, [SERVER, headers, instanceUniqueId]);

  const openPageBuilder = useCallback((page) => {
    const slotFromPage = String(page?.promoshowcase?.slot || page?.slot || 'home');
    const sourceLayout = settings?.templateState?.layout_schema || settings?.templateState?.layout || layoutBuilder;
    setLayoutBuilder(normalizeLayoutSchema(sourceLayout));
    setSelectedBuilderSlot(slotFromPage);
    setPreviewSlot(slotFromPage);
    setSelectedBuilderWidgetIndex(null);
    setBuilderRequestedSidebarView('pages');
    setActiveTab(7);
  }, [layoutBuilder, settings?.templateState]);

  const pageEditorApplyPayloadPreview = useMemo(() => {
    const pageId = Number(
      editingPage?.wpPageId
      || editingPage?.wp_page_id
      || editingPage?.id
      || editingPage?.ID
      || editingPage?.post_id
      || 0
    ) || null;
    const pageSlug = String(editingPage?.slug || '').trim();
    const pageTitle = String(editingPage?.title?.rendered || editingPage?.title || editingPage?.name || pageSlug || 'Page');
    const templateIdToSync = String(selectedTemplateId || settings?.activeTemplate?.template_id || settings?.templateState?.template_id || '').trim();

    return {
      templateId: templateIdToSync || null,
      overrides: {
        theme_vars: {
          brand_name: templateForm.brandName || undefined,
          primary_color: templateForm.primaryColor || undefined,
          secondary_color: templateForm.secondaryColor || undefined,
          announcement_text: templateForm.announcementText || undefined,
          logo_url: templateForm.logoUrl || undefined,
          font_family: templateForm.fontFamily || undefined,
        },
        site_identity: templateForm.siteIdentity || {},
        data_sources: {
          promoworld: {
            enabled: true,
            mode: 'selection_driven',
            derive_merchandising: true,
            featured_categories: String(templateForm.featuredCategories || '').split(',').map((v) => v.trim()).filter(Boolean),
          },
        },
        css: stripLegacyWidgetShellCss(templateForm.templateCss) || undefined,
        custom_css: combinedCustomCss || undefined,
        layout_schema: normalizeLayoutSchema(pageEditorLayout),
        pages: [
          {
            wp_page_id: pageId || undefined,
            post_id: pageId || undefined,
            slug: pageSlug || pageEditorSlot,
            title: pageTitle,
            slot: pageEditorSlot,
            set_front_page: !!(editingPage?.isFrontPage || editingPage?.is_front),
          },
        ],
      },
    };
  }, [combinedCustomCss, editingPage, pageEditorLayout, pageEditorSlot, selectedTemplateId, settings?.activeTemplate?.template_id, settings?.templateState?.template_id, templateForm]);

  useEffect(() => {
    if (!canViewPageEditorPayload && pageEditorViewMode === 'payload') {
      setPageEditorViewMode('builder');
    }
  }, [canViewPageEditorPayload, pageEditorViewMode]);

  const syncEditedPageLayout = useCallback(async () => {
    if (!editingPage) return;

    setPageEditorSyncing(true);
    try {
      let templateIdToSync = String(selectedTemplateId || settings?.activeTemplate?.template_id || settings?.templateState?.template_id || '').trim();
      if (!templateIdToSync) {
        const templatesRes = await axios.get(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/templates`, headers);
        const availableTemplates = Array.isArray(templatesRes?.data?.templates) ? templatesRes.data.templates : [];
        setTemplates(availableTemplates);
        const firstTemplateId = String(availableTemplates?.[0]?.id || '').trim();
        if (firstTemplateId) {
          templateIdToSync = firstTemplateId;
          setSelectedTemplateId(firstTemplateId);
          persistSelectedTemplateId(instanceUniqueId, firstTemplateId);
        }
      }

      if (!templateIdToSync) {
        toast.error('Select a site design first.');
        return;
      }

      const pageSlug = String(editingPage?.slug || '').trim();
      const pageTitle = String(editingPage?.title?.rendered || editingPage?.title || editingPage?.name || pageSlug || 'Page');

      const applyRes = await axios.post(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/template/apply`, {
        templateId: templateIdToSync,
        overrides: {
          theme_vars: {
            brand_name: templateForm.brandName || undefined,
            primary_color: templateForm.primaryColor || undefined,
            logo_url: templateForm.logoUrl || undefined,
          font_family: templateForm.fontFamily || undefined,
          },
          site_identity: templateForm.siteIdentity || {},
          data_sources: {
            promoworld: {
              enabled: true,
              mode: 'selection_driven',
              derive_merchandising: true,
              featured_categories: String(templateForm.featuredCategories || '').split(',').map((v) => v.trim()).filter(Boolean),
            },
          },
          css: stripLegacyWidgetShellCss(templateForm.templateCss) || undefined,
          custom_css: combinedCustomCss || undefined,
          layout_schema: normalizeLayoutSchema(pageEditorLayout),
          pages: [
            {
              wp_page_id: Number(editingPage?.wpPageId || editingPage?.wp_page_id || editingPage?.id || editingPage?.ID || editingPage?.post_id || 0) || undefined,
              post_id: Number(editingPage?.wpPageId || editingPage?.wp_page_id || editingPage?.id || editingPage?.ID || editingPage?.post_id || 0) || undefined,
              slug: pageSlug || pageEditorSlot,
              title: pageTitle,
              slot: pageEditorSlot,
              set_front_page: !!(editingPage?.isFrontPage || editingPage?.is_front),
            },
          ],
        },
      }, headers);

      setLastApplyResult(applyRes?.data || null);
      toast.success(`Synced page "${pageTitle}" from widget builder.`);
      await loadSettings();
      const refreshRes = await axios.post(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/pages/refresh`, {}, headers);
      const refreshedPages = Array.isArray(refreshRes?.data?.pages) ? refreshRes.data.pages : [];
      setSitePages(refreshedPages);
      setHasLoadedPages(true);
      setPageEditorOpen(false);
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to sync this page layout.';
      const detail = error?.response?.data?.detail ? ` ${error.response.data.detail}` : '';
      toast.error(`${message}${detail}`);
    } finally {
      setPageEditorSyncing(false);
    }
  }, [SERVER, combinedCustomCss, editingPage, headers, instanceUniqueId, loadSettings, pageEditorLayout, pageEditorSlot, selectedTemplateId, settings?.activeTemplate?.template_id, settings?.templateState?.template_id, templateForm]);

  const applyWidgetTemplatePreset = useCallback((slot, index, templateProps = {}) => {
    setLayoutDirty(true);
    setLayoutBuilder((prev) => {
      const next = normalizeLayoutSchema(prev);
      next.slots[slot] = (next.slots[slot] || []).map((widget, idx) =>
        idx === index
          ? { ...widget, props: { ...(widget.props || {}), ...(templateProps || {}) } }
          : widget
      );
      return next;
    });
  }, []);

  const showcaseProductOrder = useMemo(() => ({
    Products: productBrowse.data?.products || [],
    ShowcasePagination: productBrowse.data?.pagination || { total: 0 },
    ShowcaseTotal: Number(productBrowse.data?.summary?.importedCount || 0),
  }), [productBrowse.data]);

  const loadPromoShowcaseOrders = useCallback(async () => {
    if (!instanceUniqueId) return;
    setShowcaseOrdersLoading(true);
    try {
      const response = await axios.get(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/orders`,
        headers
      );
      setShowcaseOrders(Array.isArray(response?.data?.orders) ? response.data.orders : []);
    } catch (error) {
      console.error('Failed to load PromoShowcase orders', error);
      toast.error(error?.response?.data?.message || 'Unable to load Showcase orders.');
    } finally {
      setShowcaseOrdersLoading(false);
    }
  }, [SERVER, headers, instanceUniqueId]);

  const refreshPromoShowcaseOrders = useCallback(async () => {
    if (!settings.promoShowcaseEnabled) return;
    setShowcaseOrdersRefreshing(true);
    try {
      const response = await axios.post(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/orders/refresh`,
        {},
        headers
      );
      setShowcaseOrders(Array.isArray(response?.data?.orders) ? response.data.orders : []);
      const created = Array.isArray(response?.data?.autoCreatedProjects) ? response.data.autoCreatedProjects : [];
      const failed = Array.isArray(response?.data?.autoCreateErrors) ? response.data.autoCreateErrors : [];
      if (created.length) toast.success(`${created.length} paid Showcase order${created.length === 1 ? '' : 's'} created as Elite Core project${created.length === 1 ? '' : 's'}.`);
      if (failed.length) toast.warn(`${failed.length} paid order${failed.length === 1 ? '' : 's'} could not be auto-created as projects.`);
      if (!created.length && !failed.length) toast.success('Showcase orders refreshed.');
    } catch (error) {
      console.error('PromoShowcase orders refresh failed', error);
      toast.error(error?.response?.data?.message || 'Unable to refresh Showcase orders.');
    } finally {
      setShowcaseOrdersRefreshing(false);
    }
  }, [SERVER, headers, instanceUniqueId, settings.promoShowcaseEnabled]);

  const createProjectFromShowcaseOrder = useCallback(async (order) => {
    if (!order?.id) return;
    setCreatingProjectOrderId(order.id);
    try {
      const response = await axios.post(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/orders/${order.id}/create-project`,
        {},
        headers
      );
      toast.success(response?.data?.reused ? 'Existing project opened.' : 'Elite Core project created from Showcase order.');
      await loadPromoShowcaseOrders();
    } catch (error) {
      console.error('PromoShowcase create project failed', error);
      toast.error(error?.response?.data?.message || 'Unable to create project from Showcase order.');
    } finally {
      setCreatingProjectOrderId(null);
    }
  }, [SERVER, headers, instanceUniqueId, loadPromoShowcaseOrders]);

  async function loadSettingsImpl() {
    if (!instanceUniqueId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/settings`,
        headers
      );
      const next = res.data?.settings || defaultSettings;
      setSettings({
        instanceName: String(next.instanceName || instanceUniqueId || '').trim(),
        promoShowcaseEnabled: !!next.promoShowcaseEnabled,
        promoShowcaseOutputTarget: next.promoShowcaseOutputTarget || "wordpress",
        promoShowcaseUrl: next.promoShowcaseUrl || "",
        promoShowcaseHosting: next.promoShowcaseHosting && typeof next.promoShowcaseHosting === "object" ? next.promoShowcaseHosting : null,
        promoShowcaseConnected: !!next.promoShowcaseConnected,
        promoShowcaseConnectedAt: next.promoShowcaseConnectedAt || null,
        promoShowcaseLastError: next.promoShowcaseLastError || null,
        promoShowcaseBlogConnectorPath: next.promoShowcaseBlogConnectorPath || "/include/elitecore-blog.php",
        requestDesignerLabel: next.requestDesignerLabel || "Request designer",
        designerTeamUserIds: Array.isArray(next.designerTeamUserIds) ? next.designerTeamUserIds : [],
        templateSyncState: next.templateSyncState && typeof next.templateSyncState === "object" ? next.templateSyncState : defaultSettings.templateSyncState,
        templateCapabilities: next.templateCapabilities && typeof next.templateCapabilities === "object" ? next.templateCapabilities : null,
        productMerchandising: next.productMerchandising && typeof next.productMerchandising === "object"
          ? next.productMerchandising
          : defaultSettings.productMerchandising,
        activeTemplate: next.activeTemplate && typeof next.activeTemplate === "object" ? next.activeTemplate : null,
        templateState: next.templateState && typeof next.templateState === "object" ? next.templateState : null,
        canonicalDocument: next.canonicalDocument && typeof next.canonicalDocument === "object" ? next.canonicalDocument : null,
        templateHistory: Array.isArray(next.templateHistory) ? next.templateHistory : [],
      });
      setCustomDomainDraft(next?.promoShowcaseHosting?.customDomain || "");
      setDesignConfig(next.designConfig && typeof next.designConfig === "object" ? next.designConfig : {
        primaryColor: "", secondaryColor: "", heroTitle: "", heroSubtitle: "", logoUrl: "", globalStylesheet: "",
      });
      setAddonsConfig(next.addonsConfig && typeof next.addonsConfig === "object" ? next.addonsConfig : { enabled: {}, settings: {} });
      setDesignerTeamUsers(res.data?.teamUsers || []);
      const instanceBrandName = String(next.instanceName || instanceUniqueId || '').trim();
      if (instanceBrandName) {
        setTemplateForm((prev) => ({
          ...prev,
          brandName: (() => {
            const currentBrand = String(prev.brandName || '').trim();
            return currentBrand && currentBrand.toLowerCase() !== 'promoshowcase'
              ? currentBrand
              : instanceBrandName;
          })(),
        }));
      }
    } catch (error) {
      console.error("Failed to load PromoShowcase settings", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  const handleProvisionShowcase = async ({ title, slug }) => {
    setProvisioningShowcase(true);
    try {
      const response = await axios.post(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/hosting/provision`,
        { title, slug },
        headers
      );
      const createdUrl = response?.data?.site?.url || '';
      toast.success(response?.data?.reused ? "Existing Showcase site connected" : "Showcase website created");
      await loadSettings();
      if (createdUrl) {
        setLaunchVerification(null);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to create Showcase website.";
      toast.error(message);
    } finally {
      setProvisioningShowcase(false);
    }
  };

  const handleSaveCustomDomain = async () => {
    const domain = String(customDomainDraft || "").trim();
    if (!domain) {
      toast.error("Enter a custom domain first.");
      return;
    }
    setCustomDomainSaving(true);
    try {
      const response = await axios.put(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/hosting/custom-domain`,
        { domain },
        headers
      );
      toast.success("Custom domain saved. Update DNS, then verify.");
      await loadSettings();
      return response?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to save custom domain.");
    } finally {
      setCustomDomainSaving(false);
    }
  };

  const handleVerifyCustomDomain = async () => {
    setCustomDomainVerifying(true);
    try {
      const response = await axios.post(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/hosting/custom-domain/verify`,
        {},
        headers
      );
      toast.success(response?.data?.message || "Custom domain verified and mapped.");
      await loadSettings();
    } catch (error) {
      const data = error?.response?.data;
      toast.error(data?.message || "Custom domain is not ready yet.");
      await loadSettings();
    } finally {
      setCustomDomainVerifying(false);
    }
  };

  const handleReconnect = async () => {
    setConnecting(true);
    try {
      await axios.post(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/connect`,
        {},
        headers
      );
      toast.success("PromoShowcase connected");
      await loadSettings();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Unable to connect to PromoShowcase. Please verify URL / API Key / API Secret.";
      toast.error(message);
    } finally {
      setConnecting(false);
    }
  };
  const uploadShowcaseLogo = useCallback(async (file) => {
    if (!file) return '';
    const allowedTypes = new Set(['image/png', 'image/jpeg', 'image/webp']);
    if (!allowedTypes.has(file.type)) {
      toast.error('Logo must be PNG, JPG, or WebP.');
      return '';
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be 5 MB or smaller.');
      return '';
    }

    const dataBase64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const value = String(reader.result || '');
        resolve(value.includes(',') ? value.split(',').pop() : value);
      };
      reader.onerror = () => reject(reader.error || new Error('Unable to read logo file.'));
      reader.readAsDataURL(file);
    });

    try {
      const response = await axios.post(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/media/logo`,
        {
          filename: file.name,
          mime_type: file.type,
          data_base64: dataBase64,
        },
        headers
      );
      const url = String(response?.data?.url || '');
      if (!url) throw new Error('WordPress did not return a logo URL.');
      toast.success('Logo uploaded to WordPress.');
      return url;
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Unable to upload Showcase logo.');
      return '';
    }
  }, [SERVER, headers, instanceUniqueId]);

  const launchShowcaseFromWizard = useCallback(async (launchForm) => {
    if (!settings.promoShowcaseEnabled) {
      toast.error('Enable PromoShowcase before launching.');
      return;
    }
    if (!settings.promoShowcaseConnected) {
      toast.error('Connect the WordPress runtime before launching.');
      return;
    }

    const launchTemplateId = String(launchForm?.templateId || '').trim();
    const launchBrandName = String(launchForm?.brandName || '').trim();
    if (!launchTemplateId || !launchBrandName) {
      toast.error('Store name and site design are required.');
      return;
    }

    const selectedLaunchTemplate = (templates || []).find(
      (template) => String(template?.id) === launchTemplateId
    );
    if (!selectedLaunchTemplate) {
      toast.error('Selected site design is no longer available.');
      return;
    }

    const defaults = selectedLaunchTemplate?.defaults || {};
    const launchLayout = normalizeLayoutSchema(
      defaults?.layout_schema || defaults?.layout || createEmptyLayoutSchema()
    );

    const contactEmail = String(launchForm?.contactEmail || '').trim();
    const contactPhone = String(launchForm?.contactPhone || '').trim();
    const contactAddress = String(launchForm?.contactAddress || '').trim();
    Object.keys(launchLayout.slots || {}).forEach((slot) => {
      launchLayout.slots[slot] = (launchLayout.slots[slot] || []).map((widget) => {
        if (widget?.component !== 'contact_block') return widget;
        return {
          ...widget,
          props: {
            ...(widget.props || {}),
            ...(contactEmail ? { email: contactEmail } : {}),
            ...(contactPhone ? { phone: contactPhone } : {}),
            ...(contactAddress ? { address: contactAddress } : {}),
          },
        };
      });
    });
    const launchPages = ensureSystemShowcasePages(
      Array.isArray(defaults?.pages) && defaults.pages.length
        ? defaults.pages
        : DEFAULT_SHOWCASE_PAGES
    );
    const launchSiteChrome = cloneBuilderValue(defaults?.site_chrome || DEFAULT_SITE_CHROME);
    const footerBits = [
      launchBrandName,
      String(launchForm?.contactEmail || '').trim(),
      String(launchForm?.contactPhone || '').trim(),
    ].filter(Boolean);
    launchSiteChrome.footer = {
      ...(launchSiteChrome.footer || {}),
      text: launchSiteChrome?.footer?.text || footerBits.join(' · '),
    };
    const defaultStoreSettings = defaults?.store_settings || {};
    const launchMode = String(launchForm?.storeMode || 'quote');

    const paymentMethods = {
      request_quote: launchMode === 'quote',
      stripe: launchMode === 'checkout',
      ...(defaultStoreSettings?.payment_methods || {}),
      request_quote: launchMode === 'quote',
      stripe: launchMode === 'checkout',
    };

    const storeSettings = {
      ecommerce_enabled: launchMode !== 'catalog',
      show_prices: launchMode !== 'catalog',
      guest_checkout: true,
      taxes_enabled: true,
      shipping_enabled: true,
      currency: 'CAD',
      ...defaultStoreSettings,
      payment_methods: paymentMethods,
      orders: {
        paid_order_action: 'create_project',
        quote_order_action: 'manual',
        ...(defaultStoreSettings?.orders || {}),
      },
    };

    const overrides = {
      theme_vars: {
        ...(defaults?.theme_vars || {}),
        brand_name: launchBrandName,
        primary_color: launchForm?.primaryColor || defaults?.theme_vars?.primary_color || '#B91C1C',
        secondary_color: launchForm?.secondaryColor || defaults?.theme_vars?.secondary_color || '#111111',
        announcement_text: launchForm?.announcementText || defaults?.theme_vars?.announcement_text || '',
        logo_url: launchForm?.logoUrl || null,
        font_family: defaults?.theme_vars?.font_family || 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      },
      site_identity: {
        ...(defaults?.site_identity || {}),
        site_title: launchBrandName,
        tagline: String(launchForm?.tagline || '').trim(),
        meta_description: String(launchForm?.metaDescription || launchForm?.tagline || '').trim(),
        social_image_url: launchForm?.logoUrl || '',
        site_icon_url: launchForm?.logoUrl || '',
        contact: {
          email: String(launchForm?.contactEmail || '').trim(),
          phone: String(launchForm?.contactPhone || '').trim(),
          address: String(launchForm?.contactAddress || '').trim(),
        },
      },
      data_sources: {
        ...(defaults?.data_sources || {}),
        promoworld: {
          enabled: true,
          mode: 'selection_driven',
          derive_merchandising: true,
          ...(defaults?.data_sources?.promoworld || {}),
        },
      },
      css: String(defaults?.css || defaults?.template_css || '').trim() || null,
      custom_css: String(defaults?.custom_css || '').trim() || null,
      site_chrome: launchSiteChrome,
      pages: launchPages,
      store_settings: storeSettings,
      addons: defaults?.addons || addonsConfig || { enabled: {}, settings: {} },
      layout_schema: launchLayout,
    };

    setLaunchingShowcase(true);
    setLaunchVerification(null);
    try {
      const endpoint = `${SERVER}/promoshowcase/instances/${instanceUniqueId}/template/apply`;
      const applyRes = await publishBuilderDocument({
        axiosClient: axios,
        endpoint,
        headers,
        selectedTemplateId: launchTemplateId,
        overrides,
      });

      setSelectedTemplateId(launchTemplateId);
      persistSelectedTemplateId(instanceUniqueId, launchTemplateId);
      forceTemplateHydrationRef.current = true;
      setLastApplyResult(applyRes?.data || null);
      setTemplateForm((prev) => ({
        ...prev,
        brandName: launchBrandName,
        logoUrl: launchForm?.logoUrl || '',
        primaryColor: overrides.theme_vars.primary_color,
        secondaryColor: overrides.theme_vars.secondary_color,
        announcementText: overrides.theme_vars.announcement_text,
        fontFamily: overrides.theme_vars.font_family,
        siteIdentity: overrides.site_identity,
        pages: launchPages,
        siteChrome: launchSiteChrome,
        storeSettings,
      }));
      setLayoutBuilder(launchLayout);

      if (launchForm?.syncProducts && Number(storeProductSummary?.importedCount || 0) > 0) {
        const syncData = await runProductSync();
        if (syncData?.partial) {
          toast.warn(`Showcase launched, but product sync completed with ${syncData?.session?.failedCount || 0} failed products.`);
        }
      }

      const verificationResponse = await axios.get(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/launch/status`,
        headers
      );
      const verification = verificationResponse?.data?.launchStatus || null;
      setLaunchVerification(verification);

      await Promise.all([loadSettings(), loadPromoShowcaseProductSummary()]);
      if (verification?.ready) {
        toast.success('Showcase launched and verified.');
      } else {
        toast.warn('Showcase launched, but the verification checklist needs attention.');
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to launch PromoShowcase.';
      toast.error(message);
    } finally {
      setLaunchingShowcase(false);
    }
  }, [
    SERVER,
    addonsConfig,
    headers,
    instanceUniqueId,
    loadPromoShowcaseProductSummary,
    loadSettings,
    settings.promoShowcaseConnected,
    settings.promoShowcaseEnabled,
    storeProductSummary?.importedCount,
    runProductSync,
    templates,
  ]);


  const loadDesignConfig = useCallback(async () => {
    if (!instanceUniqueId) return;
    try {
      const res = await axios.get(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/design`, headers);
      const next = res?.data?.designConfig && typeof res.data.designConfig === "object" ? res.data.designConfig : {};
      setDesignConfig(next);
    } catch (error) {
      console.error("Failed to load PromoShowcase design config", error);
    }
  }, [SERVER, headers, instanceUniqueId]);

  const loadAddonsConfig = useCallback(async () => {
    if (!instanceUniqueId) return;
    try {
      const res = await axios.get(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/addons`, headers);
      const next = res?.data?.addonsConfig && typeof res.data.addonsConfig === "object" ? res.data.addonsConfig : { enabled: {}, settings: {} };
      setAddonsConfig(next);
    } catch (error) {
      console.error("Failed to load PromoShowcase add-ons config", error);
    }
  }, [SERVER, headers, instanceUniqueId]);

  const saveAddonsConfig = async () => {
    if (!settings.promoShowcaseEnabled) {
      toast.error("Enable PromoShowcase first.");
      return;
    }
    setSavingAddons(true);
    try {
      await axios.put(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/addons`, { addonsConfig }, headers);
      toast.success("PromoShowcase add-ons settings saved.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to save add-ons settings.");
    } finally {
      setSavingAddons(false);
    }
  };

  useEffect(() => {
    if (activeTab === 0) loadDesignConfig();
  }, [activeTab, loadDesignConfig]);

  useEffect(() => {
    if (activeTab === 2) loadAddonsConfig();
  }, [activeTab, loadAddonsConfig]);


  const loadTemplates = useCallback(async () => {
    if (!instanceUniqueId) return;
    setTemplatesLoading(true);
    try {
      const res = await axios.get(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/templates`, headers);
      const next = Array.isArray(res?.data?.templates) ? res.data.templates : [];
      setTemplates(next);
      const persistedTemplateId = readSelectedTemplateId(instanceUniqueId);
      const activeTemplateId = String(settings?.activeTemplate?.template_id || settings?.templateState?.template_id || '').trim();
      const resolved = resolveTemplateSelection({
        templates: next,
        currentTemplateId: selectedTemplateId,
        persistedTemplateId,
        activeTemplateId,
      });
      if (resolved.persistedSelectionWasStale) persistSelectedTemplateId(instanceUniqueId, '');
      if (resolved.selectedTemplateId && String(resolved.selectedTemplateId) !== String(selectedTemplateId)) {
        forceTemplateHydrationRef.current = true;
        setSelectedTemplateId(resolved.selectedTemplateId);
      }
      if (resolved.selectedTemplateId) persistSelectedTemplateId(instanceUniqueId, resolved.selectedTemplateId);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to load templates.');
    } finally {
      setTemplatesLoading(false);
    }
  }, [SERVER, headers, instanceUniqueId, selectedTemplateId, settings?.activeTemplate?.template_id, settings?.templateState?.template_id]);

  const uploadShowcaseMedia = useCallback(async (file) => {
    if (!file) throw new Error('Choose an image to upload.');
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      `${SERVER}/promoshowcase/instances/${instanceUniqueId}/media/upload`,
      formData,
      headers
    );
    const media = response?.data?.media || null;
    if (!media?.url) throw new Error('Showcase media upload did not return a file URL.');
    return media;
  }, [SERVER, headers, instanceUniqueId]);

  const runTemplatePreflight = useCallback(() => {
    // Authoring support is defined by the current Core Elements registry.
    // Do not intersect this with cached/older WordPress capability responses:
    // that caused newly supported widgets (for example cards_grid) to be
    // incorrectly rejected before the request ever reached the server/runtime.
    //
    // The Core server still validates the canonical document, and the active
    // WordPress adapter remains authoritative if a genuinely old deployment
    // cannot render a component.
    const errors = getPublishPreflightErrors({
      selectedTemplateId,
      brandName: templateForm.brandName,
      layoutBuilder,
      normalizeWidgetComponent,
      supportedComponents: SUPPORTED_TEMPLATE_COMPONENTS,
    });
    setTemplateFormErrors(errors);
    return errors;
  }, [layoutBuilder, selectedTemplateId, templateForm.brandName]);

  const savePrivateTemplate = useCallback(async () => {
    if (!settings.promoShowcaseEnabled) return toast.error('Enable PromoShowcase first.');
    if (!templateForm.brandName) return toast.error('Brand name is required.');

    setTemplatesSaving(true);
    try {
      const saved = await persistBuilderDraft({
        axiosClient: axios,
        templatesUrl: `${SERVER}/promoshowcase/instances/${instanceUniqueId}/templates`,
        headers,
        selectedTemplate,
        createTemplateId: () => `private_${instanceUniqueId}_${Date.now()}`,
        createName: `${String(templateForm.brandName || 'Showcase').replace(/PromoShowcase/gi, 'Showcase')} Saved Draft`,
        status: templateStatus,
        defaults: {
          theme_vars: {
            brand_name: templateForm.brandName,
            primary_color: templateForm.primaryColor,
            secondary_color: templateForm.secondaryColor || null,
            announcement_text: templateForm.announcementText || '',
            logo_url: templateForm.logoUrl || null,
            font_family: templateForm.fontFamily || null,
          },
          data_sources: {
            promoworld: {
              enabled: true,
              mode: 'selection_driven',
              derive_merchandising: true,
              featured_categories: String(templateForm.featuredCategories || '').split(',').map((v) => v.trim()).filter(Boolean),
            },
          },
          css: stripLegacyWidgetShellCss(templateForm.templateCss) || null,
          custom_css: String(templateForm.customCss || '').trim() || null,
          site_chrome: templateForm.siteChrome || DEFAULT_SITE_CHROME,
          pages: Array.isArray(templateForm.pages) ? templateForm.pages : DEFAULT_SHOWCASE_PAGES,
          store_settings: templateForm.storeSettings || {},
          addons: addonsConfig || { enabled: {}, settings: {} },
          layout_schema: normalizeLayoutSchema(layoutBuilder),
        },
      });

      // The API response is authoritative. Updating the catalog locally avoids a
      // refresh/hydration cycle and keeps the exact in-memory builder document visible.
      lastHydratedTemplateIdRef.current = String(saved.selectedTemplateId);
      forceTemplateHydrationRef.current = false;
      setTemplates((current) => upsertTemplate(current, saved.template));
      setSelectedTemplateId(saved.selectedTemplateId);
      persistSelectedTemplateId(instanceUniqueId, saved.selectedTemplateId);
      setLayoutDirty(false);
      toast.success('Draft saved.');
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Unable to save draft.');
    } finally {
      setTemplatesSaving(false);
    }
  }, [SERVER, addonsConfig, headers, instanceUniqueId, layoutBuilder, selectedTemplate, settings.promoShowcaseEnabled, templateForm, templateStatus]);

  const updateSelectedPrivateTemplate = useCallback(async () => {
    if (!selectedTemplate || selectedTemplate.visibility !== 'private') return toast.error('Select a private template to update.');
    return savePrivateTemplate();
  }, [savePrivateTemplate, selectedTemplate]);

  const applySelectedTemplate = useCallback(async () => {
    if (!settings.promoShowcaseEnabled) return toast.error('Enable PromoShowcase first.');
    const preflightErrors = runTemplatePreflight();
    if (preflightErrors.length) {
      toast.error(getPublishPreflightMessage(preflightErrors));
      window.setTimeout(() => {
        publishValidationRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
        publishValidationRef.current?.focus?.();
      }, 0);
      if (process.env.NODE_ENV !== 'production') console.info('[PromoShowcase publish] preflight failed', { errors: preflightErrors });
      return;
    }

    setTemplatesApplying(true);
    try {
      const endpoint = `${SERVER}/promoshowcase/instances/${instanceUniqueId}/template/apply`;
      const overrides = {
        theme_vars: {
          brand_name: templateForm.brandName || undefined,
          primary_color: templateForm.primaryColor || undefined,
          secondary_color: templateForm.secondaryColor || undefined,
          announcement_text: templateForm.announcementText || undefined,
          logo_url: templateForm.logoUrl || undefined,
          font_family: templateForm.fontFamily || undefined,
        },
        data_sources: {
          promoworld: {
            enabled: true,
            mode: 'selection_driven',
            derive_merchandising: true,
            featured_categories: String(templateForm.featuredCategories || '').split(',').map((v) => v.trim()).filter(Boolean),
          },
        },
        css: String(templateForm.templateCss || '').trim() || null,
        custom_css: combinedCustomCss || null,
        site_chrome: templateForm.siteChrome || DEFAULT_SITE_CHROME,
        pages: Array.isArray(templateForm.pages) ? templateForm.pages : DEFAULT_SHOWCASE_PAGES,
        store_settings: templateForm.storeSettings || {},
        addons: addonsConfig || { enabled: {}, settings: {} },
        layout_schema: normalizeLayoutSchema(layoutBuilder),
      };
      if (process.env.NODE_ENV !== 'production') {
        console.info('[PromoShowcase publish] starting', {
          selectedTemplateId,
          slotCounts: Object.fromEntries(Object.entries(overrides.layout_schema?.slots || {}).map(([slot, widgets]) => [slot, Array.isArray(widgets) ? widgets.length : 0])),
          endpoint,
        });
      }
      const applyRes = await publishBuilderDocument({ axiosClient: axios, endpoint, headers, selectedTemplateId, overrides });
      setLastApplyResult(applyRes?.data || null);
      const wpPages = Array.isArray(applyRes?.data?.wp?.pages) ? applyRes.data.wp.pages : [];
      const syncedCount = Number(applyRes?.data?.pagesSynced || wpPages.length || 0);
      const homePage = wpPages.find((p) => String(p?.slug || '').toLowerCase() === 'home');
      const updatedCount = wpPages.filter((p) => p?.updated).length;
      const createdCount = wpPages.filter((p) => p?.created).length;
      const homeNote = homePage?.updated ? ' Home page already existed and was updated.' : (homePage?.created ? ' Home page was created.' : '');
      const syncNote = applyRes?.data?.pagesSyncSource === 'pages_endpoint_probe' ? ' Synced page snapshot from pages endpoint.' : '';
      if (process.env.NODE_ENV !== 'production') console.info('[PromoShowcase publish] completed', { pagesSynced: syncedCount, pagesSyncSource: applyRes?.data?.pagesSyncSource });
      const successMessage = `Site design published to WordPress. Synced ${syncedCount} page records (${updatedCount} updated, ${createdCount} created).${homeNote}${syncNote}`.trim();
      if (syncedCount === 0) {
        toast.warn(`${successMessage} WordPress returned no page records; refresh pages to confirm synchronization.`);
      } else {
        toast.success(successMessage);
      }
      setLayoutDirty(false);
      await loadSettings();
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to apply template.';
      const detail = error?.response?.data?.detail ? ` ${error.response.data.detail}` : '';
      const attempts = Array.isArray(error?.response?.data?.attempts) ? error.response.data.attempts : [];
      const attemptHint = attempts.length ? ` First attempt: ${attempts[0]}` : '';
      toast.error(`${message}${detail}${attemptHint}`);
    } finally {
      setTemplatesApplying(false);
    }
  }, [SERVER, addonsConfig, combinedCustomCss, headers, instanceUniqueId, layoutBuilder, loadSettings, runTemplatePreflight, selectedTemplateId, settings.promoShowcaseEnabled, templateForm]);

  const handleResyncNow = useCallback(async () => {
    await applySelectedTemplate();
  }, [applySelectedTemplate]);

  const handleRollbackLatest = useCallback(async () => {
    const historyItem = settings?.templateHistory?.[1] || settings?.templateHistory?.[0];
    const currentPayload = settings?.templateState;
    if (!historyItem || !currentPayload) return toast.error('No rollback payload available yet.');

    setTemplatesApplying(true);
    try {
      const rollbackRes = await axios.post(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/template/apply`, { payload: currentPayload }, headers);
      setLastApplyResult(rollbackRes?.data || null);
      toast.success('Rollback apply sent.');
      await loadSettings();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Rollback failed.');
    } finally {
      setTemplatesApplying(false);
    }
  }, [SERVER, headers, instanceUniqueId, loadSettings, settings?.templateHistory, settings?.templateState]);

  const loadFormSubmissions = useCallback(async () => {
    if (!instanceUniqueId) return;
    setFormSubmissionsLoading(true);
    try {
      const response = await axios.get(
        `${SERVER}/promoshowcase/instances/${instanceUniqueId}/forms/submissions`,
        headers
      );
      setFormSubmissions(Array.isArray(response?.data?.submissions) ? response.data.submissions : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to load form submissions.');
    } finally {
      setFormSubmissionsLoading(false);
    }
  }, [SERVER, headers, instanceUniqueId]);

  useEffect(() => {
    if (activeTab === 7) loadTemplates();
    if (activeTab === 9) loadPromoShowcaseOrders();
    if (activeTab === 10) loadFormSubmissions();
  }, [activeTab, loadFormSubmissions, loadPromoShowcaseOrders, loadTemplates]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('tab') === 'forms') setActiveTab(10);
  }, []);

  const resetBuilderFromSelectedTemplate = useCallback(() => {
    if (!selectedTemplate) return;

    const selectedId = String(selectedTemplate?.id || '');
    const activeTemplateId = String(settings?.activeTemplate?.template_id || '');
    // A private template is the authoritative editable draft. templateState is
    // the last published snapshot and may be older than a subsequently saved draft.
    const shouldUseActiveState = shouldUsePublishedTemplateState({
      selectedTemplate,
      activeTemplateId,
      force: forceTemplateHydrationRef.current,
    });

    const templateState = shouldUseActiveState && settings?.templateState && typeof settings.templateState === 'object'
      ? settings.templateState
      : null;

    const existingLayout = templateState?.layout_schema
      || templateState?.layout
      || selectedTemplate?.defaults?.layout_schema
      || selectedTemplate?.layout_schema
      || selectedTemplate?.layout;

    setLayoutBuilder(normalizeLayoutSchema(existingLayout));

    const defaults = selectedTemplate?.defaults || {};
    const themeVars = {
      ...(defaults?.theme_vars || {}),
      ...(templateState?.theme_vars || {}),
    };
    const featuredCategories = templateState?.data_sources?.promoworld?.featured_categories
      || defaults?.data_sources?.promoworld?.featured_categories
      || [];
    const templateCss = stripLegacyWidgetShellCss(String(
      templateState?.template_css
      || defaults?.template_css
      || selectedTemplate?.template_css
      || templateState?.css
      || defaults?.css
      || selectedTemplate?.css
      || ''
    ));
    const customCssRaw = String(templateState?.custom_css || defaults?.custom_css || selectedTemplate?.custom_css || '').trim();
    const customCss = stripGlobalStylesheetFromCustomCss({
      globalCss: globalStylesheet,
      customCss: customCssRaw,
    });

    const nextSiteIdentity = {
      site_title: '',
      tagline: '',
      meta_description: '',
      social_image_url: '',
      site_icon_url: '',
      contact: { email: '', phone: '', address: '' },
      ...(defaults?.site_identity || {}),
      ...(templateState?.site_identity || {}),
      contact: {
        ...(defaults?.site_identity?.contact || {}),
        ...(templateState?.site_identity?.contact || {}),
      },
    };

    const nextPages = ensureSystemShowcasePages(
      Array.isArray(templateState?.pages) && templateState.pages.length
        ? templateState.pages
        : (Array.isArray(defaults?.pages) && defaults.pages.length ? defaults.pages : DEFAULT_SHOWCASE_PAGES)
    );
    const nextSiteChrome = {
      ...DEFAULT_SITE_CHROME,
      ...(defaults?.site_chrome || {}),
      ...(templateState?.site_chrome || {}),
      header: { ...DEFAULT_SITE_CHROME.header, ...(defaults?.site_chrome?.header || {}), ...(templateState?.site_chrome?.header || {}) },
      navigation: {
        ...DEFAULT_SITE_CHROME.navigation,
        ...(defaults?.site_chrome?.navigation || {}),
        ...(templateState?.site_chrome?.navigation || {}),
        menus: Array.isArray(templateState?.site_chrome?.navigation?.menus)
          ? templateState.site_chrome.navigation.menus
          : (Array.isArray(defaults?.site_chrome?.navigation?.menus)
            ? defaults.site_chrome.navigation.menus
            : DEFAULT_SITE_CHROME.navigation.menus),
      },
      footer: { ...DEFAULT_SITE_CHROME.footer, ...(defaults?.site_chrome?.footer || {}), ...(templateState?.site_chrome?.footer || {}) },
    };

    setTemplateForm((prev) => ({
      ...prev,
      brandName: (() => {
        const storedBrand = String(themeVars.brand_name || '').trim();
        const previousBrand = String(prev.brandName || '').trim();
        const instanceBrand = String(settings?.instanceName || instanceUniqueId || '').trim();
        if (storedBrand && storedBrand.toLowerCase() !== 'promoshowcase') return storedBrand;
        if (previousBrand && previousBrand.toLowerCase() !== 'promoshowcase') return previousBrand;
        return instanceBrand;
      })(),
      primaryColor: String(themeVars.primary_color || prev.primaryColor || '#B91C1C'),
      secondaryColor: String(themeVars.secondary_color || prev.secondaryColor || '#111111'),
      announcementText: String(themeVars.announcement_text || prev.announcementText || ''),
      logoUrl: String(themeVars.logo_url || prev.logoUrl || ''),
      fontFamily: String(themeVars.font_family || prev.fontFamily || 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'),
      siteIdentity: nextSiteIdentity,
      featuredCategories: Array.isArray(featuredCategories) ? featuredCategories.join(', ') : prev.featuredCategories,
      templateCss,
      customCss,
      pages: nextPages,
      siteChrome: nextSiteChrome,
      storeSettings: {
        ecommerce_enabled: true,
        show_prices: true,
        guest_checkout: true,
        taxes_enabled: true,
        shipping_enabled: true,
        currency: 'CAD',
        payment_methods: { request_quote: true, stripe: false },
        request_quote: {
          title: 'Request Quote/Estimate',
          description: 'Request a quote for your order.',
          checkout_button_text: 'Submit Request',
        },
        ...(defaults?.store_settings || {}),
        ...(templateState?.store_settings || {}),
        payment_methods: {
          request_quote: true,
          stripe: false,
          ...(defaults?.store_settings?.payment_methods || {}),
          ...(templateState?.store_settings?.payment_methods || {}),
        },
        request_quote: {
          title: 'Request Quote/Estimate',
          description: 'Request a quote for your order.',
          checkout_button_text: 'Submit Request',
          ...(defaults?.store_settings?.request_quote || {}),
          ...(templateState?.store_settings?.request_quote || {}),
        },
        orders: {
          paid_order_action: 'create_project',
          quote_order_action: 'manual',
          ...(defaults?.store_settings?.orders || {}),
          ...(templateState?.store_settings?.orders || {}),
        },
        catalog_filters: {
          enabled: true,
          category: true,
          theme: true,
          brand: true,
          supplier: true,
          color: true,
          size: true,
          price: true,
          order: ['category', 'theme', 'brand', 'supplier', 'color', 'size', 'price'],
          ...(defaults?.store_settings?.catalog_filters || {}),
          ...(templateState?.store_settings?.catalog_filters || {}),
        },
      },
    }));

    setTemplateStatus(String(selectedTemplate?.status || 'active'));
    setLayoutDirty(false);
    lastHydratedTemplateIdRef.current = selectedId;
    forceTemplateHydrationRef.current = false;
  }, [globalStylesheet, instanceUniqueId, selectedTemplate, settings?.activeTemplate?.template_id, settings?.instanceName, settings?.templateState]);

  useEffect(() => {
    if (!selectedTemplate) return;
    const selectedId = String(selectedTemplate?.id || '');
    const shouldHydrate = shouldHydrateSelectedTemplate({
      force: forceTemplateHydrationRef.current,
      selectedTemplateId: selectedId,
      lastHydratedTemplateId: lastHydratedTemplateIdRef.current,
    });
    if (!shouldHydrate) return;
    resetBuilderFromSelectedTemplate();
  }, [resetBuilderFromSelectedTemplate, selectedTemplate]);

  const connectedLabel = settings.promoShowcaseConnected ? "Connected" : "Not connected";
  const connectedAtLabel = settings.promoShowcaseConnectedAt
    ? new Date(settings.promoShowcaseConnectedAt).toLocaleString()
    : "Not connected yet";

  const connectionUrl = useMemo(
    () => clampUrl(settings.promoShowcaseUrl),
    [settings.promoShowcaseUrl]
  );
  const siteUrl = useMemo(
    () => clampUrl(
      settings?.promoShowcaseHosting?.publicUrl
      || settings?.promoShowcaseHosting?.effectivePublicUrl
      || settings.promoShowcaseUrl
    ),
    [
      settings?.promoShowcaseHosting?.publicUrl,
      settings?.promoShowcaseHosting?.effectivePublicUrl,
      settings.promoShowcaseUrl,
    ]
  );

  const adminUrl = useMemo(() => {
    if (!connectionUrl) return "";
    return `${connectionUrl.replace(/\/$/, "")}/wp-admin`;
  }, [connectionUrl]);

  const activeTemplateLabel = useMemo(() => {
    const templateName = settings?.activeTemplate?.name
      || settings?.activeTemplate?.template_name
      || selectedTemplate?.name
      || selectedTemplate?.title
      || '';
    const templateId = settings?.activeTemplate?.template_id
      || settings?.templateState?.template_id
      || selectedTemplateId
      || selectedTemplate?.id
      || '';

    if (templateName && templateId && String(templateName) !== String(templateId)) {
      return `${templateName} (${templateId})`;
    }

    return String(templateName || templateId || 'No template applied');
  }, [
    selectedTemplate?.id,
    selectedTemplate?.name,
    selectedTemplate?.title,
    selectedTemplateId,
    settings?.activeTemplate?.name,
    settings?.activeTemplate?.template_id,
    settings?.activeTemplate?.template_name,
    settings?.templateState?.template_id,
  ]);

  const templateSyncUpdatedAtLabel = useMemo(() => {
    if (!settings?.templateSyncState?.updatedAt) return 'Not available';
    const date = new Date(settings.templateSyncState.updatedAt);
    if (Number.isNaN(date.getTime())) return 'Not available';
    return date.toLocaleString();
  }, [settings?.templateSyncState?.updatedAt]);

  const productSyncUpdatedAtLabel = useMemo(() => {
    if (!storeProductSummary?.syncMeta?.lastSyncedAt) return 'Not available';
    const date = new Date(storeProductSummary.syncMeta.lastSyncedAt);
    if (Number.isNaN(date.getTime())) return 'Not available';
    return date.toLocaleString();
  }, [storeProductSummary?.syncMeta?.lastSyncedAt]);

  const buildPageViewUrl = useCallback((page) => {
    const base = siteUrl ? siteUrl.replace(/\/$/, "") : "";
    const slug = page?.slug || "";
    if (page?.link) return page.link;
    if (!base) return "";
    if (!slug || slug === "home" || page?.isFrontPage) return `${base}/`;
    return `${base}/${String(slug).replace(/^\/+/, "")}`;
  }, [siteUrl]);

  const buildPageEditUrl = useCallback((page) => {
    const base = connectionUrl ? connectionUrl.replace(/\/$/, "") : "";
    const id = page?.id || page?.wpPageId || page?.ID || page?.post_id;
    if (!base || !id) return "";
    return `${base}/wp-admin/post.php?post=${id}&action=elementor`;
  }, [connectionUrl]);

  const getIsoDateLabel = useCallback((value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString();
  }, []);

  const isRecentDate = useCallback((value, days = 5) => {
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    return (Date.now() - date.getTime()) <= (days * 24 * 60 * 60 * 1000);
  }, []);

  const filteredSitePages = useMemo(() => {
    const query = String(sitePagesQuery || '').trim().toLowerCase();
    const sorted = [...sitePages].sort((a, b) => {
      const aDate = new Date(a?.dateGmt || a?.date_gmt || 0).getTime();
      const bDate = new Date(b?.dateGmt || b?.date_gmt || 0).getTime();
      return bDate - aDate;
    });
    if (!query) return sorted;
    return sorted.filter((page) => {
      const title = String(page?.title?.rendered || page?.title || '').toLowerCase();
      const slug = String(page?.slug || '').toLowerCase();
      const slot = String(page?.promoshowcase?.slot || '').toLowerCase();
      return title.includes(query) || slug.includes(query) || slot.includes(query);
    });
  }, [sitePages, sitePagesQuery]);

  const filteredSiteUsers = useMemo(() => {
    const query = String(siteUsersQuery || '').trim().toLowerCase();
    if (!query) return siteUsers;
    return siteUsers.filter((user) => {
      const name = String(user?.displayName || user?.display_name || user?.userLogin || user?.user_login || '').toLowerCase();
      const email = String(user?.email || user?.user_email || '').toLowerCase();
      const roles = Array.isArray(user?.roles) ? user.roles.join(' ').toLowerCase() : String(user?.roles || '').toLowerCase();
      return name.includes(query) || email.includes(query) || roles.includes(query);
    });
  }, [siteUsers, siteUsersQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTemplateCss(String(templateForm.templateCss || ''));
      setDebouncedCustomCss(String(templateForm.customCss || ''));
    }, 220);
    return () => clearTimeout(timer);
  }, [templateForm.customCss, templateForm.templateCss]);

  const localTemplateStylesPreviewSrcDoc = useMemo(() => {
    const widgets = layoutBuilder?.slots?.[previewSlot] || [];
    return buildTemplateStylePreviewDoc({
      slot: previewSlot,
      widgets,
      products: storeProductSummary.products,
      merchandising: settings.productMerchandising,
      storeSettings: templateForm.storeSettings,
      templateCss: debouncedTemplateCss,
      customCss: mergeCssBlocks(globalStylesheet, debouncedCustomCss),
      brandName: templateForm.brandName,
      primaryColor: templateForm.primaryColor,
      secondaryColor: templateForm.secondaryColor,
      announcementText: templateForm.announcementText,
      logoUrl: templateForm.logoUrl,
      fontFamily: templateForm.fontFamily,
      pages: templateForm.pages,
      siteChrome: templateForm.siteChrome,
    });
  }, [
    debouncedCustomCss,
    debouncedTemplateCss,
    globalStylesheet,
    layoutBuilder?.slots,
    previewSlot,
    storeProductSummary.products,
    settings.productMerchandising,
    templateForm.storeSettings,
    templateForm.announcementText,
    templateForm.brandName,
    templateForm.logoUrl,
    templateForm.fontFamily,
    templateForm.primaryColor,
    templateForm.secondaryColor,
    templateForm.pages,
    templateForm.siteChrome,
  ]);

  useEffect(() => {
    if (previewRendererMode !== 'wordpress') return undefined;
    if (!settings.promoShowcaseEnabled || !selectedTemplateId) {
      setWordpressPreviewHtml('');
      setWordpressPreviewError('Connect PromoShowcase and select a template to use WordPress Preview.');
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setWordpressPreviewLoading(true);
      setWordpressPreviewError('');
      try {
        const response = await axios.post(
          `${SERVER}/promoshowcase/instances/${instanceUniqueId}/template/preview`,
          {
            templateId: selectedTemplateId,
            previewSlot,
            overrides: {
              theme_vars: {
                brand_name: templateForm.brandName || undefined,
                primary_color: templateForm.primaryColor || undefined,
                secondary_color: templateForm.secondaryColor || undefined,
                announcement_text: templateForm.announcementText || undefined,
                logo_url: templateForm.logoUrl || undefined,
          font_family: templateForm.fontFamily || undefined,
              },
              data_sources: {
                promoworld: {
                  enabled: true,
                  mode: 'selection_driven',
                  derive_merchandising: true,
                  featured_categories: String(templateForm.featuredCategories || '').split(',').map((value) => value.trim()).filter(Boolean),
                },
              },
              css: String(templateForm.templateCss || '').trim() || null,
              custom_css: combinedCustomCss || null,
              site_chrome: templateForm.siteChrome || DEFAULT_SITE_CHROME,
              pages: Array.isArray(templateForm.pages) ? templateForm.pages : DEFAULT_SHOWCASE_PAGES,
              store_settings: templateForm.storeSettings || {},
              addons: addonsConfig || { enabled: {}, settings: {} },
              layout_schema: normalizeLayoutSchema(layoutBuilder),
            },
          },
          headers
        );
        if (!cancelled) {
          setWordpressPreviewHtml(String(response?.data?.html || ''));
          setWordpressPreviewError('');
        }
      } catch (error) {
        if (!cancelled) {
          setWordpressPreviewError(error?.response?.data?.message || 'Unable to render WordPress Preview.');
        }
      } finally {
        if (!cancelled) setWordpressPreviewLoading(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [SERVER, addonsConfig, combinedCustomCss, headers, instanceUniqueId, layoutBuilder, previewRendererMode, previewSlot, selectedTemplateId, settings.promoShowcaseEnabled, templateForm]);

  const templateStylesPreviewSrcDoc = previewRendererMode === 'wordpress' && wordpressPreviewHtml
    ? wordpressPreviewHtml
    : localTemplateStylesPreviewSrcDoc;

    const pageEditorTemplateStylesPreviewSrcDoc = useMemo(() => {
    const widgets = pageEditorLayout?.slots?.[pageEditorSlot] || [];
    return buildTemplateStylePreviewDoc({
      slot: pageEditorSlot,
      widgets,
      products: storeProductSummary.products,
      merchandising: settings.productMerchandising,
      storeSettings: templateForm.storeSettings,
      templateCss: debouncedTemplateCss,
      customCss: mergeCssBlocks(globalStylesheet, debouncedCustomCss),
      brandName: templateForm.brandName,
      primaryColor: templateForm.primaryColor,
      secondaryColor: templateForm.secondaryColor,
      announcementText: templateForm.announcementText,
      logoUrl: templateForm.logoUrl,
      fontFamily: templateForm.fontFamily,
      pages: templateForm.pages,
      siteChrome: templateForm.siteChrome,
    });
  }, [
    debouncedCustomCss,
    debouncedTemplateCss,
    globalStylesheet,
    pageEditorLayout?.slots,
    pageEditorSlot,
    storeProductSummary.products,
    templateForm.storeSettings,
    templateForm.announcementText,
    templateForm.brandName,
    templateForm.logoUrl,
    templateForm.fontFamily,
    templateForm.primaryColor,
    templateForm.secondaryColor,
    templateForm.pages,
    templateForm.siteChrome,
  ]);


  const insertSelectorIntoCustomCss = useCallback((selector) => {
    const clean = String(selector || '').trim();
    if (!clean) return;
    setTemplateForm((prev) => {
      const current = String(prev?.customCss || '');
      const snippet = `${clean} {\n  \n}`;
      if (current.includes(`${clean} {`)) return prev;
      const spacer = current.trim() ? '\n\n' : '';
      const nextCss = `${current.trim()}${spacer}${snippet}`;
      return { ...prev, customCss: nextCss };
    });
  }, []);

  useEffect(() => {
    const onPreviewMessage = (event) => {
      const payload = event?.data;
      if (!payload || payload.source !== 'ps-template-style-preview') return;
      const selector = String(payload.selector || '').trim();
      if (payload.type === 'hover') {
        setPreviewHoveredSelector(selector);
      }
      if (payload.type === 'select') {
        setPreviewSelectedSelector(selector);
        const nodeId = String(payload.nodeId || '').trim();
        if (nodeId) {
          const index = currentBuilderWidgets.findIndex((widget) => String(widget?.id || '') === nodeId);
          if (index >= 0) {
            setSelectedBuilderWidgetIndex(index);
            setBuilderRequestedSidebarView('inspector');
            setBuilderRequestedSidebarViewTick((tick) => tick + 1);
          }
        }
      }
    };
    window.addEventListener('message', onPreviewMessage);
    return () => window.removeEventListener('message', onPreviewMessage);
  }, [currentBuilderWidgets]);

  const renderWidgetPreviewCard = (widget, idx, slotKey = previewSlot) => {
    const widgetMeta = getWidgetMeta(widget?.component);
    const WidgetIcon = widgetMeta.icon;
    const props = widget?.props || {};

    if (widget?.component === 'hero') {
      const slides = Array.isArray(props.slides) ? props.slides : [];
      const activeSlide = slides[0] || {};
      const bg = activeSlide?.background_url || '';
      const minHeight = Number(props?.min_height || 360);
      return (
        <Box key={`preview-${slotKey}-${idx}`} sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${BRAND.line}`, background: '#fff' }}>
          <Box sx={{
            minHeight: Math.max(220, Math.min(minHeight, 620)),
            p: 2,
            display: 'grid',
            alignContent: 'end',
            gap: 0.75,
            background: bg
              ? `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.55)), url(${bg}) center/cover no-repeat`
              : 'linear-gradient(140deg, rgba(185,28,28,0.18), rgba(255,106,61,0.20))',
            color: bg ? '#fff' : BRAND.ink,
          }}>
            <Typography sx={{ fontWeight: 900, fontSize: 24 }}>{activeSlide?.h1 || props?.title || 'Hero headline'}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{activeSlide?.paragraph || props?.subtitle || 'Hero supporting copy preview.'}</Typography>
            {(activeSlide?.button_text || props?.cta_text) ? (
              <Box sx={{ mt: 0.5 }}>
                <Chip size="small" label={activeSlide?.button_text || props?.cta_text} sx={{ fontWeight: 700, background: '#fff', color: BRAND.ink }} />
              </Box>
            ) : null}
          </Box>
          <Box sx={{ p: 1, borderTop: `1px solid ${BRAND.line}`, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: BRAND.slate }}>Hero • min-height {Math.max(220, Math.min(minHeight, 620))}px</Typography>
            <Typography variant="caption" sx={{ color: BRAND.slate }}>{slides.length || 0} slide(s)</Typography>
          </Box>
        </Box>
      );
    }

    if (widget?.component === 'product_grid' || widget?.component === 'category_grid' || widget?.component === 'brand_grid' || widget?.component === 'theme_grid') {
      const colsDesktop = Number(props?.columns_desktop || 4);
      const items = Array.from({ length: Math.min(8, Number(props?.limit || 8) || 8) });
      return (
        <Box key={`preview-${slotKey}-${idx}`} sx={{ p: 1, borderRadius: 1.5, border: `1px solid ${BRAND.line}`, background: 'rgba(255,255,255,0.96)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
            <WidgetIcon sx={{ fontSize: 16, color: widgetMeta.accent }} />
            <Typography sx={{ fontWeight: 700, color: BRAND.ink }}>{props?.heading || props?.title || widget.component}</Typography>
          </Box>
          <Box sx={{ display: 'grid', gap: 0.6, gridTemplateColumns: `repeat(${Math.max(2, Math.min(colsDesktop, 8))}, minmax(0,1fr))` }}>
            {items.map((_, i) => (
              <Box key={`ph-${slotKey}-${idx}-${i}`} sx={{
                height: 46,
                borderRadius: 1,
                border: `1px dashed ${BRAND.line}`,
                background: 'linear-gradient(135deg, rgba(148,163,184,0.10), rgba(203,213,225,0.16))',
                display: 'grid',
                placeItems: 'center',
              }}>
                <WidgetIcon sx={{ fontSize: 14, color: BRAND.slate }} />
              </Box>
            ))}
          </Box>
          <Typography variant="caption" sx={{ color: BRAND.slate, mt: 0.6, display: 'block' }}>Grid preview • desktop cols: {Math.max(2, Math.min(colsDesktop, 8))}</Typography>
        </Box>
      );
    }

    return (
      <Box key={`preview-${slotKey}-${idx}`} sx={{ p: 1, borderRadius: 1.5, border: `1px solid ${BRAND.line}`, background: 'rgba(255,255,255,0.96)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <WidgetIcon sx={{ fontSize: 16, color: widgetMeta.accent }} />
          <Typography sx={{ fontWeight: 700, color: BRAND.ink }}>{widget.component}</Typography>
        </Box>
        <Typography variant="caption" sx={{ color: BRAND.slate }}>
          {String(props?.title || props?.headline || props?.cta || props?.subtitle || props?.body || 'Configured')}
        </Typography>
      </Box>
    );
  };

  // ✅ WordPress “mShots” screenshot URL (no WP plugin needed)
  // Example: https://s.wordpress.com/mshots/v1/https%3A%2F%2Felitepromomarketing.com?w=1400
  const wpScreenshotUrl = useMemo(() => {
    if (!siteUrl) return "";
    const encoded = encodeURIComponent(siteUrl);
    // width param controls resolution (WordPress will scale)
    return `https://s.wordpress.com/mshots/v1/${encoded}?w=1400`;
  }, [siteUrl]);

  const statusLabel = useMemo(() => {
    if (!settings.promoShowcaseEnabled) return "Disabled";
    return connectedLabel;
  }, [connectedLabel, settings.promoShowcaseEnabled]);

  const statusChipSx = useMemo(() => {
    if (!settings.promoShowcaseEnabled) return styles.statusChipDisabled;
    return settings.promoShowcaseConnected
      ? styles.statusChipConnected
      : styles.statusChipNotConnected;
  }, [settings.promoShowcaseConnected, settings.promoShowcaseEnabled]);

  const handlePreviewMode = (_e, next) => {
    // ToggleButtonGroup passes null when you click the selected option — ignore
    if (!next) return;
    setPreviewMode(next);
  };

  const loadBlogPosts = useCallback(async () => {
    if (!instanceUniqueId) return;
    setBlogLoading(true);
    try {
      const res = await axios.get(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/blog-posts`, headers);
      const posts = Array.isArray(res?.data?.posts) ? res.data.posts : [];
      setBlogPosts(posts);
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to load blog posts.';
      toast.error(message);
    } finally {
      setBlogLoading(false);
    }
  }, [SERVER, headers, instanceUniqueId]);

  useEffect(() => {
    if (activeTab !== 7 && builderFocusMode) setBuilderFocusMode(false);
  }, [activeTab, builderFocusMode]);

  useEffect(() => {
    if (activeTab === 4) loadBlogPosts();
  }, [activeTab, loadBlogPosts]);

  const loadBlogDrafts = useCallback(async () => {
    if (!instanceUniqueId) return;
    try {
      const res = await axios.get(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/blog-drafts`, headers);
      const drafts = Array.isArray(res?.data?.drafts) ? res.data.drafts : [];
      setBlogDrafts(drafts);
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to load blog drafts.';
      toast.error(message);
    }
  }, [SERVER, headers, instanceUniqueId]);

  useEffect(() => {
    if (activeTab === 4) loadBlogDrafts();
  }, [activeTab, loadBlogDrafts]);

  const handleCreateDraft = async () => {
    if (!String(blogForm.title || '').trim() && !String(blogForm.content || '').trim()) {
      toast.error('Add at least a title or content to save a draft.');
      return;
    }
    try {
      const res = await axios.post(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/blog-drafts`, blogForm, headers);
      const draft = res?.data?.draft;
      toast.success('Draft saved in Elite Core.');
      await loadBlogDrafts();
      if (draft?.id) setActiveDraftId(draft.id);
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to save draft.';
      toast.error(message);
    }
  };

  const handleSaveDraftChanges = async () => {
    if (!activeDraftId) {
      toast.error('Select a draft first.');
      return;
    }
    try {
      await axios.put(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/blog-drafts/${activeDraftId}`, blogForm, headers);
      toast.success('Draft updated.');
      await loadBlogDrafts();
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to update draft.';
      toast.error(message);
    }
  };

  const handleLoadDraft = (draftId) => {
    setActiveDraftId(draftId);
    const draft = blogDrafts.find((d) => String(d?.id) === String(draftId));
    if (!draft) return;
    setBlogForm({
      title: draft.title || '',
      excerpt: draft.excerpt || '',
      content: draft.content || '',
      status: draft.status || 'draft',
    });
  };

  const handlePublishDraft = async () => {
    if (!activeDraftId) {
      toast.error('Select a draft first.');
      return;
    }

    setBlogPublishing(true);
    try {
      await axios.post(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/blog-drafts/${activeDraftId}/publish`, {}, headers);
      toast.success('Draft published to PromoShowcase site.');
      await Promise.all([loadBlogDrafts(), loadBlogPosts()]);
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to publish draft.';
      toast.error(message);
    } finally {
      setBlogPublishing(false);
    }
  };

  const loadSitePages = useCallback(async () => {
    if (!instanceUniqueId) return;
    setSitePagesLoading(true);
    try {
      const res = await axios.get(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/pages`, headers);
      const pages = Array.isArray(res?.data?.pages) ? res.data.pages : [];
      setSitePages(pages);
      setHasLoadedPages(true);
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to load website pages.';
      toast.error(message);
    } finally {
      setSitePagesLoading(false);
    }
  }, [SERVER, headers, instanceUniqueId]);

  useEffect(() => {
    if (activeTab === 6 && !hasLoadedPages) loadSitePages();
  }, [activeTab, hasLoadedPages, loadSitePages]);

  const handleRefreshSitePages = async () => {
    if (!settings.promoShowcaseEnabled) {
      toast.error('Enable PromoShowcase first.');
      return;
    }
    setSitePagesRefreshing(true);
    try {
      const res = await axios.post(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/pages/refresh`, {}, headers);
      const pages = Array.isArray(res?.data?.pages) ? res.data.pages : [];
      setSitePages(pages);
      setHasLoadedPages(true);
      toast.success(`Loaded ${pages.length} pages from the connected PromoShowcase site.`);
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to refresh website pages.';
      toast.error(message);
    } finally {
      setSitePagesRefreshing(false);
    }
  };

  const loadSiteUsers = useCallback(async () => {
    if (!instanceUniqueId) return;
    setSiteUsersLoading(true);
    try {
      const res = await axios.get(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/users`, headers);
      const users = Array.isArray(res?.data?.users) ? res.data.users : [];
      setSiteUsers(users);
      setHasLoadedUsers(true);
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to load website users.';
      toast.error(message);
    } finally {
      setSiteUsersLoading(false);
    }
  }, [SERVER, headers, instanceUniqueId]);

  useEffect(() => {
    if (activeTab === 5 && !hasLoadedUsers) loadSiteUsers();
  }, [activeTab, hasLoadedUsers, loadSiteUsers]);

  const handleRefreshSiteUsers = async () => {
    if (!settings.promoShowcaseEnabled) {
      toast.error('Enable PromoShowcase first.');
      return;
    }
    setSiteUsersRefreshing(true);
    try {
      const res = await axios.post(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/users/refresh`, {}, headers);
      const users = Array.isArray(res?.data?.users) ? res.data.users : [];
      setSiteUsers(users);
      setHasLoadedUsers(true);
      toast.success(`Loaded ${users.length} users from the connected PromoShowcase site.`);
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to refresh website users.';
      toast.error(message);
    } finally {
      setSiteUsersRefreshing(false);
    }
  };

  const handleCreateSiteUser = async () => {
    if (!settings.promoShowcaseEnabled) {
      toast.error('Enable PromoShowcase first.');
      return;
    }
    setCreatingSiteUser(true);
    try {
      await axios.post(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/users/create`, newSiteUser, headers);
      toast.success('User created on PromoShowcase website.');
      setNewSiteUser({ email: '', username: '', displayName: '', role: 'customer', password: '' });
      await handleRefreshSiteUsers();
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to create website user.';
      toast.error(message);
    } finally {
      setCreatingSiteUser(false);
    }
  };

  const handleRequestDesigner = () => {
    setRequestDesignerOpen(true);
  };

  const handleConfirmRequestDesigner = async () => {
    setRequestingDesigner(true);
    try {
      await axios.post(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/request-designer`, {}, headers);
      toast.success("PromoShowcase designer request sent.");
      setRequestDesignerOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to send designer request.");
    } finally {
      setRequestingDesigner(false);
    }
  };

  const handlePublishBlogPost = async () => {
    if (!String(blogForm.title || '').trim() || !String(blogForm.content || '').trim()) {
      toast.error('Title and content are required.');
      return;
    }
    setBlogPublishing(true);
    try {
      await axios.post(`${SERVER}/promoshowcase/instances/${instanceUniqueId}/blog-posts`, blogForm, headers);
      toast.success('Blog post sent to PromoShowcase.');
      setBlogForm((prev) => ({ ...prev, title: '', excerpt: '', content: '' }));
      await loadBlogPosts();
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to publish blog post.';
      toast.error(message);
    } finally {
      setBlogPublishing(false);
    }
  };

  const needsInitialProvisioning =
    settings.promoShowcaseEnabled
    && !settings?.promoShowcaseHosting?.blogId
    && !String(settings.promoShowcaseUrl || "").trim();

  const suggestedShowcaseSlug = String(onboardingSiteSlug || instanceUniqueId || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return (
    <div className={[
      "instance-dashboard",
      showcaseMode ? "ps-showcase-mode-root" : "",
      builderFocusMode ? "ps-site-builder-focus" : "",
    ].filter(Boolean).join(" ")}>
      <ShowcaseModeChrome
        active={showcaseMode && !builderFocusMode}
        booting={showcaseIntroVisible || loading}
        onExit={() => setShowcaseMode(false)}
      />
      {!showcaseMode ? <InstanceLeftSidebar /> : null}
      <div className={showcaseMode ? "rightSide promoshowcase-scope ps-showcase-mode-content" : "rightSide promoshowcase-scope"}>
        <div className="options-container fade-in-effect-usability">
          <div className="options-inner-container">
            {!showcaseMode ? (
              <PageHeaderWithBreadcrumbs
                title="Showcase"
                breadcrumb="Showcase"
                includeButton={false}
              />
            ) : null}

            <style>
              {`
                @keyframes gridFloat {
                  0% { transform: translateY(0px); opacity: .30; }
                  50% { transform: translateY(10px); opacity: .38; }
                  100% { transform: translateY(0px); opacity: .30; }
                }
              `}
            </style>

            <Box sx={styles.pageWrap}>
              {loading ? (
                <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : !settings.promoShowcaseEnabled ? (
                <Box sx={styles.heroPanel}>
                  <Box sx={styles.animatedGrid} />
                  <Box sx={styles.heroHeaderRow}>
                    <Box>
                      <Typography variant="h5" sx={styles.heroTitle}>
                        PromoShowcase Command Center
                      </Typography>
                      <Typography sx={styles.heroSub}>
                        PromoShowcase isn’t enabled for this instance yet — enable it in Company Settings to
                        unlock the frontend control layer.
                      </Typography>
                    </Box>
                    <Chip size="small" label="Disabled" sx={styles.statusChipDisabled} />
                  </Box>

                  <Box sx={styles.heroBody}>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="contained"
                        sx={styles.primaryButton}
                        onClick={() =>
                          history.push(
                            `/instance/${instanceUniqueId}/settings/global-settings/promoshowcase`
                          )
                        }
                      >
                        Go to Company Settings
                      </Button>
                      <Button variant="outlined" sx={styles.outlineButton} onClick={loadSettings}>
                        Refresh
                      </Button>
                    </Box>

                    <Typography variant="body2" sx={{ color: BRAND.slate }}>
                      Tip: Once enabled, you’ll see live connection status, a website preview, and the add-on
                      roadmap.
                    </Typography>


                  </Box>
                </Box>
              ) : needsInitialProvisioning ? (
                <Box
                  sx={{
                    ...styles.heroPanel,
                    borderRadius: 0,
                    minHeight: "100vh",
                    display: "grid",
                    alignItems: "center",
                    borderLeft: 0,
                    borderRight: 0,
                    borderBottom: 0,
                    boxShadow: "none",
                  }}
                >
                  <Box sx={styles.animatedGrid} />
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 1,
                      width: "100%",
                      maxWidth: 860,
                      mx: "auto",
                      px: { xs: 2, md: 4 },
                      py: { xs: 4, md: 7 },
                      display: "grid",
                      gap: 2.5,
                    }}
                  >
                    <Box sx={{ display: "grid", gap: 1 }}>
                      <Chip
                        size="small"
                        label="Website setup"
                        sx={{ justifySelf: "start", fontWeight: 800 }}
                      />
                      <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.ink, letterSpacing: "-0.03em" }}>
                        Create your Showcase
                      </Typography>
                      <Typography sx={{ color: BRAND.slate, maxWidth: 680, fontSize: 16, lineHeight: 1.65 }}>
                        Showcase is enabled, but this Elite Core instance has not claimed a website yet.
                        Create the managed WordPress site first; once it is connected, the Design, Products,
                        Pages, Store Settings, Orders, and Site Builder tools will unlock here.
                      </Typography>
                    </Box>

                    <Card sx={{ ...styles.glassCard, position: "relative", zIndex: 1 }}>
                      <CardContent sx={{ display: "grid", gap: 2.25, p: { xs: 2.25, md: 3.25 } }}>
                        <Box sx={{ display: "grid", gap: 0.5 }}>
                          <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>
                            Claim your showcase.elitecore.ca address
                          </Typography>
                          <Typography variant="body2" sx={{ color: BRAND.slate }}>
                            Elite Core will create a new WordPress Multisite website, assign its permanent site ID,
                            generate its private connection credentials, and connect it automatically.
                          </Typography>
                        </Box>

                        <TextField
                          label="Website name"
                          value={onboardingSiteName}
                          onChange={(event) => setOnboardingSiteName(event.target.value)}
                          placeholder="Elite Promotional Marketing"
                          fullWidth
                          autoFocus
                        />

                        <TextField
                          label="Website address"
                          value={onboardingSiteSlug}
                          onChange={(event) =>
                            setOnboardingSiteSlug(
                              event.target.value
                                .toLowerCase()
                                .replace(/[^a-z0-9-]+/g, "-")
                                .replace(/^-+|-+$/g, "")
                            )
                          }
                          placeholder={instanceUniqueId || "your-company"}
                          helperText={`Your starting address will be https://showcase.elitecore.ca/${suggestedShowcaseSlug || "your-company"}/. If it is already claimed, Elite Core will allocate the next available suffix.`}
                          fullWidth
                        />

                        {provisioningShowcase ? (
                          <Box sx={{ display: "grid", gap: 1 }}>
                            <LinearProgress />
                            <Typography variant="caption" sx={{ color: BRAND.slate }}>
                              Creating WordPress site → assigning site ID → connecting Elite Core → checking runtime…
                            </Typography>
                          </Box>
                        ) : null}

                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                          <Button
                            variant="contained"
                            sx={styles.primaryButton}
                            disabled={provisioningShowcase || !String(onboardingSiteName || "").trim()}
                            onClick={() =>
                              handleProvisionShowcase({
                                title: String(onboardingSiteName || "").trim(),
                                slug: suggestedShowcaseSlug,
                              })
                            }
                          >
                            {provisioningShowcase ? "Creating your Showcase..." : "Create my Showcase"}
                          </Button>
                          <Button
                            variant="outlined"
                            sx={styles.outlineButton}
                            disabled={provisioningShowcase}
                            onClick={() => history.push(`/instance/${instanceUniqueId}/connect/feed-and-activity`)}
                          >
                            Cancel
                          </Button>
                          <Typography variant="caption" sx={{ color: BRAND.slate }}>
                            Custom domains can be connected later. This step only claims your managed Showcase site.
                          </Typography>
                        </Box>

                        {settings.promoShowcaseLastError ? (
                          <Typography variant="body2" sx={{ color: BRAND.red, fontWeight: 700 }}>
                            {settings.promoShowcaseLastError}
                          </Typography>
                        ) : null}
                      </CardContent>
                    </Card>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    ...styles.heroPanel,
                    borderRadius: 0,
                    ...(builderFocusMode ? {
                      border: 0,
                      boxShadow: 'none',
                      background: '#f6f7fb',
                      minHeight: '100dvh',
                      overflow: 'hidden',
                    } : {}),
                  }}
                >
                  {!builderFocusMode ? <Box sx={styles.animatedGrid} /> : null}

                  {/* Header */}
                  {!builderFocusMode ? (
                  <Box sx={styles.heroHeaderRow}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        position: "absolute",
                        top: "30px",
                        right: "30px",
                        borderRadius: "0px"
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        sx={styles.primaryButton}
                        startIcon={<AutoAwesomeRoundedIcon />}
                        onClick={() => {
                          loadTemplates();
                          loadPromoShowcaseProductSummary();
                          setLaunchVerification(null);
                          setLaunchWizardOpen(true);
                        }}
                      >
                        Launch Showcase
                      </Button>
                      <Chip size="small" label={statusLabel} sx={statusChipSx} />
                      <Tooltip title="Refresh">
                        <IconButton size="small" onClick={loadSettings} sx={styles.iconButton}>
                          <RefreshRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {activeTab === 7 ? (
                        <Tooltip title="Focus Site Builder">
                          <IconButton
                            size="small"
                            onClick={() => setBuilderFocusMode(true)}
                            sx={styles.iconButton}
                            aria-label="Focus Site Builder"
                          >
                            <FullscreenRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>

                    {/* Tabs */}
                    <Tabs
                      value={activeTab}
                      onChange={(_event, next) => {
                        setActiveTab(next);
                        if (next === 7) setBuilderFocusMode(true);
                      }}
                      variant="scrollable"
                      sx={styles.tabBar}

                    >
                      <Tab label="Design" />
                      <Tab label="Products" />
                      <Tab label="Add-ons" />
                      <Tab label="Connection" />
                      <Tab label="Blog Automation" />
                      <Tab label="Users" />
                      <Tab label="Pages" />
                      <Tab label="Site Builder" />
                      <Tab label="Store Settings" />
                      <Tab label="Orders" />
                      <Tab label="Forms" />
                    </Tabs>
                  </Box>
                  ) : null}

                  <Box
                    className={builderFocusMode ? "ps-site-builder-focus__body" : ""}
                    sx={{
                      ...styles.heroBody,
                      ...(builderFocusMode ? {
                        p: 0,
                        px: 0,
                        py: 0,
                        pt: 0,
                        pr: 0,
                        pb: 0,
                        pl: 0,
                        gap: 0,
                        minHeight: '100dvh',
                      } : {}),
                    }}
                  >
                    {/* DESIGN TAB */}
                    {activeTab === 0 && (
                      <Card sx={styles.glassCard}>
                        <CardContent
                          sx={{
                            display: "grid",
                            gap: 2,
                            gridTemplateColumns: { xs: "1fr", lg: "2fr 1.2fr" },
                            alignItems: "start",
                            border: 0,
                            borderRadius: 0
                          }}
                        >
                          {/* Website Preview */}
                          <Box sx={{ display: "grid", gap: 1.25 }}>
                            <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>
                              Website Preview
                            </Typography>

                            <Box sx={styles.previewFrameWrap}>
                              {/* Top bar */}
                              <Box sx={styles.previewTopBar}>
                                <Box sx={styles.dots}>
                                  <Box sx={styles.dot(BRAND.red)} />
                                  <Box sx={styles.dot(BRAND.orange)} />
                                  <Box sx={styles.dot("rgba(34,197,94,0.95)")} />
                                </Box>

                                <Box
                                  sx={{
                                    minWidth: 0,
                                    flex: 1,
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                  }}
                                >
                                  <LinkRoundedIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                                  <Typography sx={styles.tinyUrl}>
                                    {siteUrl || "No site URL configured"}
                                  </Typography>
                                </Box>

                                {siteUrl ? (
                                  <Tooltip title="Open site">
                                    <IconButton
                                      size="small"
                                      onClick={() => window.open(siteUrl, "_blank")}
                                      sx={styles.iconButton}
                                    >
                                      <OpenInNewRoundedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Box />
                                )}
                              </Box>

                              {/* ✅ NEW: Preview Mode Toggle */}
                              {/*<Box sx={styles.previewModeRow}>
                                <Typography variant="body2" sx={{ color: BRAND.slate, fontWeight: 800 }}>
                                  Preview Mode
                                </Typography>

                                <ToggleButtonGroup
                                  exclusive
                                  value={previewMode}
                                  onChange={handlePreviewMode}
                                  size="small"
                                  sx={styles.toggleGroup}
                                >
                                  <ToggleButton value="screenshot" disabled={!siteUrl}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                      <ImageRoundedIcon sx={{ fontSize: 18 }} />
                                      Screenshot
                                    </Box>
                                  </ToggleButton>
                                  <ToggleButton value="live" disabled={!siteUrl}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                      <LanguageRoundedIcon sx={{ fontSize: 18 }} />
                                      Live
                                    </Box>
                                  </ToggleButton>
                                </ToggleButtonGroup>
                                </Box>*/}

                              {/* Body */}
                              {siteUrl ? (
                                previewMode === "live" ? (
                                  <Box sx={{ height: { xs: 360, md: 420 }, background: "#fff" }}>
                                    {wpScreenshotUrl ? (
                                      <img
                                        src={wpScreenshotUrl}
                                        alt="WordPress screenshot preview"
                                        style={styles.screenshotImg}
                                        onError={(e) => {
                                          // fallback if mshots fails (rare)
                                          e.currentTarget.style.display = "none";
                                        }}
                                      />
                                    ) : null}

                                    {!wpScreenshotUrl && (
                                      <Box sx={styles.screenshotPlaceholder}>
                                        <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>
                                          Screenshot unavailable
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                ) : (
                                  <Box sx={{ height: { xs: 500, md: 700 }, background: "#fff" }}>
                                    <iframe
                                      title="PromoShowcase live preview"
                                      src={siteUrl}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        border: 0,
                                        display: "block",
                                      }}
                                      sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                                      referrerPolicy="no-referrer"
                                    />
                                  </Box>
                                )
                              ) : (
                                <Box sx={styles.screenshotPlaceholder}>
                                  <Box sx={{ textAlign: "center", maxWidth: 520, px: 2 }}>
                                    <Typography sx={{ fontWeight: 900, mb: 0.5, color: BRAND.ink }}>
                                      No URL connected yet
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.slate }}>
                                      Add your site URL in settings to enable screenshot + live preview.
                                    </Typography>
                                  </Box>
                                </Box>
                              )}

                              {/* Note about iframe blocking (kept subtle) */}
                              {siteUrl && previewMode === "live" && (
                                <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${BRAND.line}` }}>
                                  <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                    If the live view is blocked, your site may restrict iframes. Screenshot mode will
                                    still work.
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>

                          {/* Right info */}
                          <Box sx={{ display: "grid", gap: 1.2, borderRadius: "0px"}}>
                            <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>
                              Website Control
                            </Typography>

                            <Box
                              sx={{
                                borderRadius: 0,
                                border: `1px solid ${BRAND.line}`,
                                background: "rgba(255,255,255,0.92)",
                                p: 2,
                                display: "grid",
                                gap: 1.25,
                                boxShadow: "0 10px 24px rgba(17,24,39,0.06)",
                              }}
                            >
                              <Box>
                                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                  Active template
                                </Typography>
                                <Typography sx={{ color: BRAND.ink, fontWeight: 900 }}>
                                  {activeTemplateLabel && activeTemplateLabel !== 'No template applied'
                                    ? activeTemplateLabel
                                    : <><span>Custom </span><i>(Created by Elite team)</i></>}
                                </Typography>
                              </Box>

                              <Divider sx={{ borderColor: BRAND.line }} />

                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                  Connection status
                                </Typography>
                                <Chip size="small" label={connectedLabel} sx={statusChipSx} />
                              </Box>

                              <Box>
                                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                  Site URL
                                </Typography>
                                <Typography sx={{ color: BRAND.slate }}>
                                  {siteUrl || "Not set"}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                  Admin URL
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                  <Typography sx={{ color: BRAND.slate }}>
                                    {adminUrl || "Not set"}
                                  </Typography>
                                  {adminUrl && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      sx={styles.outlineButton}
                                      onClick={() => window.open(adminUrl, "_blank")}
                                    >
                                      Open
                                    </Button>
                                  )}
                                </Box>
                              </Box>

                              <Box sx={{ display: "grid", gap: 0.5 }}>
                                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                  Last template sync
                                </Typography>
                                <Typography sx={{ color: BRAND.ink, fontWeight: 800, textTransform: "capitalize" }}>
                                  {settings?.templateSyncState?.status || "Unknown"}
                                </Typography>
                                <Typography variant="body2" sx={{ color: BRAND.slate }}>
                                  {settings?.templateSyncState?.message || "No template sync message available."}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                  Last updated: {templateSyncUpdatedAtLabel}
                                </Typography>
                              </Box>

                              <Box sx={{ display: "grid", gap: 0.5 }}>
                                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                  Product sync
                                </Typography>
                                <Typography sx={{ color: BRAND.ink, fontWeight: 800 }}>
                                  {storeProductSummary.isSynced == null
                                    ? "Unknown"
                                    : storeProductSummary.isSynced
                                      ? `Synced (${storeProductSummary.importedCount} product${storeProductSummary.importedCount === 1 ? '' : 's'})`
                                      : `Out of sync (${storeProductSummary.importedCount} product${storeProductSummary.importedCount === 1 ? '' : 's'})`}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                  Last synced: {productSyncUpdatedAtLabel}
                                </Typography>
                              </Box>

                              <Box sx={{ display: "grid", gap: 0.5 }}>
                                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                  Current error
                                </Typography>
                                <Typography sx={{ color: settings.promoShowcaseLastError ? BRAND.red : BRAND.slate, fontWeight: settings.promoShowcaseLastError ? 700 : 500 }}>
                                  {settings.promoShowcaseLastError || "No current PromoShowcase error"}
                                </Typography>
                              </Box>

                              <Divider sx={{ borderColor: BRAND.line }} />

                              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                <Button
                                  variant="contained"
                                  sx={styles.primaryButton}
                                  onClick={() =>
                                    history.push(
                                      `/instance/${instanceUniqueId}/settings/global-settings/promoshowcase`
                                    )
                                  }
                                >
                                  Open Settings
                                </Button>

                                <Button
                                  variant="outlined"
                                  startIcon={<OpenInNewRoundedIcon />}
                                  sx={styles.outlineButton}
                                  onClick={() => adminUrl && window.open(adminUrl, "_blank")}
                                  disabled={!adminUrl}
                                >
                                  Open WordPress Admin
                                </Button>

                                <Button
                                  variant="outlined"
                                  sx={styles.outlineButton}
                                  onClick={handleRequestDesigner}
                                  disabled={!settings.promoShowcaseEnabled || requestingDesigner}
                                >
                                  {requestingDesigner ? "Requesting..." : settings.requestDesignerLabel || "Request designer"}
                                </Button>

                                <Button
                                  variant="outlined"
                                  startIcon={<RefreshRoundedIcon />}
                                  sx={styles.outlineButton}
                                  onClick={loadSettings}
                                >
                                  Refresh
                                </Button>

                                {selectedTemplateId ? (
                                  <Button
                                    variant="outlined"
                                    sx={styles.outlineButton}
                                    onClick={handleResyncNow}
                                    disabled={!settings.promoShowcaseEnabled || templatesApplying}
                                  >
                                    {templatesApplying ? "Re-applying..." : "Re-publish Design"}
                                  </Button>
                                ) : null}
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    )}

                    {/* PRODUCTS TAB */}
                    {activeTab === 1 && (
                      <Card sx={styles.glassCard}>
                        <CardContent sx={{ display: "grid", gap: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                            <Box>
                              <Typography component="h2" variant="h6" sx={{ fontWeight: 900, color: BRAND.ink }}>
                                Website products
                              </Typography>
                              <Typography variant="body2" sx={{ color: BRAND.slate }}>
                                Manage catalogue and custom products for your website.
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                              <Button
                                variant="outlined"
                                sx={styles.outlineButton}
                                startIcon={<Inventory2RoundedIcon />}
                                onClick={() => setShowPromoWorldPicker(true)}
                                disabled={!settings.promoShowcaseEnabled}
                              >
                                Catalogues
                              </Button>
                              <Button variant="contained" sx={styles.primaryButton}
                                onClick={handleOpenMyProducts} disabled={showcaseActions.busy}>
                                Add product
                              </Button>
                              <Button
                                variant="outlined"
                                sx={styles.outlineButton}
                                startIcon={<RefreshRoundedIcon />}
                                disabled={!settings.promoShowcaseEnabled || syncingProducts || productSync.refreshing || productSync.cancelPending}
                                onClick={handleSyncProductsToShowcase}
                              >
                                {productSync.actionLabel}
                              </Button>
                            </Box>
                          </Box>

                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Chip label="PromoWorld + Custom" color="primary" />
                            <Chip label={`Catalogues: ${storeProductSummary.catalogueCount || 0}`} variant="outlined" />
                            <Chip label={`Direct products: ${Math.max(0, (storeProductSummary.selectionCount || 0) - (storeProductSummary.catalogueCount || 0))}`} variant="outlined" />
                            <Chip
                              label={`Website products: ${storeProductSummary.importedCount}`}
                              color="success"
                              variant={storeProductSummary.importedCount > 0 ? "filled" : "outlined"}
                            />
                            <Chip
                              size="small"
                              label={storeProductSummary.isSynced == null ? "WordPress sync unknown" : storeProductSummary.isSynced ? "WordPress synced" : "WordPress out of sync"}
                              sx={storeProductSummary.isSynced ? styles.statusChipConnected : styles.statusChipNotConnected}
                            />
                            {productBrowse.loading && <CircularProgress size={18} />}
                          </Box>

                          <ShowcaseProductSyncStatus sync={productSync} totalProducts={storeProductSummary.importedCount} instanceUniqueId={instanceUniqueId} />

                          {!productHelpHidden && (
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                              <Chip
                                size="small"
                                color={storeProductSummary.importedCount > 0 ? "success" : "default"}
                                variant={storeProductSummary.importedCount > 0 ? "filled" : "outlined"}
                                label={`1. Add products to cart ${storeProductSummary.importedCount > 0 ? "✓" : ""}`}
                              />
                              <Chip
                                size="small"
                                color={storeProductSummary.importedCount > 0 ? "info" : "default"}
                                variant={storeProductSummary.importedCount > 0 ? "filled" : "outlined"}
                                label={`2. Save products in Showcase ${storeProductSummary.importedCount > 0 ? "✓" : ""}`}
                              />
                              <Chip
                                size="small"
                                color={storeProductSummary.isSynced ? "success" : "default"}
                                variant={storeProductSummary.isSynced ? "filled" : "outlined"}
                                label={`3. Push to WordPress ${storeProductSummary.isSynced ? "✓" : ""}`}
                              />
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  try { localStorage.setItem(productHelpStorageKey, '1'); } catch (_error) {}
                                  setProductHelpHidden(true);
                                }}
                              >
                                Hide help
                              </Button>
                            </Box>
                          )}

                          <PresentationTabs
                            showcaseActions={showcaseActions}
                            showcaseProductViewScope={instanceUniqueId}
                            renderShowcaseToolbar={({ view, onViewChange }) => <ShowcaseProductPagination
                              browse={productBrowse} view={view} onViewChange={onViewChange} />}
                            showcaseFooter={<ShowcaseProductPagination browse={productBrowse} controls={false} />}
                            fetchOrder={loadPromoShowcaseProductSummary}
                            order={showcaseProductOrder}
                            selectedProducts={[]}
                            handleShowProductsPopup={handleOpenMyProducts}
                            isPromoStore={true}
                          />
                        </CardContent>
                      </Card>
                    )}

                    {/* ADD-ONS TAB */}
                    {activeTab === 2 && (
                      <Card sx={styles.glassCard}>
                        <CardContent sx={{ display: "grid", gap: 2 }}>
                          <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>
                            Distributor Add-Ons
                          </Typography>
                          <Typography variant="body2" sx={{ color: BRAND.slate }}>
                            Optional modules extend the base Showcase catalogue. Product metadata such as categories, themes, brands, suppliers, colors, and sizes remains available to every site.
                          </Typography>

                          <Box sx={{ display: "grid", gap: 1 }}>
                            <Typography variant="body2" sx={{ color: BRAND.slate }}>
                              Smart Merchandising is the first live add-on contract. Other modules remain visible as roadmap items.
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                              <Button
                                variant="contained"
                                sx={styles.primaryButton}
                                onClick={saveAddonsConfig}
                                disabled={
                                  savingAddons ||
                                  !settings.promoShowcaseEnabled
                                }
                              >
                                {savingAddons ? "Saving add-ons..." : "Save Add-ons"}
                              </Button>
                              <Button variant="outlined" sx={styles.outlineButton} onClick={loadAddonsConfig}>
                                Refresh Add-ons
                              </Button>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                              gap: 2,
                            }}
                          >
                            {DISTRIBUTOR_ADDONS.map((addon) => {
                              const addonKey = addon.key || addon.title;
                              const enabled = !!addonsConfig?.enabled?.[addonKey];
                              const addonAvailable = addon.available === true;
                              return (
                                <Box key={addon.title} sx={styles.moduleTile}>
                                  <PromoShowcaseAddonPreview title={addon.title} />
                                  <Box sx={styles.moduleHeader}>
                                    <Typography sx={styles.moduleTitle}>{addon.title}</Typography>
                                    <span style={styles.comingSoonPill}>
                                      {addonAvailable ? (enabled ? "Enabled" : "Available") : "Coming soon"}
                                    </span>
                                  </Box>
                                  <Typography sx={styles.moduleDesc}>{addon.description}</Typography>
                                  {addonKey === 'smart-merchandising' ? (
                                    <Box sx={{ mt: 1, display: 'flex', gap: .5, flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
                                      <Chip size="small" label={`${settings?.productMerchandising?.categories?.length || 0} categories`} />
                                      <Chip size="small" label={`${settings?.productMerchandising?.themes?.length || 0} themes`} />
                                      <Chip size="small" label={`${settings?.productMerchandising?.brands?.length || 0} brands`} />
                                    </Box>
                                  ) : null}
                                  <Box sx={{ mt: 1 }}>
                                    <Tooltip
                                      title={!addonAvailable ? "Coming soon — this feature is not available yet." : ""}
                                      disableHoverListener={addonAvailable}
                                      disableFocusListener={addonAvailable}
                                      disableTouchListener={addonAvailable}
                                    >
                                      <span>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          sx={styles.outlineButton}
                                          disabled={!addonAvailable}
                                          onClick={() => {
                                            if (!addonAvailable) return;
                                            setAddonsConfig((prev) => ({
                                              ...prev,
                                              enabled: {
                                                ...(prev?.enabled || {}),
                                                [addonKey]: !enabled,
                                              },
                                            }));
                                          }}
                                        >
                                          {!addonAvailable ? "Coming Soon" : enabled ? "Disable" : "Enable"}
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </CardContent>
                      </Card>
                    )}


                    {/* USERS TAB */}
                    {activeTab === 5 && (
                      <Card sx={styles.glassCard}>
                        <CardContent sx={{ display: "grid", gap: 1.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                            <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>
                              Website Users (Admin + Customer)
                            </Typography>
                            <Chip size="small" label={`Users: ${filteredSiteUsers.length}`} sx={styles.statusChipConnected} />
                          </Box>

                          <Typography variant="body2" sx={{ color: BRAND.slate }}>
                            Create and manage WordPress users for the connected PromoShowcase site.
                          </Typography>

                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              variant="contained"
                              sx={styles.primaryButton}
                              startIcon={<RefreshRoundedIcon />}
                              onClick={handleRefreshSiteUsers}
                              disabled={!settings.promoShowcaseEnabled || siteUsersRefreshing}
                            >
                              {siteUsersRefreshing ? "Refreshing..." : "Refresh from WordPress"}
                            </Button>
                            <Button variant="outlined" sx={styles.outlineButton} onClick={loadSiteUsers}>
                              Load Local Snapshot
                            </Button>
                          </Box>

                          <TextField
                            size="small"
                            label="Search users"
                            placeholder="Search by name, email, or role"
                            value={siteUsersQuery}
                            onChange={(e) => setSiteUsersQuery(e.target.value)}
                          />

                          <Divider sx={{ borderColor: BRAND.line }} />

                          <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>Create User</Typography>
                          <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))' } }}>
                            <TextField size="small" label="Email" value={newSiteUser.email} onChange={(e) => setNewSiteUser((p) => ({ ...p, email: e.target.value }))} />
                            <TextField size="small" label="Username" value={newSiteUser.username} onChange={(e) => setNewSiteUser((p) => ({ ...p, username: e.target.value }))} />
                            <TextField size="small" label="Display Name" value={newSiteUser.displayName} onChange={(e) => setNewSiteUser((p) => ({ ...p, displayName: e.target.value }))} />
                            <TextField size="small" label="Role" value={newSiteUser.role} onChange={(e) => setNewSiteUser((p) => ({ ...p, role: e.target.value }))} helperText="customer or administrator" />
                            <TextField size="small" label="Password" type="password" value={newSiteUser.password} onChange={(e) => setNewSiteUser((p) => ({ ...p, password: e.target.value }))} />
                          </Box>
                          <Box>
                            <Button variant="contained" sx={styles.primaryButton} onClick={handleCreateSiteUser} disabled={creatingSiteUser || !settings.promoShowcaseEnabled}>
                              {creatingSiteUser ? 'Creating...' : 'Create User on PromoShowcase'}
                            </Button>
                          </Box>

                          <Divider sx={{ borderColor: BRAND.line }} />

                          {siteUsersLoading ? (
                            <CircularProgress size={20} />
                          ) : filteredSiteUsers.length === 0 ? (
                            <Typography variant="body2" sx={{ color: BRAND.slate }}>
                              No users loaded yet. Use “Refresh from WordPress” to fetch users.
                            </Typography>
                          ) : (
                            <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' } }}>
                              {filteredSiteUsers.map((user, idx) => (
                                <Box key={user.wpUserId || user.id || user.email || idx} sx={{ p: 1.25, border: `1px solid ${BRAND.line}`, borderRadius: 2, background: "rgba(255,255,255,0.92)" }}>
                                  <Typography sx={{ fontWeight: 800, color: BRAND.ink }}>{user.displayName || user.userLogin || `User ${idx + 1}`}</Typography>
                                  <Typography variant="caption" sx={{ color: BRAND.slate, display: 'block' }}>
                                    {user.email || user.user_email || 'No email'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: BRAND.slate, display: 'block' }}>
                                    Roles: {(Array.isArray(user.roles) ? user.roles.join(', ') : user.roles || 'No role')}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: BRAND.slate, display: 'block' }}>
                                    Created: {getIsoDateLabel(user.registeredGmt || user.registered_gmt) || 'Unknown'}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    )}


                    {/* PAGES TAB */}
                    {activeTab === 6 && (
                      <Card sx={styles.glassCard}>
                        <CardContent sx={{ display: "grid", gap: 1.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                            <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>
                              WordPress Pages
                            </Typography>
                            <Chip size="small" label={`Pages: ${filteredSitePages.length}`} sx={styles.statusChipConnected} />
                          </Box>

                          <Typography variant="body2" sx={{ color: BRAND.slate }}>
                            View all WordPress pages from the connected PromoShowcase site.
                          </Typography>

                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              variant="contained"
                              sx={styles.primaryButton}
                              startIcon={<RefreshRoundedIcon />}
                              onClick={handleRefreshSitePages}
                              disabled={!settings.promoShowcaseEnabled || sitePagesRefreshing}
                            >
                              {sitePagesRefreshing ? "Refreshing..." : "Refresh from WordPress"}
                            </Button>
                            <Button variant="outlined" sx={styles.outlineButton} onClick={loadSitePages}>
                              Load Local Snapshot
                            </Button>
                          </Box>

                          <TextField
                            size="small"
                            label="Search pages"
                            placeholder="Search by title, slug, or slot"
                            value={sitePagesQuery}
                            onChange={(e) => setSitePagesQuery(e.target.value)}
                          />

                          <Divider sx={{ borderColor: BRAND.line }} />

                          {sitePagesLoading ? (
                            <CircularProgress size={20} />
                          ) : filteredSitePages.length === 0 ? (
                            <Typography variant="body2" sx={{ color: BRAND.slate }}>
                              No pages match this search yet.
                            </Typography>
                          ) : (
                            <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' } }}>
                              {filteredSitePages.map((page, idx) => {
                                const viewUrl = buildPageViewUrl(page);
                                const editUrl = buildPageEditUrl(page);
                                const createdAt = page?.dateGmt || page?.date_gmt;
                                const isNewPage = isRecentDate(createdAt, 5);
                                const createdLabel = getIsoDateLabel(createdAt);
                                return (
                                  <Box key={page.id || page.wpPageId || page.slug || idx} sx={{ position: 'relative', p: 1.25, border: `1px solid ${BRAND.line}`, borderRadius: 2, background: "rgba(255,255,255,0.92)" }}>
                                    {isNewPage ? (
                                      <Chip size="small" label="NEW" sx={{ position: 'absolute', top: 8, right: 8, height: 20, fontWeight: 800, background: '#dc2626', color: '#fff' }} />
                                    ) : null}
                                    <Typography sx={{ fontWeight: 800, color: BRAND.ink, pr: 5 }}>{page.title?.rendered || page.title || `Page ${idx + 1}`}</Typography>
                                    <Typography variant="caption" sx={{ color: BRAND.slate, display: 'block' }}>/{page.slug || "(no-slug)"}</Typography>
                                    <Typography variant="caption" sx={{ color: BRAND.slate, display: 'block' }}>ID: {page?.wpPageId || page?.wp_page_id || page?.id || page?.ID || page?.post_id || 'Unknown'}</Typography>
                                    <Typography variant="caption" sx={{ color: BRAND.slate, display: 'block' }}>Created: {createdLabel || 'Unknown'}</Typography>
                                    {page?.promoshowcase?.slot ? (
                                      <Typography variant="caption" sx={{ color: BRAND.slate, display: 'block' }}>Slot: {page.promoshowcase.slot}</Typography>
                                    ) : null}
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                      <Button size="small" variant="outlined" sx={styles.outlineButton} disabled={!viewUrl} onClick={() => viewUrl && window.open(viewUrl, '_blank')}>
                                        View
                                      </Button>
                                      <Button size="small" variant="outlined" sx={styles.outlineButton} disabled={!editUrl} onClick={() => editUrl && window.open(editUrl, '_blank')}>
                                        Edit in WordPress
                                      </Button>
                                      <Button size="small" variant="contained" sx={styles.primaryButton} onClick={() => openPageBuilder(page)}>
                                        Edit in Site Builder
                                      </Button>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    )}


                    {/* BLOG TAB */}
                    {activeTab === 4 && (
                      <Card sx={styles.glassCard}>
                        <CardContent sx={{ display: "grid", gap: 1.5 }}>
                          <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>
                            Blog Post Automation
                          </Typography>
                          <Typography variant="body2" sx={{ color: BRAND.slate }}>
                            Publish blog posts to PromoShowcase through the `/include/` connector and review previously published posts.
                          </Typography>

                          <Box sx={{ display: 'grid', gap: 1 }}>
                            <TextField size="small" label="Title" value={blogForm.title} onChange={(e) => setBlogForm((p) => ({ ...p, title: e.target.value }))} />
                            <TextField size="small" label="Excerpt" value={blogForm.excerpt} onChange={(e) => setBlogForm((p) => ({ ...p, excerpt: e.target.value }))} />
                            <TextField size="small" label="Content" multiline minRows={5} value={blogForm.content} onChange={(e) => setBlogForm((p) => ({ ...p, content: e.target.value }))} />
                            <Box sx={{ p: 1.5, border: `1px solid ${BRAND.line}`, borderRadius: 2, background: 'rgba(255,255,255,0.92)' }}>
                            <Typography sx={{ fontWeight: 800, color: BRAND.ink, mb: 0.75 }}>Live Preview ({previewSlot})</Typography>
                            <Box sx={{ display: 'grid', gap: 0.75 }}>
                              {(layoutBuilder?.slots?.[previewSlot] || []).length === 0 ? (
                                <Typography variant="caption" sx={{ color: BRAND.slate }}>No widgets in this slot yet.</Typography>
                              ) : (layoutBuilder?.slots?.[previewSlot] || []).map((widget, idx) => renderWidgetPreviewCard(widget, idx, previewSlot))}
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Button variant="contained" sx={styles.primaryButton} onClick={handleCreateDraft}>
                                Save Draft in Elite Core
                              </Button>
                              <Button variant="outlined" sx={styles.outlineButton} onClick={handleSaveDraftChanges} disabled={!activeDraftId}>
                                Update Selected Draft
                              </Button>
                              <Button variant="contained" sx={styles.primaryButton} onClick={handlePublishDraft} disabled={blogPublishing || !activeDraftId}>
                                {blogPublishing ? 'Publishing...' : 'Publish Selected Draft'}
                              </Button>
                              <Button variant="outlined" sx={styles.outlineButton} onClick={handlePublishBlogPost} disabled={blogPublishing}>
                                Quick Publish Current Form
                              </Button>
                              <Button variant="outlined" sx={styles.outlineButton} onClick={() => Promise.all([loadBlogPosts(), loadBlogDrafts()])}>
                                Refresh Posts + Drafts
                              </Button>
                            </Box>
                          </Box>

                          <Divider sx={{ borderColor: BRAND.line }} />
                          <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>Local Drafts (Elite Core)</Typography>
                          <Box sx={{ display: 'grid', gap: 1 }}>
                            {blogDrafts.length === 0 ? (
                              <Typography variant="body2" sx={{ color: BRAND.slate }}>No local drafts yet.</Typography>
                            ) : blogDrafts.slice(0, 20).map((draft) => (
                              <Box key={draft.id} sx={{ p: 1.25, border: `1px solid ${BRAND.line}`, borderRadius: 2, background: activeDraftId === draft.id ? 'rgba(255,106,61,0.12)' : 'rgba(255,255,255,0.92)' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <Typography sx={{ fontWeight: 800, color: BRAND.ink }}>{draft.title || 'Untitled draft'}</Typography>
                                  <Button size="small" variant="outlined" sx={styles.outlineButton} onClick={() => handleLoadDraft(draft.id)}>
                                    Load
                                  </Button>
                                </Box>
                                <Typography variant="caption" sx={{ color: BRAND.slate }}>
                                  Updated: {draft.updatedAt ? new Date(draft.updatedAt).toLocaleString() : 'Unknown'}
                                  {draft.publishedAt ? ` • Published: ${new Date(draft.publishedAt).toLocaleString()}` : ''}
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                          <Divider sx={{ borderColor: BRAND.line }} />

                          <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>Previous Website Posts</Typography>
                          {blogLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Box sx={{ display: 'grid', gap: 1 }}>
                              {blogPosts.length === 0 ? (
                                <Typography variant="body2" sx={{ color: BRAND.slate }}>No posts returned yet.</Typography>
                              ) : blogPosts.slice(0, 20).map((post, idx) => (
                                <Box key={post.id || post.post_id || idx} sx={{ p: 1.25, border: `1px solid ${BRAND.line}`, borderRadius: 2, background: 'rgba(255,255,255,0.92)' }}>
                                  <Typography sx={{ fontWeight: 800, color: BRAND.ink }}>{post.title?.rendered || post.title || `Post ${idx + 1}`}</Typography>
                                  <Typography variant="caption" sx={{ color: BRAND.slate }}>
                                    {(post.date || post.post_date || '').toString() || 'No date'}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* CONNECTION TAB */}
                    {activeTab === 3 && (
                      <Card sx={styles.glassCard}>
                        <CardContent sx={{ display: "grid", gap: 1.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>
                              Connection Diagnostics
                            </Typography>
                            <Chip size="small" label={connectedLabel} sx={statusChipSx} />
                          </Box>

                          <Box
                            sx={{
                              display: "grid",
                              gap: 0.75,
                              borderRadius: 4,
                              border: `1px solid ${BRAND.line}`,
                              background: "rgba(255,255,255,0.92)",
                              p: 2,
                              boxShadow: "0 10px 24px rgba(17,24,39,0.06)",
                            }}
                          >
                            <Typography variant="body2" sx={{ color: BRAND.ink }}>
                              <span style={{ color: "#6B7280" }}>Public URL:</span>{" "}
                              <span style={{ color: BRAND.slate }}>{siteUrl || "Not set"}</span>
                            </Typography>
                            <Typography variant="body2" sx={{ color: BRAND.ink }}>
                              <span style={{ color: "#6B7280" }}>Core connection URL:</span>{" "}
                              <span style={{ color: BRAND.slate }}>{connectionUrl || "Not set"}</span>
                            </Typography>
                            <Typography variant="body2" sx={{ color: BRAND.ink }}>
                              <span style={{ color: "#6B7280" }}>Last connected:</span>{" "}
                              <span style={{ color: BRAND.slate }}>{connectedAtLabel}</span>
                            </Typography>

                            {settings.promoShowcaseLastError && (
                              <Typography variant="body2" sx={{ color: BRAND.red, fontWeight: 700 }}>
                                {settings.promoShowcaseLastError}
                              </Typography>
                            )}
                          </Box>

                          <Divider />
                          <Box sx={{ display: "grid", gap: 1.1 }}>
                            <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>Custom Domain</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.slate }}>
                              Optional after launch. The Showcase keeps its network identity while the public site can use a customer-owned domain.
                            </Typography>
                            <TextField
                              size="small"
                              label="Custom domain"
                              placeholder="shop.customer.com"
                              value={customDomainDraft}
                              onChange={(event) => setCustomDomainDraft(event.target.value)}
                              disabled={!settings?.promoShowcaseHosting?.blogId}
                              helperText={
                                settings?.promoShowcaseHosting?.dnsTarget
                                  ? `Point this domain to ${settings.promoShowcaseHosting.dnsTarget}, then verify it here.`
                                  : "Save the domain after PROMOSHOWCASE_DNS_TARGET is configured on the Elite Core server."
                              }
                            />
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                              <Button
                                variant="outlined"
                                sx={styles.outlineButton}
                                onClick={handleSaveCustomDomain}
                                disabled={!settings?.promoShowcaseHosting?.blogId || customDomainSaving}
                              >
                                {customDomainSaving ? "Saving..." : "Save Domain"}
                              </Button>
                              <Button
                                variant="contained"
                                sx={styles.primaryButton}
                                onClick={handleVerifyCustomDomain}
                                disabled={
                                  !settings?.promoShowcaseHosting?.customDomain
                                  || customDomainVerifying
                                  || customDomainSaving
                                }
                              >
                                {customDomainVerifying ? "Checking DNS..." : "Verify DNS & Connect"}
                              </Button>
                              {settings?.promoShowcaseHosting?.domainStatus ? (
                                <Chip
                                  size="small"
                                  label={`Domain: ${String(settings.promoShowcaseHosting.domainStatus).replace(/_/g, " ")}`}
                                />
                              ) : null}
                              {settings?.promoShowcaseHosting?.tlsStatus ? (
                                <Chip
                                  size="small"
                                  label={`SSL: ${String(settings.promoShowcaseHosting.tlsStatus).replace(/_/g, " ")}`}
                                />
                              ) : null}
                            </Box>
                          </Box>

                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                            <Button
                              variant="contained"
                              sx={styles.primaryButton}
                              onClick={() =>
                                history.push(
                                  `/instance/${instanceUniqueId}/settings/global-settings/promoshowcase`
                                )
                              }
                            >
                              Open Settings
                            </Button>

                            <Button
                              variant="outlined"
                              startIcon={<RefreshRoundedIcon />}
                              sx={styles.outlineButton}
                              onClick={handleReconnect}
                              disabled={connecting}
                            >
                              {connecting ? "Connecting..." : "Reconnect"}
                            </Button>

                            {siteUrl && (
                              <Button
                                variant="outlined"
                                startIcon={<OpenInNewRoundedIcon />}
                                sx={styles.outlineButton}
                                onClick={() => window.open(siteUrl, "_blank")}
                              >
                                Open Site
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    )}

                    {activeTab === 7 && (
                      <Box
                        className={builderFocusMode ? "ps-site-builder-focus__workspace" : ""}
                        sx={{ position: 'relative', minWidth: 0, minHeight: 0 }}
                      >
                      <PromoShowcaseBuilderIntegrated
                        templates={templates}
                        selectedTemplate={selectedTemplate}
                        selectedTemplateId={selectedTemplateId}
                        selectTemplateAndHydrate={selectTemplateAndHydrate}
                        loadTemplates={loadTemplates}
                        resetBuilderFromSelectedTemplate={resetBuilderFromSelectedTemplate}
                        templateForm={templateForm}
                        setTemplateForm={setTemplateForm}
                        templateStatus={templateStatus}
                        setTemplateStatus={setTemplateStatus}
                        settings={settings}
                        statusChipSx={statusChipSx}
                        layoutBuilder={layoutBuilder}
                        builderSlots={builderSlots}
                        slotMeta={SLOT_META}
                        selectedBuilderSlot={selectedBuilderSlot}
                        setSelectedBuilderSlot={setSelectedBuilderSlot}
                        selectedBuilderSlotMeta={selectedBuilderSlotMeta}
                        currentBuilderWidgets={currentBuilderWidgets}
                        selectedBuilderWidget={selectedBuilderWidget}
                        selectedBuilderWidgetIndex={selectedBuilderWidgetIndex}
                        setSelectedBuilderWidgetIndex={setSelectedBuilderWidgetIndex}
                        selectedBuilderWidgetMeta={selectedBuilderWidgetMeta}
                        selectedBuilderWidgetPresets={selectedBuilderWidgetPresets}
                        getWidgetLabel={getWidgetLabel}
                        getWidgetMeta={getWidgetMeta}
                        widgetLibrary={WIDGET_LIBRARY}
                        widgetDetails={WIDGET_DETAILS}
                        quickSections={QUICK_SECTIONS}
                        insertQuickSection={insertQuickSection}
                        widgetDraft={widgetDraft}
                        setWidgetDraft={setWidgetDraft}
                        addWidgetToSlot={addWidgetToSlot}
                        updateWidgetProps={updateWidgetProps}
                        addHeroSlide={addHeroSlide}
                        updateHeroSlide={updateHeroSlide}
                        removeHeroSlide={removeHeroSlide}
                        applyWidgetTemplatePreset={applyWidgetTemplatePreset}
                        moveWidgetInSlot={moveWidgetInSlot}
                        reorderWidgetInSlot={reorderWidgetInSlot}
                        removeWidgetFromSlot={removeWidgetFromSlot}
                        builderCanvasMode={builderCanvasMode}
                        setBuilderCanvasMode={setBuilderCanvasMode}
                        previewSelectedSelector={previewSelectedSelector}
                        previewHoveredSelector={previewHoveredSelector}
                        showSelectorOverlay={showSelectorOverlay}
                        setShowSelectorOverlay={setShowSelectorOverlay}
                        insertSelectorIntoCustomCss={insertSelectorIntoCustomCss}
                        templateStylesPreviewSrcDoc={templateStylesPreviewSrcDoc}
                        previewRendererMode={previewRendererMode}
                        setPreviewRendererMode={setPreviewRendererMode}
                        wordpressPreviewLoading={wordpressPreviewLoading}
                        wordpressPreviewError={wordpressPreviewError}
                        renderWidgetPreviewCard={renderWidgetPreviewCard}
                        templateCssEditorTab={templateCssEditorTab}
                        setTemplateCssEditorTab={setTemplateCssEditorTab}
                        showSelectorContract={showSelectorContract}
                        setShowSelectorContract={setShowSelectorContract}
                        templatesApplying={templatesApplying}
                        templatesSaving={templatesSaving}
                        applySelectedTemplate={applySelectedTemplate}
                        handleResyncNow={handleResyncNow}
                        handleRollbackLatest={handleRollbackLatest}
                        savePrivateTemplate={savePrivateTemplate}
                        updateSelectedPrivateTemplate={updateSelectedPrivateTemplate}
                        lastApplyResult={lastApplyResult}
                        templateFormErrors={templateFormErrors}
                        publishValidationRef={publishValidationRef}
                        styles={styles}
                        brand={BRAND}
                        siteUrl={siteUrl}
                        onImportPageTemplate={importPageTemplateToCurrentSlot}
                        layoutDirty={layoutDirty}
                        canUndoLayoutImport={canUndoLayoutImport}
                        onUndoLayoutImport={undoLastLayoutImport}
                        showcasePages={templateForm.pages}
                        addShowcasePage={addShowcasePage}
                        updateShowcasePage={updateShowcasePage}
                        removeShowcasePage={removeShowcasePage}
                        requestedSidebarView={builderRequestedSidebarView}
                        requestedSidebarViewTick={builderRequestedSidebarViewTick}
                        instanceUniqueId={instanceUniqueId}
                        onUploadShowcaseMedia={uploadShowcaseMedia}
                        focusMode={builderFocusMode}
                        onCloseFocusMode={() => setBuilderFocusMode(false)}
                      />
                      </Box>
                    )}

                    {activeTab === 8 && (
                      <Card sx={styles.glassCard}>
                        <CardContent sx={{ display: 'grid', gap: 2 }}>
                          <Box>
                            <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>Store Settings</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.slate }}>
                              Control whether this Showcase is a catalogue, quote-request store, or full eCommerce storefront.
                            </Typography>
                          </Box>

                          <TextField
                            select
                            size="small"
                            label="eCommerce"
                            value={templateForm?.storeSettings?.ecommerce_enabled === false ? 'off' : 'on'}
                            onChange={(e) => setTemplateForm((prev) => ({ ...prev, storeSettings: { ...(prev.storeSettings || {}), ecommerce_enabled: e.target.value === 'on' } }))}
                          >
                            <MenuItem value="on">Enabled — cart and checkout available</MenuItem>
                            <MenuItem value="off">Disabled — catalogue / enquiry only</MenuItem>
                          </TextField>

                          <TextField
                            select
                            size="small"
                            label="Show product prices"
                            value={templateForm?.storeSettings?.show_prices === false ? 'off' : 'on'}
                            onChange={(e) => setTemplateForm((prev) => ({ ...prev, storeSettings: { ...(prev.storeSettings || {}), show_prices: e.target.value === 'on' } }))}
                          >
                            <MenuItem value="on">Show prices</MenuItem>
                            <MenuItem value="off">Hide prices</MenuItem>
                          </TextField>

                          <Divider />
                          <Typography sx={{ fontWeight: 800 }}>Payment methods</Typography>

                          <TextField
                            select
                            size="small"
                            label="Request Quote / Estimate"
                            value={templateForm?.storeSettings?.payment_methods?.request_quote ? 'on' : 'off'}
                            disabled={templateForm?.storeSettings?.ecommerce_enabled === false}
                            onChange={(e) => setTemplateForm((prev) => ({ ...prev, storeSettings: { ...(prev.storeSettings || {}), payment_methods: { ...(prev.storeSettings?.payment_methods || {}), request_quote: e.target.value === 'on' } } }))}
                          >
                            <MenuItem value="on">Enabled</MenuItem>
                            <MenuItem value="off">Disabled</MenuItem>
                          </TextField>

                          <TextField
                            select
                            size="small"
                            label="Stripe"
                            value={templateForm?.storeSettings?.payment_methods?.stripe ? 'on' : 'off'}
                            disabled={templateForm?.storeSettings?.ecommerce_enabled === false}
                            onChange={(e) => setTemplateForm((prev) => ({ ...prev, storeSettings: { ...(prev.storeSettings || {}), payment_methods: { ...(prev.storeSettings?.payment_methods || {}), stripe: e.target.value === 'on' } } }))}
                          >
                            <MenuItem value="on">Enabled when WooCommerce Stripe is connected</MenuItem>
                            <MenuItem value="off">Disabled</MenuItem>
                          </TextField>

                          <TextField
                            size="small"
                            label="Quote gateway title"
                            value={templateForm?.storeSettings?.request_quote?.title || ''}
                            onChange={(e) => setTemplateForm((prev) => ({ ...prev, storeSettings: { ...(prev.storeSettings || {}), request_quote: { ...(prev.storeSettings?.request_quote || {}), title: e.target.value } } }))}
                          />
                          <TextField
                            size="small"
                            label="Quote description"
                            value={templateForm?.storeSettings?.request_quote?.description || ''}
                            onChange={(e) => setTemplateForm((prev) => ({ ...prev, storeSettings: { ...(prev.storeSettings || {}), request_quote: { ...(prev.storeSettings?.request_quote || {}), description: e.target.value } } }))}
                          />
                          <TextField
                            size="small"
                            label="Quote checkout button"
                            value={templateForm?.storeSettings?.request_quote?.checkout_button_text || ''}
                            onChange={(e) => setTemplateForm((prev) => ({ ...prev, storeSettings: { ...(prev.storeSettings || {}), request_quote: { ...(prev.storeSettings?.request_quote || {}), checkout_button_text: e.target.value } } }))}
                          />

                          <Divider />
                          <Typography sx={{ fontWeight: 800 }}>Checkout</Typography>

                          <TextField
                            select
                            size="small"
                            label="Guest checkout"
                            value={templateForm?.storeSettings?.guest_checkout === false ? 'off' : 'on'}
                            disabled={templateForm?.storeSettings?.ecommerce_enabled === false}
                            onChange={(e) => setTemplateForm((prev) => ({ ...prev, storeSettings: { ...(prev.storeSettings || {}), guest_checkout: e.target.value === 'on' } }))}
                          >
                            <MenuItem value="on">Allow guest checkout</MenuItem>
                            <MenuItem value="off">Require an account</MenuItem>
                          </TextField>

                          <TextField
                            select
                            size="small"
                            label="Taxes"
                            value={templateForm?.storeSettings?.taxes_enabled === false ? 'off' : 'on'}
                            onChange={(e) => setTemplateForm((prev) => ({ ...prev, storeSettings: { ...(prev.storeSettings || {}), taxes_enabled: e.target.value === 'on' } }))}
                          >
                            <MenuItem value="on">Use WooCommerce tax configuration</MenuItem>
                            <MenuItem value="off">Do not calculate tax</MenuItem>
                          </TextField>

                          <TextField
                            select
                            size="small"
                            label="Shipping"
                            value={templateForm?.storeSettings?.shipping_enabled === false ? 'off' : 'on'}
                            onChange={(e) => setTemplateForm((prev) => ({ ...prev, storeSettings: { ...(prev.storeSettings || {}), shipping_enabled: e.target.value === 'on' } }))}
                          >
                            <MenuItem value="on">Use WooCommerce shipping methods</MenuItem>
                            <MenuItem value="off">No shipping calculation</MenuItem>
                          </TextField>

                          <TextField
                            select
                            size="small"
                            label="Currency"
                            value={templateForm?.storeSettings?.currency || 'CAD'}
                            onChange={(e) => setTemplateForm((prev) => ({ ...prev, storeSettings: { ...(prev.storeSettings || {}), currency: e.target.value } }))}
                          >
                            <MenuItem value="CAD">CAD</MenuItem>
                            <MenuItem value="USD">USD</MenuItem>
                          </TextField>

                          <Divider />
                          <Typography sx={{ fontWeight: 800 }}>Catalog filters</Typography>
                          <TextField
                            select
                            size="small"
                            label="Archive filter sidebar"
                            value={templateForm?.storeSettings?.catalog_filters?.enabled === false ? 'off' : 'on'}
                            onChange={(e) => setTemplateForm((prev) => ({
                              ...prev,
                              storeSettings: {
                                ...(prev.storeSettings || {}),
                                catalog_filters: {
                                  ...(prev.storeSettings?.catalog_filters || {}),
                                  enabled: e.target.value === 'on',
                                },
                              },
                            }))}
                          >
                            <MenuItem value="on">Enabled</MenuItem>
                            <MenuItem value="off">Disabled</MenuItem>
                          </TextField>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 1 }}>
                            {[
                              ['category', 'Category'],
                              ['theme', 'Theme'],
                              ['brand', 'Brand'],
                              ['supplier', 'Supplier'],
                              ['color', 'Color'],
                              ['size', 'Size'],
                              ['price', 'Price'],
                            ].map(([key, label]) => (
                              <TextField
                                key={key}
                                select
                                size="small"
                                label={label}
                                value={templateForm?.storeSettings?.catalog_filters?.[key] === false ? 'off' : 'on'}
                                onChange={(e) => setTemplateForm((prev) => ({
                                  ...prev,
                                  storeSettings: {
                                    ...(prev.storeSettings || {}),
                                    catalog_filters: {
                                      ...(prev.storeSettings?.catalog_filters || {}),
                                      [key]: e.target.value === 'on',
                                    },
                                  },
                                }))}
                              >
                                <MenuItem value="on">Show</MenuItem>
                                <MenuItem value="off">Hide</MenuItem>
                              </TextField>
                            ))}
                          </Box>
                          <TextField
                            size="small"
                            label="Filter order"
                            value={(templateForm?.storeSettings?.catalog_filters?.order || ['category','theme','brand','supplier','color','size','price']).join(', ')}
                            helperText="Comma-separated: category, theme, brand, supplier, color, size, price"
                            onChange={(e) => {
                              const allowed = new Set(['category','theme','brand','supplier','color','size','price']);
                              const order = String(e.target.value || '').split(',').map((value) => value.trim().toLowerCase()).filter((value) => allowed.has(value));
                              setTemplateForm((prev) => ({
                                ...prev,
                                storeSettings: {
                                  ...(prev.storeSettings || {}),
                                  catalog_filters: {
                                    ...(prev.storeSettings?.catalog_filters || {}),
                                    order,
                                  },
                                },
                              }));
                            }}
                          />

                          <Divider />
                          <Typography sx={{ fontWeight: 800 }}>Order routing</Typography>
                          <TextField
                            select
                            size="small"
                            label="Paid website orders"
                            value={templateForm?.storeSettings?.orders?.paid_order_action || 'create_project'}
                            onChange={(e) => setTemplateForm((prev) => ({
                              ...prev,
                              storeSettings: {
                                ...(prev.storeSettings || {}),
                                orders: { ...(prev.storeSettings?.orders || {}), paid_order_action: e.target.value },
                              },
                            }))}
                          >
                            <MenuItem value="create_project">Automatically create an Elite Core project</MenuItem>
                            <MenuItem value="manual">Store in Showcase Orders for manual conversion</MenuItem>
                          </TextField>
                          <TextField
                            select
                            size="small"
                            label="Quote / estimate requests"
                            value={templateForm?.storeSettings?.orders?.quote_order_action || 'manual'}
                            onChange={(e) => setTemplateForm((prev) => ({
                              ...prev,
                              storeSettings: {
                                ...(prev.storeSettings || {}),
                                orders: { ...(prev.storeSettings?.orders || {}), quote_order_action: e.target.value },
                              },
                            }))}
                          >
                            <MenuItem value="manual">Store in Showcase Orders for manual conversion</MenuItem>
                            <MenuItem value="create_project">Automatically create a project</MenuItem>
                          </TextField>

                          <Typography variant="caption" sx={{ color: BRAND.slate }}>
                            Stripe credentials remain in WooCommerce/Stripe. PromoShowcase only controls whether the gateway is offered. All web orders retain a stable Showcase ID even after they become Elite Core projects.
                          </Typography>
                        </CardContent>
                      </Card>
                    )}

                    {activeTab === 9 && (
                      <Card sx={styles.glassCard}>
                        <CardContent sx={{ display: 'grid', gap: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Box>
                              <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>Showcase Orders</Typography>
                              <Typography variant="body2" sx={{ color: BRAND.slate }}>
                                Web orders are stored independently with a stable Showcase ID. Quote requests can be converted manually; paid orders create projects automatically when refreshed.
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              sx={styles.primaryButton}
                              startIcon={<RefreshRoundedIcon />}
                              disabled={!settings.promoShowcaseEnabled || showcaseOrdersRefreshing}
                              onClick={refreshPromoShowcaseOrders}
                            >
                              {showcaseOrdersRefreshing ? 'Refreshing…' : 'Refresh from WordPress'}
                            </Button>
                          </Box>

                          {showcaseOrdersLoading ? (
                            <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box>
                          ) : showcaseOrders.length === 0 ? (
                            <Typography variant="body2" sx={{ color: BRAND.slate }}>No Showcase orders have been synchronized yet.</Typography>
                          ) : (
                            <Box sx={{ display: 'grid', gap: 1 }}>
                              {showcaseOrders.map((order) => {
                                const paid = String(order?.paymentStatus || '').toLowerCase() === 'paid';
                                const quote = String(order?.orderMode || '').toLowerCase() === 'estimate';
                                const linked = !!order?.linkedOrderId;
                                return (
                                  <Box key={order.orderUid || order.id} sx={{ p: 1.5, border: `1px solid ${BRAND.line}`, background: '#fff', display: 'grid', gap: .75 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                                      <Box>
                                        <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>
                                          {order.wooOrderNumber ? `#${order.wooOrderNumber}` : `Woo #${order.wooOrderId}`}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: BRAND.slate, display: 'block' }}>
                                          Showcase ID: {order.orderUid}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: BRAND.slate }}>
                                          {order.customerName || order.customerEmail || 'Customer'} · {order.currency || 'CAD'} {Number(order.total || 0).toFixed(2)}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap' }}>
                                        <Chip size="small" label={quote ? 'Quote / Estimate' : 'eCommerce'} color={quote ? 'warning' : 'primary'} variant="outlined" />
                                        <Chip size="small" label={order.paymentStatus || 'unpaid'} color={paid ? 'success' : 'default'} />
                                        <Chip size="small" label={order.status || 'unknown'} variant="outlined" />
                                        {linked ? <Chip size="small" label="Project created" color="success" /> : null}
                                      </Box>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: BRAND.slate }}>
                                      {Array.isArray(order.items) ? order.items.length : 0} item{Array.isArray(order.items) && order.items.length === 1 ? '' : 's'}
                                      {order.paymentMethod ? ` · ${order.paymentMethod}` : ''}
                                      {order.dateCreated ? ` · ${new Date(order.dateCreated).toLocaleString()}` : ''}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      {order.adminUrl ? (
                                        <Button size="small" variant="outlined" onClick={() => window.open(order.adminUrl, '_blank')}>Open in WordPress</Button>
                                      ) : null}
                                      {linked ? (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          onClick={() => history.push(`/instance/${instanceUniqueId}/sales/orders/${order.linkedOrderId}`)}
                                        >
                                          Open Project
                                        </Button>
                                      ) : (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          disabled={creatingProjectOrderId === order.id}
                                          onClick={() => createProjectFromShowcaseOrder(order)}
                                        >
                                          {creatingProjectOrderId === order.id ? 'Creating…' : 'Create Project'}
                                        </Button>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {activeTab === 10 && (
                      <Card sx={styles.glassCard}>
                        <CardContent sx={{ display: 'grid', gap: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Box>
                              <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>Forms</Typography>
                              <Typography variant="body2" sx={{ color: BRAND.slate }}>
                                Responses submitted through Form blocks are stored here in Elite Core. New submissions also notify the configured PromoShowcase designer team.
                              </Typography>
                            </Box>
                            <Button variant="outlined" sx={styles.outlineButton} startIcon={<RefreshRoundedIcon />} onClick={loadFormSubmissions} disabled={formSubmissionsLoading}>
                              Refresh
                            </Button>
                          </Box>
                          {formSubmissionsLoading ? (
                            <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box>
                          ) : formSubmissions.length === 0 ? (
                            <Typography variant="body2" sx={{ color: BRAND.slate }}>No form submissions yet. Add a Form block in Site Builder and publish the site.</Typography>
                          ) : (
                            <Box sx={{ display: 'grid', gap: 1 }}>
                              {formSubmissions.map((submission) => (
                                <Box key={submission.submissionUid || submission.id} sx={{ p: 1.5, border: `1px solid ${BRAND.line}`, background: '#fff', display: 'grid', gap: .75 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                    <Box>
                                      <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>{submission.formTitle || 'Website form'}</Typography>
                                      <Typography variant="body2" sx={{ color: BRAND.slate }}>
                                        {submission.submitterName || submission.submitterEmail || 'Website visitor'}
                                        {submission.createdAt ? ` · ${new Date(submission.createdAt).toLocaleString()}` : ''}
                                      </Typography>
                                    </Box>
                                    <Chip size="small" label={submission.status || 'new'} color={submission.status === 'new' ? 'primary' : 'default'} />
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap' }}>
                                    {submission.submitterEmail ? <Chip size="small" variant="outlined" label={submission.submitterEmail} /> : null}
                                    {submission.sourcePage ? <Chip size="small" variant="outlined" label={`Page: ${submission.sourcePage}`} /> : null}
                                    {submission.ipAddress ? <Chip size="small" variant="outlined" label={`IP: ${submission.ipAddress}`} /> : null}
                                    {submission.emailDeliveryStatus ? <Chip size="small" variant="outlined" label={`Email: ${submission.emailDeliveryStatus}`} color={submission.emailDeliveryStatus === 'sent' ? 'success' : submission.emailDeliveryStatus === 'failed' ? 'error' : 'default'} /> : null}
                                  </Box>
                                  <Box sx={{ p: 1.25, borderRadius: 1.5, background: 'rgba(248,250,252,.9)', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))' }, gap: 1 }}>
                                    {Object.entries(submission.fields || {}).map(([key, value]) => (
                                      <Box key={key}>
                                        <Typography variant="caption" sx={{ color: BRAND.slate, fontWeight: 800, textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</Typography>
                                        <Typography variant="body2" sx={{ color: BRAND.ink, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{Array.isArray(value) ? value.join(', ') : String(value || '—')}</Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                  <Box sx={{ display: 'grid', gap: .25 }}>
                                    {submission.sourceUrl ? <Typography variant="caption" sx={{ color: BRAND.slate, overflowWrap: 'anywhere' }}><strong>Source:</strong> {submission.sourceUrl}</Typography> : null}
                                    {submission.referrer ? <Typography variant="caption" sx={{ color: BRAND.slate, overflowWrap: 'anywhere' }}><strong>Referrer:</strong> {submission.referrer}</Typography> : null}
                                    {submission.userAgent ? <Typography variant="caption" sx={{ color: BRAND.slate, overflowWrap: 'anywhere' }}><strong>Browser:</strong> {submission.userAgent}</Typography> : null}
                                    {submission.acceptLanguage ? <Typography variant="caption" sx={{ color: BRAND.slate }}><strong>Language:</strong> {submission.acceptLanguage}</Typography> : null}
                                    {submission.utm && Object.keys(submission.utm).length ? <Typography variant="caption" sx={{ color: BRAND.slate }}><strong>Campaign:</strong> {Object.entries(submission.utm).map(([key, value]) => `${key}=${value}`).join(' · ')}</Typography> : null}
                                    {submission.clientMeta && Object.keys(submission.clientMeta).length ? <Typography variant="caption" sx={{ color: BRAND.slate }}><strong>Device:</strong> {[submission.clientMeta.timezone, submission.clientMeta.viewport, submission.clientMeta.screen, submission.clientMeta.platform].filter(Boolean).join(' · ')}</Typography> : null}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {activeTab === 700 && (
                      <Card sx={styles.glassCard}>
                        <CardContent sx={{ display: 'grid', gap: 2.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AutoAwesomeRoundedIcon sx={{ color: BRAND.red }} />
                            <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>Template Studio</Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: BRAND.slate }}>
                            Choose a template, set your brand, then build slot widgets visually. No code needed.
                          </Typography>

                          <Box sx={{ p: 1.25, borderRadius: 2, border: `1px solid ${BRAND.line}`, background: 'rgba(255,255,255,0.9)' }}>
                            <Typography variant="caption" sx={{ color: BRAND.slate }}>
                              Sync status: <strong>{settings?.templateSyncState?.status || 'unknown'}</strong>
                              {settings?.templateSyncState?.updatedAt ? ` • ${new Date(settings.templateSyncState.updatedAt).toLocaleString()}` : ''}
                            </Typography>
                            {settings?.templateSyncState?.message ? (
                              <Typography variant="body2" sx={{ color: BRAND.ink }}>{settings.templateSyncState.message}</Typography>
                            ) : null}
                            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Button size="small" variant="outlined" sx={styles.outlineButton} onClick={handleResyncNow} disabled={templatesApplying}>Re-sync now</Button>
                              <Button size="small" variant="outlined" sx={styles.outlineButton} onClick={handleRollbackLatest} disabled={templatesApplying}>Rollback latest</Button>
                            </Box>
                          </Box>

                          {templateFormErrors.length > 0 && (
                            <Box sx={{ p: 1, borderRadius: 2, border: '1px solid #fecaca', background: '#fff1f2' }}>
                              {templateFormErrors.map((err, i) => (
                                <Typography key={`pref-${i}`} variant="caption" sx={{ display: 'block', color: '#991b1b' }}>• {err}</Typography>
                              ))}
                            </Box>
                          )}

                          <Box sx={{ p: 1.25, borderRadius: 2, border: `1px solid ${BRAND.line}`, background: 'rgba(255,255,255,0.9)' }}>
                            <Typography variant="caption" sx={{ color: BRAND.slate }}>Last applied snapshot</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.ink }}>
                              Template: {settings?.activeTemplate?.template_id || 'n/a'} {settings?.activeTemplate?.template_version ? `(${settings.activeTemplate.template_version})` : ''}
                            </Typography>
                            <Typography variant="caption" sx={{ color: BRAND.slate }}>
                              History entries: {Array.isArray(settings?.templateHistory) ? settings.templateHistory.length : 0}
                            </Typography>
                            {lastApplyResult?.via ? <Typography variant="caption" sx={{ display:'block', color: BRAND.slate }}>Last endpoint: {lastApplyResult.via}</Typography> : null}
                          </Box>

                          <Box sx={{ display: 'grid', gap: 1.5 }}>
                            <Box>
                              <Typography sx={{ fontWeight: 800, color: BRAND.ink }}>Choose a complete Showcase design</Typography>
                              <Typography variant="caption" sx={{ color: BRAND.slate }}>
                                Each starter installs a coordinated Home, Catalog, Services, About and Contact experience. You can customize every page after choosing.
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
                              {starterTemplates.map((tpl) => {
                                const active = String(tpl.id) === String(selectedTemplateId);
                                const theme = tpl?.defaults?.theme_vars || {};
                                const homeWidgets = tpl?.defaults?.layout_schema?.slots?.home || [];
                                const hero = homeWidgets.find((widget) => widget?.component === 'hero') || {};
                                const heroProps = hero?.props || {};
                                const primary = theme.primary_color || '#111827';
                                const secondary = theme.secondary_color || '#B91C1C';
                                const announcement = theme.announcement_text || '';
                                const heroTitle = heroProps.title || (heroProps.title_var ? 'Your Brand' : '') || 'Your Brand';
                                const heroSubtitle = heroProps.subtitle || tpl.description || '';
                                const pageCount = Array.isArray(tpl?.defaults?.pages) ? tpl.defaults.pages.length : 0;

                                return (
                                  <Box
                                    key={`starter-template-${tpl.id}`}
                                    onClick={() => selectTemplateAndHydrate(tpl.id)}
                                    sx={{
                                      overflow: 'hidden',
                                      borderRadius: 2.5,
                                      border: active ? `2px solid ${BRAND.red}` : `1px solid ${BRAND.line}`,
                                      background: '#fff',
                                      cursor: 'pointer',
                                      boxShadow: active ? '0 10px 28px rgba(195,23,29,0.12)' : '0 6px 20px rgba(15,23,42,0.06)',
                                      transition: 'transform 160ms ease, box-shadow 160ms ease',
                                      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 30px rgba(15,23,42,0.11)' },
                                    }}
                                  >
                                    <Box sx={{ background: '#f8fafc', p: 1 }}>
                                      <Box sx={{ borderRadius: 1.5, overflow: 'hidden', border: '1px solid rgba(15,23,42,0.1)', background: '#fff', aspectRatio: '16 / 10' }}>
                                        {announcement ? (
                                          <Box sx={{ px: 1, py: 0.45, bgcolor: primary, color: '#fff', fontSize: 7, textAlign: 'center', fontWeight: 700 }}>
                                            {announcement}
                                          </Box>
                                        ) : null}
                                        <Box sx={{ px: 1.1, py: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(15,23,42,0.08)' }}>
                                          <Box sx={{ width: 42, height: 7, borderRadius: 5, bgcolor: primary }} />
                                          <Box sx={{ display: 'flex', gap: 0.7 }}>
                                            {['Home', 'Catalog', 'Services', 'About', 'Contact'].map((item) => (
                                              <Typography key={item} sx={{ fontSize: 5.5, color: '#64748b', lineHeight: 1 }}>{item}</Typography>
                                            ))}
                                          </Box>
                                        </Box>
                                        <Box
                                          sx={{
                                            minHeight: 112,
                                            px: 2,
                                            py: 2.2,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 135%)`,
                                            color: '#fff',
                                          }}
                                        >
                                          <Typography sx={{ fontSize: 15, lineHeight: 1.05, fontWeight: 900, maxWidth: '80%' }}>{heroTitle}</Typography>
                                          <Typography sx={{ mt: 0.7, fontSize: 7.5, lineHeight: 1.35, opacity: 0.86, maxWidth: '82%' }}>{heroSubtitle}</Typography>
                                          <Box sx={{ mt: 1.2, width: 48, height: 15, borderRadius: 10, bgcolor: '#fff', opacity: 0.95 }} />
                                        </Box>
                                        <Box sx={{ p: 1, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.6 }}>
                                          {[0, 1, 2, 3].map((item) => (
                                            <Box key={item} sx={{ height: 23, borderRadius: 0.8, bgcolor: '#eef2f7', border: '1px solid #e2e8f0' }} />
                                          ))}
                                        </Box>
                                      </Box>
                                    </Box>

                                    <Box sx={{ p: 1.25 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                        <Typography sx={{ fontWeight: 850, color: BRAND.ink, fontSize: 14 }}>{tpl.name}</Typography>
                                        {active ? <Chip size="small" label="Selected" color="error" /> : null}
                                      </Box>
                                      <Typography variant="caption" sx={{ color: BRAND.slate, display: 'block', mt: 0.35, minHeight: 34 }}>
                                        {tpl.description}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: BRAND.slate, fontWeight: 700 }}>
                                        {pageCount || 5} pages included
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>

                            {savedTemplates.length > 0 ? (
                              <Box sx={{ mt: 0.5, p: 1.25, borderRadius: 2, border: `1px solid ${BRAND.line}`, background: 'rgba(248,250,252,0.8)' }}>
                                <Typography sx={{ fontWeight: 800, color: BRAND.ink, fontSize: 13 }}>Saved drafts</Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: BRAND.slate, mb: 1 }}>
                                  These are your editable builder drafts, separate from the complete starter designs above.
                                </Typography>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label={`Saved drafts (${savedTemplates.length})`}
                                  value={savedTemplates.some((tpl) => String(tpl.id) === String(selectedTemplateId)) ? selectedTemplateId : ''}
                                  onChange={(e) => selectTemplateAndHydrate(e.target.value)}
                                  disabled={templatesLoading}
                                >
                                  {savedTemplates.map((tpl) => (
                                    <MenuItem key={tpl.id} value={tpl.id}>
                                      {String(tpl.name || 'Saved Showcase draft').replace(/PromoShowcase/gi, 'Showcase')}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Box>
                            ) : null}

                            <Box sx={{ mt: 0.5 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={styles.outlineButton}
                                onClick={resetBuilderFromSelectedTemplate}
                                disabled={!selectedTemplate}
                              >
                                Reset builder to selected template
                              </Button>
                            </Box>
                          </Box>

                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                            <Typography variant="caption" sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' }, color: BRAND.slate }}>
                              Stable selector contract: use <code>.promoshowcase-slot .ps-widget--hero</code>, <code>.ps-widget--category_grid</code>, <code>.ps-widget--product_grid</code>, <code>.ps-widget--product_carousel</code>, <code>.ps-widget--rich_text</code>, and <code>.ps-widget--contact_block</code>.
                            </Typography>
                            <TextField size="small" label="Brand name" value={templateForm.brandName} onChange={(e) => setTemplateForm((p) => ({ ...p, brandName: e.target.value }))} />
                            <TextField size="small" label="Primary color" value={templateForm.primaryColor} onChange={(e) => setTemplateForm((p) => ({ ...p, primaryColor: e.target.value }))} />
                            <TextField size="small" label="Secondary color" value={templateForm.secondaryColor} onChange={(e) => setTemplateForm((p) => ({ ...p, secondaryColor: e.target.value }))} />
                            <TextField size="small" label="Announcement text" value={templateForm.announcementText} onChange={(e) => setTemplateForm((p) => ({ ...p, announcementText: e.target.value }))} />
                            <TextField size="small" label="Logo URL" value={templateForm.logoUrl} onChange={(e) => setTemplateForm((p) => ({ ...p, logoUrl: e.target.value }))} />
                            <TextField size="small" label="Featured categories (comma separated)" value={templateForm.featuredCategories} onChange={(e) => setTemplateForm((p) => ({ ...p, featuredCategories: e.target.value }))} />
                            <TextField size="small" multiline minRows={4} label="Template CSS (default theme layer)" value={templateForm.templateCss} onChange={(e) => setTemplateForm((p) => ({ ...p, templateCss: e.target.value }))} sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }} />
                            <TextField
                              size="small"
                              multiline
                              minRows={4}
                              label="Custom CSS (site override layer)"
                              value={templateForm.customCss}
                              onChange={(e) => setTemplateForm((p) => ({ ...p, customCss: e.target.value }))}
                              helperText={globalStylesheet ? "Global stylesheet from Design Architecture is prepended automatically on apply." : undefined}
                              sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}
                            />
                            <Box sx={{ gridColumn: { xs: "1 / -1", md: "1 / -1" }, border: `1px solid ${BRAND.line}`, borderRadius: 2, overflow: "hidden", background: "#fff" }}>
                              <Box sx={{ px: 1.25, py: 0.9, borderBottom: `1px solid ${BRAND.line}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Box>
                                  <Typography sx={{ fontWeight: 800, color: BRAND.ink }}>Template Styles Live Preview</Typography>
                                  <Typography variant="caption" sx={{ color: BRAND.slate }}>Live updates while typing (debounced). Hover/click a block to grab selector.</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <TextField select size="small" label="Slot" value={previewSlot} onChange={(e) => setPreviewSlot(e.target.value)} sx={{ minWidth: 170 }}>
                                    {['home', 'catalog', 'about', 'contact'].map((slot) => (
                                      <MenuItem key={`css-preview-slot-${slot}`} value={slot}>{slot}</MenuItem>
                                    ))}
                                  </TextField>
                                  <Tooltip title={showSelectorOverlay ? 'Hide selector helper' : 'Show selector helper'}>
                                    <IconButton size="small" onClick={() => setShowSelectorOverlay((prev) => !prev)} sx={{ border: `1px solid ${BRAND.line}`, borderRadius: 1.5 }}>
                                      {showSelectorOverlay ? <VisibilityRoundedIcon fontSize="small" /> : <VisibilityOffRoundedIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                              <Box sx={{ height: { xs: 340, md: 420 }, background: "#f8fafc", position: 'relative' }}>
                                <iframe
                                  title="Template styles live preview"
                                  sandbox="allow-same-origin allow-scripts"
                                  srcDoc={templateStylesPreviewSrcDoc}
                                  style={{ width: "100%", height: "100%", border: 0, display: "block" }}
                                />
                                {showSelectorOverlay ? (
                                  <Box sx={{ position: 'absolute', right: 10, bottom: 10, background: 'rgba(15,23,42,0.9)', color: '#fff', borderRadius: 1.5, px: 1, py: 0.75, maxWidth: '75%', boxShadow: '0 6px 18px rgba(2,6,23,0.35)' }}>
                                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>Selector</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{previewSelectedSelector || previewHoveredSelector || '.promoshowcase-slot .ps-widget--hero'}</Typography>
                                  </Box>
                                ) : null}
                              </Box>
                              {showSelectorOverlay ? (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1, borderTop: `1px solid ${BRAND.line}`, background: 'rgba(248,250,252,0.95)' }}>
                                  <Typography variant="caption" sx={{ color: BRAND.slate, flex: 1 }}>Click block in preview, then insert selector into Custom CSS.</Typography>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    sx={styles.outlineButton}
                                    onClick={() => insertSelectorIntoCustomCss(previewSelectedSelector || previewHoveredSelector)}
                                    disabled={!(previewSelectedSelector || previewHoveredSelector)}
                                  >
                                    Insert Selector
                                  </Button>
                                </Box>
                              ) : null}
                            </Box>
                            <TextField select size="small" label="Template status" value={templateStatus} onChange={(e) => setTemplateStatus(e.target.value)}>
                              <MenuItem value="draft">Draft</MenuItem>
                              <MenuItem value="active">Active</MenuItem>
                            </TextField>
                          </Box>

                          <Box sx={{ p: 1.5, border: `1px solid ${BRAND.line}`, borderRadius: 2, background: 'rgba(255,255,255,0.85)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <ViewQuiltRoundedIcon sx={{ color: BRAND.red }} />
                              <Typography sx={{ fontWeight: 800, color: BRAND.ink }}>Widget Builder</Typography>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' }, gap: 1, alignItems: 'center' }}>
                              <TextField
                                select
                                size="small"
                                label="Slot"
                                value={widgetDraft.slot}
                                onChange={(e) => setWidgetDraft((p) => ({ ...p, slot: e.target.value }))}
                              >
                                {['home', 'catalog', 'about', 'contact'].map((slot) => (
                                  <MenuItem key={slot} value={slot}>{slot}</MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                select
                                size="small"
                                label="Widget"
                                value={widgetDraft.type}
                                onChange={(e) => setWidgetDraft((p) => ({ ...p, type: e.target.value }))}
                              >
                                {WIDGET_LIBRARY.map((widget) => (
                                  <MenuItem key={widget.key} value={widget.key}>{widget.label}</MenuItem>
                                ))}
                              </TextField>
                              <Button variant="outlined" sx={styles.outlineButton} onClick={addWidgetToSlot}>Add Widget</Button>
                            </Box>

                            <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 0.75 }}>
                              {WIDGET_LIBRARY.map((widget) => {
                                const meta = getWidgetMeta(widget.key);
                                const Icon = meta.icon;
                                const selected = widgetDraft.type === widget.key;
                                return (
                                  <Box
                                    key={`widget-palette-${widget.key}`}
                                    onClick={() => setWidgetDraft((p) => ({ ...p, type: widget.key }))}
                                    sx={{
                                      p: 1,
                                      borderRadius: 1.5,
                                      border: selected ? `1px solid ${BRAND.red}` : `1px solid ${BRAND.line}`,
                                      background: selected ? 'rgba(195,23,29,0.08)' : 'rgba(255,255,255,0.95)',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                      <Icon sx={{ fontSize: 16, color: meta.accent }} />
                                      <Typography sx={{ fontWeight: 800, color: BRAND.ink, fontSize: 13 }}>{widget.label}</Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: BRAND.slate }}>
                                      {WIDGET_DETAILS[widget.key] || 'Composable widget block.'}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                            <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <PaletteRoundedIcon sx={{ fontSize: 18, color: BRAND.slate }} />
                              <Typography variant="caption" sx={{ color: BRAND.slate }}>Preview slot</Typography>
                              <TextField select size="small" value={previewSlot} onChange={(e) => setPreviewSlot(e.target.value)} sx={{ minWidth: 170 }}>
                                {['home', 'catalog', 'about', 'contact'].map((slot) => <MenuItem key={slot} value={slot}>{slot}</MenuItem>)}
                              </TextField>
                            </Box>

                            <Box sx={{ mt: 1.25, display: 'grid', gap: 1 }}>
                              {Object.entries(layoutBuilder?.slots || {}).map(([slot, widgets]) => {
                                const slotMeta = SLOT_META[slot] || {};
                                const SlotIcon = slotMeta.icon || ViewQuiltRoundedIcon;
                                return (
                                <Box key={slot} sx={{ border: `1px solid ${BRAND.line}`, borderRadius: 2, p: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                      <Box sx={{ width: 26, height: 26, borderRadius: 2, display: 'grid', placeItems: 'center', background: slotMeta.tint || 'rgba(148,163,184,0.12)' }}>
                                        <SlotIcon sx={{ fontSize: 16, color: BRAND.ink }} />
                                      </Box>
                                      <Typography sx={{ fontWeight: 800, color: BRAND.ink, textTransform: 'capitalize' }}>{slotMeta.label || slot} slot</Typography>
                                    </Box>
                                    <Chip size="small" label={`${Array.isArray(widgets) ? widgets.length : 0} widget${Array.isArray(widgets) && widgets.length === 1 ? '' : 's'}`} />
                                  </Box>
                                  {!Array.isArray(widgets) || widgets.length === 0 ? (
                                    <Typography variant="caption" sx={{ color: BRAND.slate }}>No widgets yet.</Typography>
                                  ) : (
                                    <Box sx={{ display: 'grid', gap: 0.75 }}>
                                      {widgets.map((widget, idx) => (
                                        <Box key={`${slot}-${idx}`} sx={{ p: 1, borderRadius: 1.5, background: 'rgba(248,250,252,0.95)', border: `1px dashed ${BRAND.line}` }}>
                                          {(() => {
                                            const widgetMeta = getWidgetMeta(widget.component);
                                            const WidgetIcon = widgetMeta.icon;
                                            const presets = WIDGET_TEMPLATES[widget.component] || [];
                                            return (
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                              <WidgetIcon sx={{ fontSize: 16, color: widgetMeta.accent }} />
                                              <Typography sx={{ fontWeight: 700, color: BRAND.ink }}>{widget.component}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                              {presets.map((preset) => (
                                                <Button
                                                  key={`preset-${slot}-${idx}-${preset.key}`}
                                                  size="small"
                                                  variant="outlined"
                                                  sx={styles.outlineButton}
                                                  onClick={() => applyWidgetTemplatePreset(slot, idx, preset.props)}
                                                >
                                                  {preset.label}
                                                </Button>
                                              ))}
                                              <Tooltip title="Move up">
                                                <IconButton size="small" onClick={() => moveWidgetInSlot(slot, idx, 'up')}>
                                                  <ArrowUpwardRoundedIcon fontSize="inherit" />
                                                </IconButton>
                                              </Tooltip>
                                              <Tooltip title="Move down">
                                                <IconButton size="small" onClick={() => moveWidgetInSlot(slot, idx, 'down')}>
                                                  <ArrowDownwardRoundedIcon fontSize="inherit" />
                                                </IconButton>
                                              </Tooltip>
                                              <Tooltip title="Remove">
                                                <IconButton size="small" color="error" onClick={() => removeWidgetFromSlot(slot, idx)}>
                                                  <DeleteOutlineRoundedIcon fontSize="inherit" />
                                                </IconButton>
                                              </Tooltip>
                                            </Box>
                                          </Box>
                                            );
                                          })()}
                                          <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.75 }}>
                                            {widget.component === 'hero' && (
                                              <TextField
                                                size="small"
                                                type="number"
                                                label="Min height (px)"
                                                value={widget?.props?.min_height ?? 460}
                                                onChange={(e) => updateWidgetProps(slot, idx, 'min_height', Number(e.target.value || 0))}
                                              />
                                            )}
                                            {(widget.component === 'hero' || widget.component === 'hero_split' || widget.component === 'rich_text' || widget.component === 'product_grid' || widget.component === 'featured_categories' || widget.component === 'category_grid' || widget.component === 'brand_grid' || widget.component === 'theme_grid') && (
                                              <TextField size="small" label="Title / Headline" value={widget?.props?.title || widget?.props?.headline || ''} onChange={(e) => updateWidgetProps(slot, idx, 'title', e.target.value)} />
                                            )}
                                            {(widget.component === 'hero' || widget.component === 'hero_split') && (
                                              <TextField size="small" label="Subtitle / Body" value={widget?.props?.subtitle || ''} onChange={(e) => updateWidgetProps(slot, idx, 'subtitle', e.target.value)} />
                                            )}
                                            {widget.component === 'gallery' && (
                                              <>
                                                <TextField size="small" label="Subheading" value={widget?.props?.subheading || ''} onChange={(e) => updateWidgetProps(slot, idx, 'subheading', e.target.value)} />
                                                <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updateWidgetProps(slot, idx, 'title', e.target.value)} />
                                                <TextField size="small" label="Description" value={widget?.props?.description || ''} onChange={(e) => updateWidgetProps(slot, idx, 'description', e.target.value)} />
                                                <TextField select size="small" label="Layout" value={widget?.props?.layout || 'grid'} onChange={(e) => updateWidgetProps(slot, idx, 'layout', e.target.value)}><MenuItem value="grid">Grid</MenuItem><MenuItem value="masonry">Masonry</MenuItem></TextField>
                                                <TextField size="small" type="number" label="Desktop columns" value={widget?.props?.columns_desktop ?? 3} inputProps={{ min: 1, max: 6 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_desktop', Number(e.target.value || 3))} />
                                                <TextField size="small" type="number" label="Tablet columns" value={widget?.props?.columns_tablet ?? 2} inputProps={{ min: 1, max: 4 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_tablet', Number(e.target.value || 2))} />
                                                <TextField size="small" type="number" label="Mobile columns" value={widget?.props?.columns_mobile ?? 1} inputProps={{ min: 1, max: 2 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_mobile', Number(e.target.value || 1))} />
                                                <TextField size="small" type="number" label="Gap" value={widget?.props?.gap ?? 16} inputProps={{ min: 0, max: 80 }} onChange={(e) => updateWidgetProps(slot, idx, 'gap', Number(e.target.value || 0))} />
                                                <TextField size="small" type="number" label="Image radius" value={widget?.props?.image_radius ?? 16} inputProps={{ min: 0, max: 100 }} onChange={(e) => updateWidgetProps(slot, idx, 'image_radius', Number(e.target.value || 0))} />
                                                <FormControlLabel control={<Switch checked={widget?.props?.lightbox !== false} onChange={(e) => updateWidgetProps(slot, idx, 'lightbox', e.target.checked)} />} label="Open images in lightbox" />
                                                <TextField multiline minRows={5} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Images — Image URL | Alt text | Caption | Link URL" value={(widget?.props?.items || []).map((item) => [item.image_url,item.alt,item.caption,item.link_url].join(' | ')).join('\n')} onChange={(e) => updateWidgetProps(slot, idx, 'items', e.target.value.split('\n').filter(Boolean).map((line) => { const [image_url,alt,caption,link_url] = line.split('|').map((v) => v.trim()); return { image_url, alt, caption, link_url }; }))} />
                                              </>
                                            )}
                                            {widget.component === 'video' && (
                                              <>
                                                <TextField size="small" label="Subheading" value={widget?.props?.subheading || ''} onChange={(e) => updateWidgetProps(slot, idx, 'subheading', e.target.value)} />
                                                <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updateWidgetProps(slot, idx, 'title', e.target.value)} />
                                                <TextField size="small" label="Body" value={widget?.props?.body || ''} onChange={(e) => updateWidgetProps(slot, idx, 'body', e.target.value)} />
                                                <TextField size="small" label="Video URL" helperText="YouTube, Vimeo, MP4 or WebM." value={widget?.props?.video_url || ''} onChange={(e) => updateWidgetProps(slot, idx, 'video_url', e.target.value)} />
                                                <TextField size="small" label="Poster image URL" value={widget?.props?.poster_url || ''} onChange={(e) => updateWidgetProps(slot, idx, 'poster_url', e.target.value)} />
                                                <TextField size="small" label="Caption" value={widget?.props?.caption || ''} onChange={(e) => updateWidgetProps(slot, idx, 'caption', e.target.value)} />
                                                <TextField select size="small" label="Media position" value={widget?.props?.media_position || 'right'} onChange={(e) => updateWidgetProps(slot, idx, 'media_position', e.target.value)}><MenuItem value="right">Right</MenuItem><MenuItem value="left">Left</MenuItem></TextField>
                                                <TextField size="small" type="number" label="Content width %" value={widget?.props?.content_width ?? 44} inputProps={{ min: 30, max: 70 }} onChange={(e) => updateWidgetProps(slot, idx, 'content_width', Number(e.target.value || 44))} />
                                                <TextField size="small" type="number" label="Radius" value={widget?.props?.radius ?? 18} inputProps={{ min: 0, max: 100 }} onChange={(e) => updateWidgetProps(slot, idx, 'radius', Number(e.target.value || 0))} />
                                                <FormControlLabel control={<Switch checked={!!widget?.props?.autoplay} onChange={(e) => updateWidgetProps(slot, idx, 'autoplay', e.target.checked)} />} label="Autoplay direct video" />
                                                <FormControlLabel control={<Switch checked={!!widget?.props?.muted} onChange={(e) => updateWidgetProps(slot, idx, 'muted', e.target.checked)} />} label="Muted" />
                                                <FormControlLabel control={<Switch checked={widget?.props?.controls !== false} onChange={(e) => updateWidgetProps(slot, idx, 'controls', e.target.checked)} />} label="Show controls" />
                                              </>
                                            )}
                                            {widget.component === 'team' && (
                                              <>
                                                <TextField size="small" label="Subheading" value={widget?.props?.subheading || ''} onChange={(e) => updateWidgetProps(slot, idx, 'subheading', e.target.value)} />
                                                <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updateWidgetProps(slot, idx, 'title', e.target.value)} />
                                                <TextField size="small" label="Description" value={widget?.props?.description || ''} onChange={(e) => updateWidgetProps(slot, idx, 'description', e.target.value)} />
                                                <TextField select size="small" label="Style" value={widget?.props?.card_style || 'minimal'} onChange={(e) => updateWidgetProps(slot, idx, 'card_style', e.target.value)}><MenuItem value="minimal">Minimal</MenuItem><MenuItem value="cards">Cards</MenuItem></TextField>
                                                <TextField size="small" type="number" label="Desktop columns" value={widget?.props?.columns_desktop ?? 3} inputProps={{ min: 1, max: 5 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_desktop', Number(e.target.value || 3))} />
                                                <TextField size="small" type="number" label="Tablet columns" value={widget?.props?.columns_tablet ?? 2} inputProps={{ min: 1, max: 3 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_tablet', Number(e.target.value || 2))} />
                                                <TextField size="small" type="number" label="Mobile columns" value={widget?.props?.columns_mobile ?? 1} inputProps={{ min: 1, max: 2 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_mobile', Number(e.target.value || 1))} />
                                                <TextField multiline minRows={5} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="People — Name | Role | Bio | Image URL | Email | LinkedIn URL" value={(widget?.props?.items || []).map((item) => [item.name,item.role,item.bio,item.image_url,item.email,item.linkedin_url].join(' | ')).join('\n')} onChange={(e) => updateWidgetProps(slot, idx, 'items', e.target.value.split('\n').filter(Boolean).map((line) => { const [name,role,bio,image_url,email,linkedin_url] = line.split('|').map((v) => v.trim()); return { name, role, bio, image_url, email, linkedin_url }; }))} />
                                              </>
                                            )}
                                            {widget.component === 'timeline' && (
                                              <>
                                                <TextField size="small" label="Subheading" value={widget?.props?.subheading || ''} onChange={(e) => updateWidgetProps(slot, idx, 'subheading', e.target.value)} />
                                                <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updateWidgetProps(slot, idx, 'title', e.target.value)} />
                                                <TextField size="small" label="Description" value={widget?.props?.description || ''} onChange={(e) => updateWidgetProps(slot, idx, 'description', e.target.value)} />
                                                <TextField select size="small" label="Style" value={widget?.props?.style || 'vertical'} onChange={(e) => updateWidgetProps(slot, idx, 'style', e.target.value)}><MenuItem value="vertical">Vertical timeline</MenuItem><MenuItem value="cards">Milestone cards</MenuItem></TextField>
                                                <TextField multiline minRows={5} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Steps — Label | Title | Description" value={(widget?.props?.items || []).map((item) => [item.label,item.title,item.body].join(' | ')).join('\n')} onChange={(e) => updateWidgetProps(slot, idx, 'items', e.target.value.split('\n').filter(Boolean).map((line) => { const [label,title,...bodyParts] = line.split('|'); return { label: String(label || '').trim(), title: String(title || '').trim(), body: bodyParts.join('|').trim() }; }))} />
                                              </>
                                            )}
                                            {widget.component === 'comparison' && (
                                              <>
                                                <TextField size="small" label="Subheading" value={widget?.props?.subheading || ''} onChange={(e) => updateWidgetProps(slot, idx, 'subheading', e.target.value)} />
                                                <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updateWidgetProps(slot, idx, 'title', e.target.value)} />
                                                <TextField size="small" label="Description" value={widget?.props?.description || ''} onChange={(e) => updateWidgetProps(slot, idx, 'description', e.target.value)} />
                                                <TextField size="small" label="First column label" value={widget?.props?.first_column_label || 'Feature'} onChange={(e) => updateWidgetProps(slot, idx, 'first_column_label', e.target.value)} />
                                                <TextField multiline minRows={3} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Columns — Key | Label | Highlight (yes/no)" value={(widget?.props?.columns || []).map((col) => [col.key,col.label,col.highlight ? 'yes' : 'no'].join(' | ')).join('\n')} onChange={(e) => updateWidgetProps(slot, idx, 'columns', e.target.value.split('\n').filter(Boolean).map((line, colIndex) => { const [keyRaw,label,highlight] = line.split('|').map((v) => v.trim()); const key = keyRaw || `option_${colIndex + 1}`; return { key: key.toLowerCase().replace(/[^a-z0-9]+/g,'_'), label: label || key, highlight: String(highlight || '').toLowerCase() === 'yes' }; }))} />
                                                <TextField multiline minRows={5} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Rows — Label | value 1 | value 2 | value 3..." helperText="Values follow the column order above." value={(widget?.props?.rows || []).map((row) => [row.label, ...(widget?.props?.columns || []).map((col) => row[col.key] || '')].join(' | ')).join('\n')} onChange={(e) => { const columns = widget?.props?.columns || []; updateWidgetProps(slot, idx, 'rows', e.target.value.split('\n').filter(Boolean).map((line) => { const parts = line.split('|').map((v) => v.trim()); const row = { label: parts.shift() || '' }; columns.forEach((col, colIndex) => { row[col.key] = parts[colIndex] || ''; }); return row; })); }} />
                                                <TextField size="small" label="CTA label" value={widget?.props?.cta_label || ''} onChange={(e) => updateWidgetProps(slot, idx, 'cta_label', e.target.value)} />
                                                <TextField size="small" label="CTA URL" value={widget?.props?.cta_url || ''} onChange={(e) => updateWidgetProps(slot, idx, 'cta_url', e.target.value)} />
                                              </>
                                            )}
                                            {widget.component === 'gallery' && (
                                  <>
                                    <TextField size="small" label="Subheading" value={widget?.props?.subheading || ''} onChange={(e) => updatePageWidgetProps(idx, 'subheading', e.target.value)} />
                                    <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updatePageWidgetProps(idx, 'title', e.target.value)} />
                                    <TextField size="small" label="Description" value={widget?.props?.description || ''} onChange={(e) => updatePageWidgetProps(idx, 'description', e.target.value)} />
                                    <TextField select size="small" label="Layout" value={widget?.props?.layout || 'grid'} onChange={(e) => updatePageWidgetProps(idx, 'layout', e.target.value)}><MenuItem value="grid">Grid</MenuItem><MenuItem value="masonry">Masonry</MenuItem></TextField>
                                    <TextField size="small" type="number" label="Desktop columns" value={widget?.props?.columns_desktop ?? 3} inputProps={{ min: 1, max: 6 }} onChange={(e) => updatePageWidgetProps(idx, 'columns_desktop', Number(e.target.value || 3))} />
                                    <TextField size="small" type="number" label="Tablet columns" value={widget?.props?.columns_tablet ?? 2} inputProps={{ min: 1, max: 4 }} onChange={(e) => updatePageWidgetProps(idx, 'columns_tablet', Number(e.target.value || 2))} />
                                    <TextField size="small" type="number" label="Mobile columns" value={widget?.props?.columns_mobile ?? 1} inputProps={{ min: 1, max: 2 }} onChange={(e) => updatePageWidgetProps(idx, 'columns_mobile', Number(e.target.value || 1))} />
                                    <TextField size="small" type="number" label="Gap" value={widget?.props?.gap ?? 16} inputProps={{ min: 0, max: 80 }} onChange={(e) => updatePageWidgetProps(idx, 'gap', Number(e.target.value || 0))} />
                                    <TextField size="small" type="number" label="Image radius" value={widget?.props?.image_radius ?? 16} inputProps={{ min: 0, max: 100 }} onChange={(e) => updatePageWidgetProps(idx, 'image_radius', Number(e.target.value || 0))} />
                                    <FormControlLabel control={<Switch checked={widget?.props?.lightbox !== false} onChange={(e) => updatePageWidgetProps(idx, 'lightbox', e.target.checked)} />} label="Open images in lightbox" />
                                    <TextField multiline minRows={5} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Images — Image URL | Alt text | Caption | Link URL" value={(widget?.props?.items || []).map((item) => [item.image_url,item.alt,item.caption,item.link_url].join(' | ')).join('\n')} onChange={(e) => updatePageWidgetProps(idx, 'items', e.target.value.split('\n').filter(Boolean).map((line) => { const [image_url,alt,caption,link_url] = line.split('|').map((v) => v.trim()); return { image_url, alt, caption, link_url }; }))} />
                                  </>
                                )}
                                {widget.component === 'video' && (
                                  <>
                                    <TextField size="small" label="Subheading" value={widget?.props?.subheading || ''} onChange={(e) => updatePageWidgetProps(idx, 'subheading', e.target.value)} />
                                    <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updatePageWidgetProps(idx, 'title', e.target.value)} />
                                    <TextField size="small" label="Body" value={widget?.props?.body || ''} onChange={(e) => updatePageWidgetProps(idx, 'body', e.target.value)} />
                                    <TextField size="small" label="Video URL" helperText="YouTube, Vimeo, MP4 or WebM." value={widget?.props?.video_url || ''} onChange={(e) => updatePageWidgetProps(idx, 'video_url', e.target.value)} />
                                    <TextField size="small" label="Poster image URL" value={widget?.props?.poster_url || ''} onChange={(e) => updatePageWidgetProps(idx, 'poster_url', e.target.value)} />
                                    <TextField size="small" label="Caption" value={widget?.props?.caption || ''} onChange={(e) => updatePageWidgetProps(idx, 'caption', e.target.value)} />
                                    <TextField select size="small" label="Media position" value={widget?.props?.media_position || 'right'} onChange={(e) => updatePageWidgetProps(idx, 'media_position', e.target.value)}><MenuItem value="right">Right</MenuItem><MenuItem value="left">Left</MenuItem></TextField>
                                    <TextField size="small" type="number" label="Content width %" value={widget?.props?.content_width ?? 44} inputProps={{ min: 30, max: 70 }} onChange={(e) => updatePageWidgetProps(idx, 'content_width', Number(e.target.value || 44))} />
                                    <TextField size="small" type="number" label="Radius" value={widget?.props?.radius ?? 18} inputProps={{ min: 0, max: 100 }} onChange={(e) => updatePageWidgetProps(idx, 'radius', Number(e.target.value || 0))} />
                                    <FormControlLabel control={<Switch checked={!!widget?.props?.autoplay} onChange={(e) => updatePageWidgetProps(idx, 'autoplay', e.target.checked)} />} label="Autoplay direct video" />
                                    <FormControlLabel control={<Switch checked={!!widget?.props?.muted} onChange={(e) => updatePageWidgetProps(idx, 'muted', e.target.checked)} />} label="Muted" />
                                    <FormControlLabel control={<Switch checked={widget?.props?.controls !== false} onChange={(e) => updatePageWidgetProps(idx, 'controls', e.target.checked)} />} label="Show controls" />
                                  </>
                                )}
                                {widget.component === 'team' && (
                                  <>
                                    <TextField size="small" label="Subheading" value={widget?.props?.subheading || ''} onChange={(e) => updatePageWidgetProps(idx, 'subheading', e.target.value)} />
                                    <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updatePageWidgetProps(idx, 'title', e.target.value)} />
                                    <TextField size="small" label="Description" value={widget?.props?.description || ''} onChange={(e) => updatePageWidgetProps(idx, 'description', e.target.value)} />
                                    <TextField select size="small" label="Style" value={widget?.props?.card_style || 'minimal'} onChange={(e) => updatePageWidgetProps(idx, 'card_style', e.target.value)}><MenuItem value="minimal">Minimal</MenuItem><MenuItem value="cards">Cards</MenuItem></TextField>
                                    <TextField size="small" type="number" label="Desktop columns" value={widget?.props?.columns_desktop ?? 3} inputProps={{ min: 1, max: 5 }} onChange={(e) => updatePageWidgetProps(idx, 'columns_desktop', Number(e.target.value || 3))} />
                                    <TextField size="small" type="number" label="Tablet columns" value={widget?.props?.columns_tablet ?? 2} inputProps={{ min: 1, max: 3 }} onChange={(e) => updatePageWidgetProps(idx, 'columns_tablet', Number(e.target.value || 2))} />
                                    <TextField size="small" type="number" label="Mobile columns" value={widget?.props?.columns_mobile ?? 1} inputProps={{ min: 1, max: 2 }} onChange={(e) => updatePageWidgetProps(idx, 'columns_mobile', Number(e.target.value || 1))} />
                                    <TextField multiline minRows={5} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="People — Name | Role | Bio | Image URL | Email | LinkedIn URL" value={(widget?.props?.items || []).map((item) => [item.name,item.role,item.bio,item.image_url,item.email,item.linkedin_url].join(' | ')).join('\n')} onChange={(e) => updatePageWidgetProps(idx, 'items', e.target.value.split('\n').filter(Boolean).map((line) => { const [name,role,bio,image_url,email,linkedin_url] = line.split('|').map((v) => v.trim()); return { name, role, bio, image_url, email, linkedin_url }; }))} />
                                  </>
                                )}
                                {widget.component === 'timeline' && (
                                  <>
                                    <TextField size="small" label="Subheading" value={widget?.props?.subheading || ''} onChange={(e) => updatePageWidgetProps(idx, 'subheading', e.target.value)} />
                                    <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updatePageWidgetProps(idx, 'title', e.target.value)} />
                                    <TextField size="small" label="Description" value={widget?.props?.description || ''} onChange={(e) => updatePageWidgetProps(idx, 'description', e.target.value)} />
                                    <TextField select size="small" label="Style" value={widget?.props?.style || 'vertical'} onChange={(e) => updatePageWidgetProps(idx, 'style', e.target.value)}><MenuItem value="vertical">Vertical timeline</MenuItem><MenuItem value="cards">Milestone cards</MenuItem></TextField>
                                    <TextField multiline minRows={5} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Steps — Label | Title | Description" value={(widget?.props?.items || []).map((item) => [item.label,item.title,item.body].join(' | ')).join('\n')} onChange={(e) => updatePageWidgetProps(idx, 'items', e.target.value.split('\n').filter(Boolean).map((line) => { const [label,title,...bodyParts] = line.split('|'); return { label: String(label || '').trim(), title: String(title || '').trim(), body: bodyParts.join('|').trim() }; }))} />
                                  </>
                                )}
                                {widget.component === 'comparison' && (
                                  <>
                                    <TextField size="small" label="Subheading" value={widget?.props?.subheading || ''} onChange={(e) => updatePageWidgetProps(idx, 'subheading', e.target.value)} />
                                    <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updatePageWidgetProps(idx, 'title', e.target.value)} />
                                    <TextField size="small" label="Description" value={widget?.props?.description || ''} onChange={(e) => updatePageWidgetProps(idx, 'description', e.target.value)} />
                                    <TextField size="small" label="First column label" value={widget?.props?.first_column_label || 'Feature'} onChange={(e) => updatePageWidgetProps(idx, 'first_column_label', e.target.value)} />
                                    <TextField multiline minRows={3} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Columns — Key | Label | Highlight (yes/no)" value={(widget?.props?.columns || []).map((col) => [col.key,col.label,col.highlight ? 'yes' : 'no'].join(' | ')).join('\n')} onChange={(e) => updatePageWidgetProps(idx, 'columns', e.target.value.split('\n').filter(Boolean).map((line, colIndex) => { const [keyRaw,label,highlight] = line.split('|').map((v) => v.trim()); const key = keyRaw || `option_${colIndex + 1}`; return { key: key.toLowerCase().replace(/[^a-z0-9]+/g,'_'), label: label || key, highlight: String(highlight || '').toLowerCase() === 'yes' }; }))} />
                                    <TextField multiline minRows={5} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Rows — Label | value 1 | value 2 | value 3..." helperText="Values follow the column order above." value={(widget?.props?.rows || []).map((row) => [row.label, ...(widget?.props?.columns || []).map((col) => row[col.key] || '')].join(' | ')).join('\n')} onChange={(e) => { const columns = widget?.props?.columns || []; updatePageWidgetProps(idx, 'rows', e.target.value.split('\n').filter(Boolean).map((line) => { const parts = line.split('|').map((v) => v.trim()); const row = { label: parts.shift() || '' }; columns.forEach((col, colIndex) => { row[col.key] = parts[colIndex] || ''; }); return row; })); }} />
                                    <TextField size="small" label="CTA label" value={widget?.props?.cta_label || ''} onChange={(e) => updatePageWidgetProps(idx, 'cta_label', e.target.value)} />
                                    <TextField size="small" label="CTA URL" value={widget?.props?.cta_url || ''} onChange={(e) => updatePageWidgetProps(idx, 'cta_url', e.target.value)} />
                                  </>
                                )}
                                {widget.component === 'rich_text' && (
                                              <TextField size="small" label="Body" value={widget?.props?.body || ''} onChange={(e) => updateWidgetProps(slot, idx, 'body', e.target.value)} />
                                            )}
                                            {(widget.component === 'featured_categories' || widget.component === 'category_grid' || widget.component === 'brand_grid' || widget.component === 'theme_grid') && (
                                              <TextField size="small" label="Limit" value={widget?.props?.limit || ''} onChange={(e) => updateWidgetProps(slot, idx, 'limit', e.target.value)} />
                                            )}
                                            {widget.component === 'product_grid' && (
                                              <TextField size="small" label="Page size" value={widget?.props?.page_size || ''} onChange={(e) => updateWidgetProps(slot, idx, 'page_size', e.target.value)} />
                                            )}
                                            {widget.component === 'contact_block' && (
                                              <TextField size="small" label="CTA text" value={widget?.props?.cta || ''} onChange={(e) => updateWidgetProps(slot, idx, 'cta', e.target.value)} />
                                            )}
                                            {widget.component === 'cards_grid' && (
                                              <>
                                                <TextField size="small" label="Subheading" value={widget?.props?.subheading || widget?.props?.eyebrow || ''} onChange={(e) => updateWidgetProps(slot, idx, 'subheading', e.target.value)} />
                                                <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updateWidgetProps(slot, idx, 'title', e.target.value)} />
                                                <TextField size="small" label="Section description" value={widget?.props?.description || ''} onChange={(e) => updateWidgetProps(slot, idx, 'description', e.target.value)} />
                                                <TextField select size="small" label="Use as" value={widget?.props?.variant || 'services'} onChange={(e) => updateWidgetProps(slot, idx, 'variant', e.target.value)}>
                                                  <MenuItem value="services">Services</MenuItem><MenuItem value="features">Features</MenuItem><MenuItem value="benefits">Benefits</MenuItem><MenuItem value="process">Process / Steps</MenuItem>
                                                </TextField>
                                                <TextField select size="small" label="Card style" value={widget?.props?.card_style || 'elevated'} onChange={(e) => updateWidgetProps(slot, idx, 'card_style', e.target.value)}>
                                                  <MenuItem value="elevated">Elevated</MenuItem><MenuItem value="outlined">Outlined</MenuItem><MenuItem value="minimal">Minimal</MenuItem><MenuItem value="filled">Filled</MenuItem>
                                                </TextField>
                                                <TextField size="small" type="number" label="Desktop columns" value={widget?.props?.columns_desktop ?? 3} inputProps={{ min: 1, max: 4 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_desktop', Number(e.target.value || 3))} />
                                                <TextField size="small" type="number" label="Tablet columns" value={widget?.props?.columns_tablet ?? 2} inputProps={{ min: 1, max: 3 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_tablet', Number(e.target.value || 2))} />
                                                <TextField size="small" type="number" label="Mobile columns" value={widget?.props?.columns_mobile ?? 1} inputProps={{ min: 1, max: 2 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_mobile', Number(e.target.value || 1))} />
                                                <TextField multiline minRows={5} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Cards — Icon | Title | Description | Link label | Link URL" value={(widget?.props?.items || []).map((item) => [item.icon,item.title,item.body,item.link_label,item.link_url].join(' | ')).join('\n')} onChange={(e) => updateWidgetProps(slot, idx, 'items', e.target.value.split('\n').filter(Boolean).map((line, itemIndex) => { const [icon,title,body,link_label,link_url] = line.split('|').map((v) => v.trim()); return { icon: icon || String(itemIndex + 1).padStart(2,'0'), title: title || 'Card title', body: body || '', link_label: link_label || '', link_url: link_url || '' }; }))} />
                                              </>
                                            )}
                                            {widget.component === 'stats' && (
                                              <>
                                                <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updateWidgetProps(slot, idx, 'title', e.target.value)} />
                                                <TextField size="small" type="number" label="Desktop columns" value={widget?.props?.columns_desktop ?? 4} inputProps={{ min: 1, max: 6 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_desktop', Number(e.target.value || 4))} />
                                                <TextField size="small" type="number" label="Tablet columns" value={widget?.props?.columns_tablet ?? 2} inputProps={{ min: 1, max: 4 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_tablet', Number(e.target.value || 2))} />
                                                <TextField size="small" type="number" label="Mobile columns" value={widget?.props?.columns_mobile ?? 2} inputProps={{ min: 1, max: 2 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_mobile', Number(e.target.value || 2))} />
                                                <TextField multiline minRows={4} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Stats — Value | Label" value={(widget?.props?.items || []).map((item) => [item.value,item.label].join(' | ')).join('\n')} onChange={(e) => updateWidgetProps(slot, idx, 'items', e.target.value.split('\n').filter(Boolean).map((line) => { const [value,label] = line.split('|').map((v) => v.trim()); return { value, label }; }))} />
                                              </>
                                            )}
                                            {widget.component === 'testimonials' && (
                                              <>
                                                <TextField size="small" label="Subheading" value={widget?.props?.subheading || widget?.props?.eyebrow || ''} onChange={(e) => updateWidgetProps(slot, idx, 'subheading', e.target.value)} />
                                                <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updateWidgetProps(slot, idx, 'title', e.target.value)} />
                                                <TextField size="small" type="number" label="Desktop columns" value={widget?.props?.columns_desktop ?? 3} inputProps={{ min: 1, max: 3 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_desktop', Number(e.target.value || 3))} />
                                                <TextField size="small" type="number" label="Tablet columns" value={widget?.props?.columns_tablet ?? 2} inputProps={{ min: 1, max: 3 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_tablet', Number(e.target.value || 2))} />
                                                <TextField size="small" type="number" label="Mobile columns" value={widget?.props?.columns_mobile ?? 1} inputProps={{ min: 1, max: 2 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_mobile', Number(e.target.value || 1))} />
                                                <TextField multiline minRows={5} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Testimonials — Quote | Name | Role/company | Image URL | Rating" value={(widget?.props?.items || []).map((item) => [item.quote,item.name,item.role,item.image_url,item.rating || 5].join(' | ')).join('\n')} onChange={(e) => updateWidgetProps(slot, idx, 'items', e.target.value.split('\n').filter(Boolean).map((line) => { const [quote,name,role,image_url,rating] = line.split('|').map((v) => v.trim()); return { quote, name, role, image_url, rating: Math.max(0, Math.min(5, Number(rating || 5))) }; }))} />
                                              </>
                                            )}
                                            {widget.component === 'faq' && (
                                              <>
                                                <TextField size="small" label="Subheading" value={widget?.props?.subheading || widget?.props?.eyebrow || ''} onChange={(e) => updateWidgetProps(slot, idx, 'subheading', e.target.value)} />
                                                <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updateWidgetProps(slot, idx, 'title', e.target.value)} />
                                                <TextField select size="small" label="Style" value={widget?.props?.style || 'joined'} onChange={(e) => updateWidgetProps(slot, idx, 'style', e.target.value)}><MenuItem value="joined">Joined</MenuItem><MenuItem value="cards">Cards</MenuItem><MenuItem value="minimal">Minimal</MenuItem></TextField>
                                                <FormControlLabel control={<Switch checked={widget?.props?.open_first !== false} onChange={(e) => updateWidgetProps(slot, idx, 'open_first', e.target.checked)} />} label="Open first" />
                                                <FormControlLabel control={<Switch checked={!!widget?.props?.allow_multiple} onChange={(e) => updateWidgetProps(slot, idx, 'allow_multiple', e.target.checked)} />} label="Allow multiple open" />
                                                <TextField multiline minRows={5} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="FAQs — Question | Answer" value={(widget?.props?.items || []).map((item) => [item.question,item.answer].join(' | ')).join('\n')} onChange={(e) => updateWidgetProps(slot, idx, 'items', e.target.value.split('\n').filter(Boolean).map((line) => { const [question,...answerParts] = line.split('|'); return { question: String(question || '').trim(), answer: answerParts.join('|').trim() }; }))} />
                                              </>
                                            )}
                                            {widget.component === 'logo_cloud' && (
                                              <>
                                                <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updateWidgetProps(slot, idx, 'title', e.target.value)} />
                                                <TextField size="small" type="number" label="Desktop columns" value={widget?.props?.columns_desktop ?? 5} inputProps={{ min: 2, max: 8 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_desktop', Number(e.target.value || 5))} />
                                                <TextField size="small" type="number" label="Tablet columns" value={widget?.props?.columns_tablet ?? 3} inputProps={{ min: 1, max: 4 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_tablet', Number(e.target.value || 3))} />
                                                <TextField size="small" type="number" label="Mobile columns" value={widget?.props?.columns_mobile ?? 2} inputProps={{ min: 1, max: 2 }} onChange={(e) => updateWidgetProps(slot, idx, 'columns_mobile', Number(e.target.value || 2))} />
                                                <FormControlLabel control={<Switch checked={widget?.props?.grayscale !== false} onChange={(e) => updateWidgetProps(slot, idx, 'grayscale', e.target.checked)} />} label="Grayscale logos" />
                                                <TextField multiline minRows={4} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Logos — Name | Image URL | Link URL" value={(widget?.props?.items || []).map((item) => [item.name,item.image_url,item.link_url].join(' | ')).join('\n')} onChange={(e) => updateWidgetProps(slot, idx, 'items', e.target.value.split('\n').filter(Boolean).map((line) => { const [name,image_url,link_url] = line.split('|').map((v) => v.trim()); return { name, image_url, link_url }; }))} />
                                              </>
                                            )}
                                            {widget.component === 'cta' && (
                                              <>
                                                <TextField size="small" label="Subheading" value={widget?.props?.subheading || widget?.props?.eyebrow || ''} onChange={(e) => updateWidgetProps(slot, idx, 'subheading', e.target.value)} />
                                                <TextField size="small" label="Headline" value={widget?.props?.title || ''} onChange={(e) => updateWidgetProps(slot, idx, 'title', e.target.value)} />
                                                <TextField size="small" label="Supporting copy" value={widget?.props?.body || ''} onChange={(e) => updateWidgetProps(slot, idx, 'body', e.target.value)} />
                                                <TextField select size="small" label="Alignment" value={widget?.props?.align || 'center'} onChange={(e) => updateWidgetProps(slot, idx, 'align', e.target.value)}><MenuItem value="left">Left</MenuItem><MenuItem value="center">Center</MenuItem></TextField>
                                                <TextField select size="small" label="Style" value={widget?.props?.style || 'panel'} onChange={(e) => updateWidgetProps(slot, idx, 'style', e.target.value)}><MenuItem value="panel">Panel</MenuItem><MenuItem value="minimal">Minimal</MenuItem></TextField>
                                                <TextField size="small" label="Primary button" value={widget?.props?.primary_label || ''} onChange={(e) => updateWidgetProps(slot, idx, 'primary_label', e.target.value)} />
                                                <TextField size="small" label="Primary URL" value={widget?.props?.primary_url || ''} onChange={(e) => updateWidgetProps(slot, idx, 'primary_url', e.target.value)} />
                                                <TextField size="small" label="Secondary button" value={widget?.props?.secondary_label || ''} onChange={(e) => updateWidgetProps(slot, idx, 'secondary_label', e.target.value)} />
                                                <TextField size="small" label="Secondary URL" value={widget?.props?.secondary_url || ''} onChange={(e) => updateWidgetProps(slot, idx, 'secondary_url', e.target.value)} />
                                              </>
                                            )}
                                            {widget.component === 'cards_grid' && (
                                  <>
                                    <TextField size="small" label="Subheading" value={widget?.props?.subheading || widget?.props?.eyebrow || ''} onChange={(e) => updatePageWidgetProps(idx, 'subheading', e.target.value)} />
                                    <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updatePageWidgetProps(idx, 'title', e.target.value)} />
                                    <TextField select size="small" label="Use as" value={widget?.props?.variant || 'services'} onChange={(e) => updatePageWidgetProps(idx, 'variant', e.target.value)}>
                                      <MenuItem value="services">Services</MenuItem><MenuItem value="features">Features</MenuItem><MenuItem value="benefits">Benefits</MenuItem><MenuItem value="process">Process / Steps</MenuItem>
                                    </TextField>
                                    <TextField select size="small" label="Card style" value={widget?.props?.card_style || 'elevated'} onChange={(e) => updatePageWidgetProps(idx, 'card_style', e.target.value)}>
                                      <MenuItem value="elevated">Elevated</MenuItem><MenuItem value="outlined">Outlined</MenuItem><MenuItem value="minimal">Minimal</MenuItem><MenuItem value="filled">Filled</MenuItem>
                                    </TextField>
                                    <TextField size="small" type="number" label="Desktop columns" value={widget?.props?.columns_desktop ?? 3} inputProps={{ min: 1, max: 4 }} onChange={(e) => updatePageWidgetProps(idx, 'columns_desktop', Number(e.target.value || 3))} />
                                    <TextField size="small" type="number" label="Tablet columns" value={widget?.props?.columns_tablet ?? 2} inputProps={{ min: 1, max: 3 }} onChange={(e) => updatePageWidgetProps(idx, 'columns_tablet', Number(e.target.value || 2))} />
                                    <TextField size="small" type="number" label="Mobile columns" value={widget?.props?.columns_mobile ?? 1} inputProps={{ min: 1, max: 2 }} onChange={(e) => updatePageWidgetProps(idx, 'columns_mobile', Number(e.target.value || 1))} />
                                    <TextField multiline minRows={5} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Cards — Icon | Title | Description | Link label | Link URL" value={(widget?.props?.items || []).map((item) => [item.icon,item.title,item.body,item.link_label,item.link_url].join(' | ')).join('\n')} onChange={(e) => updatePageWidgetProps(idx, 'items', e.target.value.split('\n').filter(Boolean).map((line, itemIndex) => { const [icon,title,body,link_label,link_url] = line.split('|').map((v) => v.trim()); return { icon: icon || String(itemIndex + 1).padStart(2,'0'), title: title || 'Card title', body: body || '', link_label: link_label || '', link_url: link_url || '' }; }))} />
                                  </>
                                )}
                                {widget.component === 'stats' && (
                                  <>
                                    <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updatePageWidgetProps(idx, 'title', e.target.value)} />
                                    <TextField size="small" type="number" label="Desktop columns" value={widget?.props?.columns_desktop ?? 4} inputProps={{ min: 1, max: 6 }} onChange={(e) => updatePageWidgetProps(idx, 'columns_desktop', Number(e.target.value || 4))} />
                                    <TextField multiline minRows={4} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Stats — Value | Label" value={(widget?.props?.items || []).map((item) => [item.value,item.label].join(' | ')).join('\n')} onChange={(e) => updatePageWidgetProps(idx, 'items', e.target.value.split('\n').filter(Boolean).map((line) => { const [value,label] = line.split('|').map((v) => v.trim()); return { value, label }; }))} />
                                  </>
                                )}
                                {widget.component === 'testimonials' && (
                                  <>
                                    <TextField size="small" label="Subheading" value={widget?.props?.subheading || widget?.props?.eyebrow || ''} onChange={(e) => updatePageWidgetProps(idx, 'subheading', e.target.value)} />
                                    <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updatePageWidgetProps(idx, 'title', e.target.value)} />
                                    <TextField size="small" type="number" label="Desktop columns" value={widget?.props?.columns_desktop ?? 3} inputProps={{ min: 1, max: 3 }} onChange={(e) => updatePageWidgetProps(idx, 'columns_desktop', Number(e.target.value || 3))} />
                                    <TextField multiline minRows={5} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Testimonials — Quote | Name | Role/company | Image URL | Rating" value={(widget?.props?.items || []).map((item) => [item.quote,item.name,item.role,item.image_url,item.rating || 5].join(' | ')).join('\n')} onChange={(e) => updatePageWidgetProps(idx, 'items', e.target.value.split('\n').filter(Boolean).map((line) => { const [quote,name,role,image_url,rating] = line.split('|').map((v) => v.trim()); return { quote, name, role, image_url, rating: Math.max(0, Math.min(5, Number(rating || 5))) }; }))} />
                                  </>
                                )}
                                {widget.component === 'faq' && (
                                  <>
                                    <TextField size="small" label="Subheading" value={widget?.props?.subheading || widget?.props?.eyebrow || ''} onChange={(e) => updatePageWidgetProps(idx, 'subheading', e.target.value)} />
                                    <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updatePageWidgetProps(idx, 'title', e.target.value)} />
                                    <TextField select size="small" label="Style" value={widget?.props?.style || 'joined'} onChange={(e) => updatePageWidgetProps(idx, 'style', e.target.value)}><MenuItem value="joined">Joined</MenuItem><MenuItem value="cards">Cards</MenuItem><MenuItem value="minimal">Minimal</MenuItem></TextField>
                                    <FormControlLabel control={<Switch checked={widget?.props?.open_first !== false} onChange={(e) => updatePageWidgetProps(idx, 'open_first', e.target.checked)} />} label="Open first" />
                                    <FormControlLabel control={<Switch checked={!!widget?.props?.allow_multiple} onChange={(e) => updatePageWidgetProps(idx, 'allow_multiple', e.target.checked)} />} label="Allow multiple open" />
                                    <TextField multiline minRows={5} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="FAQs — Question | Answer" value={(widget?.props?.items || []).map((item) => [item.question,item.answer].join(' | ')).join('\n')} onChange={(e) => updatePageWidgetProps(idx, 'items', e.target.value.split('\n').filter(Boolean).map((line) => { const [question,...answerParts] = line.split('|'); return { question: String(question || '').trim(), answer: answerParts.join('|').trim() }; }))} />
                                  </>
                                )}
                                {widget.component === 'logo_cloud' && (
                                  <>
                                    <TextField size="small" label="Section title" value={widget?.props?.title || ''} onChange={(e) => updatePageWidgetProps(idx, 'title', e.target.value)} />
                                    <TextField size="small" type="number" label="Desktop columns" value={widget?.props?.columns_desktop ?? 5} inputProps={{ min: 2, max: 8 }} onChange={(e) => updatePageWidgetProps(idx, 'columns_desktop', Number(e.target.value || 5))} />
                                    <FormControlLabel control={<Switch checked={widget?.props?.grayscale !== false} onChange={(e) => updatePageWidgetProps(idx, 'grayscale', e.target.checked)} />} label="Grayscale logos" />
                                    <TextField multiline minRows={4} size="small" sx={{ gridColumn: { md: '1 / -1' } }} label="Logos — Name | Image URL | Link URL" value={(widget?.props?.items || []).map((item) => [item.name,item.image_url,item.link_url].join(' | ')).join('\n')} onChange={(e) => updatePageWidgetProps(idx, 'items', e.target.value.split('\n').filter(Boolean).map((line) => { const [name,image_url,link_url] = line.split('|').map((v) => v.trim()); return { name, image_url, link_url }; }))} />
                                  </>
                                )}
                                {widget.component === 'cta' && (
                                  <>
                                    <TextField size="small" label="Subheading" value={widget?.props?.subheading || widget?.props?.eyebrow || ''} onChange={(e) => updatePageWidgetProps(idx, 'subheading', e.target.value)} />
                                    <TextField size="small" label="Headline" value={widget?.props?.title || ''} onChange={(e) => updatePageWidgetProps(idx, 'title', e.target.value)} />
                                    <TextField size="small" label="Primary button" value={widget?.props?.primary_label || ''} onChange={(e) => updatePageWidgetProps(idx, 'primary_label', e.target.value)} />
                                    <TextField size="small" label="Primary URL" value={widget?.props?.primary_url || ''} onChange={(e) => updatePageWidgetProps(idx, 'primary_url', e.target.value)} />
                                    <TextField size="small" label="Secondary button" value={widget?.props?.secondary_label || ''} onChange={(e) => updatePageWidgetProps(idx, 'secondary_label', e.target.value)} />
                                    <TextField size="small" label="Secondary URL" value={widget?.props?.secondary_url || ''} onChange={(e) => updatePageWidgetProps(idx, 'secondary_url', e.target.value)} />
                                  </>
                                )}
                                {widget.component === 'rich_text' && (
                                              <>
                                                <TextField size="small" label="Subheading" value={widget?.props?.subheading || widget?.props?.eyebrow || ''} onChange={(e) => updateWidgetProps(slot, idx, 'subheading', e.target.value)} />
                                                <TextField size="small" label="Button label (optional)" value={widget?.props?.cta_label || ''} onChange={(e) => updateWidgetProps(slot, idx, 'cta_label', e.target.value)} />
                                                <TextField size="small" label="Button URL" value={widget?.props?.cta_url || ''} onChange={(e) => updateWidgetProps(slot, idx, 'cta_url', e.target.value)} />
                                                <TextField size="small" label="Image URL (optional)" value={widget?.props?.image_url || ''} onChange={(e) => updateWidgetProps(slot, idx, 'image_url', e.target.value)} />
                                                <TextField size="small" label="Image alt text" value={widget?.props?.image_alt || ''} onChange={(e) => updateWidgetProps(slot, idx, 'image_alt', e.target.value)} />
                                                <TextField select size="small" label="Image position" value={widget?.props?.media_position || 'right'} onChange={(e) => updateWidgetProps(slot, idx, 'media_position', e.target.value)}>
                                                  <MenuItem value="right">Right</MenuItem>
                                                  <MenuItem value="left">Left</MenuItem>
                                                </TextField>
                                                <TextField select size="small" label="Vertical align" value={widget?.props?.vertical_align || 'center'} onChange={(e) => updateWidgetProps(slot, idx, 'vertical_align', e.target.value)}>
                                                  <MenuItem value="start">Top</MenuItem>
                                                  <MenuItem value="center">Center</MenuItem>
                                                  <MenuItem value="end">Bottom</MenuItem>
                                                </TextField>
                                                <TextField size="small" type="number" label="Content width %" value={widget?.props?.content_width ?? 50} inputProps={{ min: 30, max: 70 }} onChange={(e) => updateWidgetProps(slot, idx, 'content_width', Number(e.target.value || 50))} />
                                                <TextField size="small" type="number" label="Column gap" value={widget?.props?.gap ?? 48} inputProps={{ min: 0, max: 160 }} onChange={(e) => updateWidgetProps(slot, idx, 'gap', Number(e.target.value || 0))} />
                                                <TextField size="small" type="number" label="Image radius" value={widget?.props?.image_radius ?? 18} inputProps={{ min: 0, max: 100 }} onChange={(e) => updateWidgetProps(slot, idx, 'image_radius', Number(e.target.value || 0))} />
                                                <TextField select size="small" label="Image shadow" value={widget?.props?.image_shadow || 'medium'} onChange={(e) => updateWidgetProps(slot, idx, 'image_shadow', e.target.value)}>
                                                  <MenuItem value="none">None</MenuItem>
                                                  <MenuItem value="soft">Soft</MenuItem>
                                                  <MenuItem value="medium">Medium</MenuItem>
                                                  <MenuItem value="strong">Strong</MenuItem>
                                                </TextField>
                                                <TextField select size="small" label="Image ratio" value={widget?.props?.image_aspect || '4/3'} onChange={(e) => updateWidgetProps(slot, idx, 'image_aspect', e.target.value)}>
                                                  <MenuItem value="1/1">Square</MenuItem>
                                                  <MenuItem value="4/3">4:3</MenuItem>
                                                  <MenuItem value="16/9">16:9</MenuItem>
                                                  <MenuItem value="3/4">Portrait</MenuItem>
                                                </TextField>
                                              </>
                                            )}
                                            {widget.component === 'rich_text' && (
                                  <>
                                    <TextField size="small" label="Subheading" value={widget?.props?.subheading || widget?.props?.eyebrow || ''} onChange={(e) => updatePageWidgetProps(idx, 'subheading', e.target.value)} />
                                    <TextField size="small" label="Button label (optional)" value={widget?.props?.cta_label || ''} onChange={(e) => updatePageWidgetProps(idx, 'cta_label', e.target.value)} />
                                    <TextField size="small" label="Button URL" value={widget?.props?.cta_url || ''} onChange={(e) => updatePageWidgetProps(idx, 'cta_url', e.target.value)} />
                                    <TextField size="small" label="Image URL (optional)" value={widget?.props?.image_url || ''} onChange={(e) => updatePageWidgetProps(idx, 'image_url', e.target.value)} />
                                    <TextField size="small" label="Image alt text" value={widget?.props?.image_alt || ''} onChange={(e) => updatePageWidgetProps(idx, 'image_alt', e.target.value)} />
                                    <TextField select size="small" label="Image position" value={widget?.props?.media_position || 'right'} onChange={(e) => updatePageWidgetProps(idx, 'media_position', e.target.value)}>
                                      <MenuItem value="right">Right</MenuItem>
                                      <MenuItem value="left">Left</MenuItem>
                                    </TextField>
                                    <TextField select size="small" label="Vertical align" value={widget?.props?.vertical_align || 'center'} onChange={(e) => updatePageWidgetProps(idx, 'vertical_align', e.target.value)}>
                                      <MenuItem value="start">Top</MenuItem>
                                      <MenuItem value="center">Center</MenuItem>
                                      <MenuItem value="end">Bottom</MenuItem>
                                    </TextField>
                                    <TextField size="small" type="number" label="Content width %" value={widget?.props?.content_width ?? 50} inputProps={{ min: 30, max: 70 }} onChange={(e) => updatePageWidgetProps(idx, 'content_width', Number(e.target.value || 50))} />
                                    <TextField size="small" type="number" label="Column gap" value={widget?.props?.gap ?? 48} inputProps={{ min: 0, max: 160 }} onChange={(e) => updatePageWidgetProps(idx, 'gap', Number(e.target.value || 0))} />
                                    <TextField size="small" type="number" label="Image radius" value={widget?.props?.image_radius ?? 18} inputProps={{ min: 0, max: 100 }} onChange={(e) => updatePageWidgetProps(idx, 'image_radius', Number(e.target.value || 0))} />
                                    <TextField select size="small" label="Image shadow" value={widget?.props?.image_shadow || 'medium'} onChange={(e) => updatePageWidgetProps(idx, 'image_shadow', e.target.value)}>
                                      <MenuItem value="none">None</MenuItem>
                                      <MenuItem value="soft">Soft</MenuItem>
                                      <MenuItem value="medium">Medium</MenuItem>
                                      <MenuItem value="strong">Strong</MenuItem>
                                    </TextField>
                                    <TextField select size="small" label="Image ratio" value={widget?.props?.image_aspect || '4/3'} onChange={(e) => updatePageWidgetProps(idx, 'image_aspect', e.target.value)}>
                                      <MenuItem value="1/1">Square</MenuItem>
                                      <MenuItem value="4/3">4:3</MenuItem>
                                      <MenuItem value="16/9">16:9</MenuItem>
                                      <MenuItem value="3/4">Portrait</MenuItem>
                                    </TextField>
                                  </>
                                )}
                                {widget.component === 'form' && (
                                              <>
                                                <TextField size="small" label="Form title" value={widget?.props?.title || ''} onChange={(e) => updateWidgetProps(slot, idx, 'title', e.target.value)} />
                                                <TextField size="small" label="Submit button" value={widget?.props?.submit_label || ''} onChange={(e) => updateWidgetProps(slot, idx, 'submit_label', e.target.value)} />
                                                <TextField size="small" label="From email" helperText="Defaults to Elite Core's main sender address." value={widget?.props?.email_from || 'info@elitecore.ca'} onChange={(e) => updateWidgetProps(slot, idx, 'email_from', e.target.value)} />
                                                <TextField size="small" label="From name" value={widget?.props?.email_from_name || 'Elite Core'} onChange={(e) => updateWidgetProps(slot, idx, 'email_from_name', e.target.value)} />
                                                <TextField size="small" label="To" helperText="Comma-separated email addresses. Leave blank for Core-only storage + notifications." value={widget?.props?.email_to || ''} onChange={(e) => updateWidgetProps(slot, idx, 'email_to', e.target.value)} />
                                                <TextField size="small" label="CC" helperText="Comma-separated email addresses." value={widget?.props?.email_cc || ''} onChange={(e) => updateWidgetProps(slot, idx, 'email_cc', e.target.value)} />
                                                <TextField size="small" label="BCC" helperText="Comma-separated email addresses." value={widget?.props?.email_bcc || ''} onChange={(e) => updateWidgetProps(slot, idx, 'email_bcc', e.target.value)} />
                                                <TextField
                                                  size="small"
                                                  multiline
                                                  minRows={4}
                                                  sx={{ gridColumn: { md: '1 / -1' } }}
                                                  label="Fields — Label | type | required | options"
                                                  helperText="Types: text, email, tel, textarea, select, checkbox. For select options use commas."
                                                  value={(widget?.props?.fields || []).map((field) => [field.label, field.type, field.required ? 'required' : '', Array.isArray(field.options) ? field.options.join(',') : ''].join(' | ')).join('\n')}
                                                  onChange={(e) => updateWidgetProps(slot, idx, 'fields', e.target.value.split('\n').map((line, lineIndex) => {
                                                    const [labelRaw, typeRaw, requiredRaw, optionsRaw] = line.split('|').map((part) => part.trim());
                                                    const label = labelRaw || `Field ${lineIndex + 1}`;
                                                    return {
                                                      key: String(label).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || `field_${lineIndex + 1}`,
                                                      label,
                                                      type: ['text','email','tel','textarea','select','checkbox'].includes(typeRaw) ? typeRaw : 'text',
                                                      required: String(requiredRaw || '').toLowerCase() === 'required',
                                                      options: optionsRaw ? optionsRaw.split(',').map((option) => option.trim()).filter(Boolean) : [],
                                                    };
                                                  }).filter((field) => field.label))}
                                                />
                                              </>
                                            )}
                                          </Box>

                                          <WidgetStyleControls
                                            props={widget?.props}
                                            onChange={(key, value) => updateWidgetProps(slot, idx, key, value)}
                                          />

                                          {(widget.component === 'category_grid' || widget.component === 'brand_grid' || widget.component === 'theme_grid') && (
                                            <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' }, gap: 0.75 }}>
                                              <TextField
                                                size="small"
                                                label="Heading"
                                                value={widget?.props?.heading || widget?.props?.title || ''}
                                                onChange={(e) => updateWidgetProps(slot, idx, 'heading', e.target.value)}
                                              />
                                              <TextField
                                                select
                                                size="small"
                                                label="Taxonomy"
                                                value={widget?.props?.taxonomy || 'product_category'}
                                                onChange={(e) => updateWidgetProps(slot, idx, 'taxonomy', e.target.value)}
                                              >
                                                <MenuItem value="product_category">Product Category</MenuItem>
                                                <MenuItem value="brand">Brand</MenuItem>
                                                <MenuItem value="theme">Theme</MenuItem>
                                                <MenuItem value="collection">Collection</MenuItem>
                                              </TextField>
                                              <TextField
                                                size="small"
                                                type="number"
                                                label="Desktop cols"
                                                value={widget?.props?.columns_desktop ?? 8}
                                                onChange={(e) => updateWidgetProps(slot, idx, 'columns_desktop', Number(e.target.value || 8))}
                                              />
                                              <TextField
                                                size="small"
                                                type="number"
                                                label="Tablet cols"
                                                value={widget?.props?.columns_tablet ?? 4}
                                                onChange={(e) => updateWidgetProps(slot, idx, 'columns_tablet', Number(e.target.value || 4))}
                                              />
                                              <TextField
                                                size="small"
                                                type="number"
                                                label="Mobile cols"
                                                value={widget?.props?.columns_mobile ?? 2}
                                                onChange={(e) => updateWidgetProps(slot, idx, 'columns_mobile', Number(e.target.value || 2))}
                                              />
                                            </Box>
                                          )}

                                          {widget.component === 'hero' && (
                                            <Box sx={{ mt: 1, p: 1, borderRadius: 1.5, border: `1px solid ${BRAND.line}`, background: 'rgba(255,255,255,0.9)' }}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                                                <Typography variant="caption" sx={{ color: BRAND.slate, fontWeight: 800 }}>
                                                  Hero slides ({Array.isArray(widget?.props?.slides) ? widget.props.slides.length : 0}/10)
                                                </Typography>
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  sx={styles.outlineButton}
                                                  onClick={() => addHeroSlide(slot, idx)}
                                                  disabled={Array.isArray(widget?.props?.slides) && widget.props.slides.length >= 10}
                                                >
                                                  Add Slide
                                                </Button>
                                              </Box>

                                              <Box sx={{ display: 'grid', gap: 0.75 }}>
                                                {(Array.isArray(widget?.props?.slides) ? widget.props.slides : []).map((slide, slideIndex) => (
                                                  <Box key={`hero-slide-${slot}-${idx}-${slideIndex}`} sx={{ p: 0.75, borderRadius: 1.25, border: `1px dashed ${BRAND.line}` }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                      <Typography variant="caption" sx={{ color: BRAND.ink, fontWeight: 700 }}>Slide {slideIndex + 1}</Typography>
                                                      <Button size="small" color="error" onClick={() => removeHeroSlide(slot, idx, slideIndex)}>Remove</Button>
                                                    </Box>
                                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.75 }}>
                                                      <TextField size="small" label="Background URL" value={slide?.background_url || ''} onChange={(e) => updateHeroSlide(slot, idx, slideIndex, 'background_url', e.target.value)} />
                                                      <TextField size="small" label="H1" value={slide?.h1 || ''} onChange={(e) => updateHeroSlide(slot, idx, slideIndex, 'h1', e.target.value)} />
                                                      <TextField size="small" label="Paragraph" value={slide?.paragraph || ''} onChange={(e) => updateHeroSlide(slot, idx, slideIndex, 'paragraph', e.target.value)} />
                                                      <TextField size="small" label="Button text" value={slide?.button_text || ''} onChange={(e) => updateHeroSlide(slot, idx, slideIndex, 'button_text', e.target.value)} />
                                                      <TextField size="small" label="Button URL" value={slide?.button_url || ''} onChange={(e) => updateHeroSlide(slot, idx, slideIndex, 'button_url', e.target.value)} />
                                                    </Box>
                                                  </Box>
                                                ))}
                                              </Box>
                                            </Box>
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              )})}
                            </Box>
                          </Box>

                          <Box sx={{ p: 1.5, border: `1px solid ${BRAND.line}`, borderRadius: 2, background: 'rgba(255,255,255,0.92)' }}>
                            <Typography sx={{ fontWeight: 800, color: BRAND.ink, mb: 0.75 }}>Live Preview ({previewSlot})</Typography>
                            <Box sx={{ display: 'grid', gap: 0.75 }}>
                              {(layoutBuilder?.slots?.[previewSlot] || []).length === 0 ? (
                                <Typography variant="caption" sx={{ color: BRAND.slate }}>No widgets in this slot yet.</Typography>
                              ) : (layoutBuilder?.slots?.[previewSlot] || []).map((widget, idx) => renderWidgetPreviewCard(widget, idx, previewSlot))}
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button variant="contained" sx={styles.primaryButton} onClick={applySelectedTemplate} disabled={templatesApplying || !selectedTemplateId}>
                              {templatesApplying ? 'Applying...' : 'Publish Design'}
                            </Button>
                            <Button variant="outlined" sx={styles.outlineButton} onClick={savePrivateTemplate} disabled={templatesSaving}>
                              {templatesSaving ? 'Saving...' : 'Save as Private Design'}
                            </Button>
                            <Button variant="outlined" sx={styles.outlineButton} onClick={updateSelectedPrivateTemplate} disabled={templatesSaving || !selectedTemplate || selectedTemplate.visibility !== 'private'}>
                              {templatesSaving ? 'Saving...' : 'Update Selected Design'}
                            </Button>
                            <Button variant="outlined" sx={styles.outlineButton} onClick={loadTemplates}>Refresh Designs</Button>
                          </Box>
                        </CardContent>
                      </Card>
                    )}

                  </Box>
                </Box>
              )}


              <Dialog
                open={pageEditorOpen}
                onClose={() => setPageEditorOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                  sx: {
                    width: '95vw',
                    maxWidth: '95vw',
                    height: '95vh',
                    maxHeight: '95vh',
                    m: '2.5vh auto',
                  },
                }}
              >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 800, color: BRAND.ink }}>
                    Page Layout Builder {editingPage ? `• ${editingPage?.title?.rendered || editingPage?.title || editingPage?.slug || ''}` : ''}
                  </Typography>
                  <Tabs
                    value={pageEditorViewMode}
                    onChange={(_, value) => setPageEditorViewMode(value)}
                    textColor="inherit"
                    indicatorColor="primary"
                    sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 700 } }}
                  >
                    <Tab label="Builder mode" value="builder" />
                    <Tab label="Live Preview" value="preview" />
                    {canViewPageEditorPayload ? <Tab label="Payload" value="payload" /> : null}
                  </Tabs>
                </DialogTitle>
                <DialogContent dividers>
                  {pageEditorViewMode === 'builder' ? (
                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                      <Typography variant="body2" sx={{ color: BRAND.slate }}>
                        Edit widgets for this page slot, then sync only this page to WordPress.
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' }, gap: 1, alignItems: 'center' }}>
                        <TextField select size="small" label="Slot" value={pageEditorSlot} onChange={(e) => setPageEditorSlot(e.target.value)}>
                          {builderSlots.map((slot) => (
                            <MenuItem key={`page-editor-slot-${slot}`} value={slot}>{selectedBuilderPage?.slot === slot ? selectedBuilderPage.title : (SLOT_META[slot]?.label || slot)}</MenuItem>
                          ))}
                        </TextField>
                        <TextField select size="small" label="Widget" value={pageWidgetDraftType} onChange={(e) => setPageWidgetDraftType(e.target.value)}>
                          {WIDGET_LIBRARY.map((widget) => (
                            <MenuItem key={`page-editor-widget-${widget.key}`} value={widget.key}>{widget.label}</MenuItem>
                          ))}
                        </TextField>
                        <Button variant="outlined" sx={styles.outlineButton} onClick={addPageWidget}>Add Widget</Button>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 0.75 }}>
                        {WIDGET_LIBRARY.map((widget) => {
                          const meta = getWidgetMeta(widget.key);
                          const Icon = meta.icon;
                          const selected = pageWidgetDraftType === widget.key;
                          return (
                            <Box
                              key={`page-editor-widget-palette-${widget.key}`}
                              onClick={() => setPageWidgetDraftType(widget.key)}
                              sx={{
                                p: 1,
                                borderRadius: 1.5,
                                border: selected ? `1px solid ${BRAND.red}` : `1px solid ${BRAND.line}`,
                                background: selected ? 'rgba(195,23,29,0.08)' : 'rgba(255,255,255,0.95)',
                                cursor: 'pointer',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                <Icon sx={{ fontSize: 16, color: meta.accent }} />
                                <Typography sx={{ fontWeight: 800, color: BRAND.ink, fontSize: 13 }}>{widget.label}</Typography>
                              </Box>
                              <Typography variant="caption" sx={{ color: BRAND.slate }}>
                                {WIDGET_DETAILS[widget.key] || 'Composable widget block.'}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>

                      <Box sx={{ display: 'grid', gap: 0.75 }}>
                        {(pageEditorLayout?.slots?.[pageEditorSlot] || []).length === 0 ? (
                          <Typography variant="caption" sx={{ color: BRAND.slate }}>No widgets in this slot yet.</Typography>
                        ) : (pageEditorLayout?.slots?.[pageEditorSlot] || []).map((widget, idx) => {
                          const widgetMeta = getWidgetMeta(widget.component);
                          const WidgetIcon = widgetMeta.icon;
                          const presets = WIDGET_TEMPLATES[widget.component] || [];
                          return (
                            <Box key={`page-editor-${pageEditorSlot}-${idx}`} sx={{ p: 1, borderRadius: 1.5, background: 'rgba(248,250,252,0.95)', border: `1px dashed ${BRAND.line}` }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <WidgetIcon sx={{ fontSize: 16, color: widgetMeta.accent }} />
                                  <Typography sx={{ fontWeight: 700, color: BRAND.ink }}>{widget.component}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                  {presets.map((preset) => (
                                    <Button
                                      key={`page-preset-${pageEditorSlot}-${idx}-${preset.key}`}
                                      size="small"
                                      variant="outlined"
                                      sx={styles.outlineButton}
                                      onClick={() => applyPageWidgetTemplatePreset(idx, preset.props)}
                                    >
                                      {preset.label}
                                    </Button>
                                  ))}
                                  <IconButton size="small" onClick={() => movePageWidget(idx, 'up')}><ArrowUpwardRoundedIcon fontSize="inherit" /></IconButton>
                                  <IconButton size="small" onClick={() => movePageWidget(idx, 'down')}><ArrowDownwardRoundedIcon fontSize="inherit" /></IconButton>
                                  <IconButton size="small" color="error" onClick={() => removePageWidget(idx)}><DeleteOutlineRoundedIcon fontSize="inherit" /></IconButton>
                                </Box>
                              </Box>
                              <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.75 }}>
                                {widget.component === 'hero' && (
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Min height (px)"
                                    value={widget?.props?.min_height ?? 460}
                                    onChange={(e) => updatePageWidgetProps(idx, 'min_height', Number(e.target.value || 0))}
                                  />
                                )}
                                {(widget.component === 'hero' || widget.component === 'hero_split' || widget.component === 'rich_text' || widget.component === 'product_grid' || widget.component === 'featured_categories' || widget.component === 'category_grid' || widget.component === 'brand_grid' || widget.component === 'theme_grid') && (
                                  <TextField size="small" label="Title / Headline" value={widget?.props?.title || widget?.props?.headline || ''} onChange={(e) => updatePageWidgetProps(idx, 'title', e.target.value)} />
                                )}
                                {(widget.component === 'hero' || widget.component === 'hero_split' || widget.component === 'rich_text') && (
                                  <TextField size="small" label="Subtitle / Body" value={widget?.props?.subtitle || widget?.props?.body || ''} onChange={(e) => updatePageWidgetProps(idx, 'subtitle', e.target.value)} />
                                )}
                                {(widget.component === 'featured_categories' || widget.component === 'category_grid' || widget.component === 'brand_grid' || widget.component === 'theme_grid') && (
                                  <TextField size="small" label="Limit" value={widget?.props?.limit || ''} onChange={(e) => updatePageWidgetProps(idx, 'limit', e.target.value)} />
                                )}
                                {widget.component === 'product_grid' && (
                                  <TextField size="small" label="Page size" value={widget?.props?.page_size || ''} onChange={(e) => updatePageWidgetProps(idx, 'page_size', e.target.value)} />
                                )}
                                {widget.component === 'contact_block' && (
                                  <TextField size="small" label="CTA text" value={widget?.props?.cta || ''} onChange={(e) => updatePageWidgetProps(idx, 'cta', e.target.value)} />
                                )}
                                {widget.component === 'form' && (
                                  <>
                                    <TextField size="small" label="Form title" value={widget?.props?.title || ''} onChange={(e) => updatePageWidgetProps(idx, 'title', e.target.value)} />
                                    <TextField size="small" label="Submit button" value={widget?.props?.submit_label || ''} onChange={(e) => updatePageWidgetProps(idx, 'submit_label', e.target.value)} />
                                    <TextField size="small" label="From email" helperText="Defaults to Elite Core's main sender address." value={widget?.props?.email_from || 'info@elitecore.ca'} onChange={(e) => updatePageWidgetProps(idx, 'email_from', e.target.value)} />
                                    <TextField size="small" label="From name" value={widget?.props?.email_from_name || 'Elite Core'} onChange={(e) => updatePageWidgetProps(idx, 'email_from_name', e.target.value)} />
                                    <TextField size="small" label="To" helperText="Comma-separated email addresses. Leave blank for Core-only storage + notifications." value={widget?.props?.email_to || ''} onChange={(e) => updatePageWidgetProps(idx, 'email_to', e.target.value)} />
                                    <TextField size="small" label="CC" helperText="Comma-separated email addresses." value={widget?.props?.email_cc || ''} onChange={(e) => updatePageWidgetProps(idx, 'email_cc', e.target.value)} />
                                    <TextField size="small" label="BCC" helperText="Comma-separated email addresses." value={widget?.props?.email_bcc || ''} onChange={(e) => updatePageWidgetProps(idx, 'email_bcc', e.target.value)} />
                                    <TextField
                                      size="small"
                                      multiline
                                      minRows={4}
                                      sx={{ gridColumn: { md: '1 / -1' } }}
                                      label="Fields — Label | type | required | options"
                                      helperText="Types: text, email, tel, textarea, select, checkbox. For select options use commas."
                                      value={(widget?.props?.fields || []).map((field) => [field.label, field.type, field.required ? 'required' : '', Array.isArray(field.options) ? field.options.join(',') : ''].join(' | ')).join('\n')}
                                      onChange={(e) => updatePageWidgetProps(idx, 'fields', e.target.value.split('\n').map((line, lineIndex) => {
                                        const [labelRaw, typeRaw, requiredRaw, optionsRaw] = line.split('|').map((part) => part.trim());
                                        const label = labelRaw || `Field ${lineIndex + 1}`;
                                        return {
                                          key: String(label).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || `field_${lineIndex + 1}`,
                                          label,
                                          type: ['text','email','tel','textarea','select','checkbox'].includes(typeRaw) ? typeRaw : 'text',
                                          required: String(requiredRaw || '').toLowerCase() === 'required',
                                          options: optionsRaw ? optionsRaw.split(',').map((option) => option.trim()).filter(Boolean) : [],
                                        };
                                      }).filter((field) => field.label))}
                                    />
                                  </>
                                )}
                              </Box>

                              <WidgetStyleControls
                                props={widget?.props}
                                onChange={(key, value) => updatePageWidgetProps(idx, key, value)}
                              />

                              {(widget.component === 'category_grid' || widget.component === 'brand_grid' || widget.component === 'theme_grid') && (
                                <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' }, gap: 0.75 }}>
                                  <TextField
                                    size="small"
                                    label="Heading"
                                    value={widget?.props?.heading || widget?.props?.title || ''}
                                    onChange={(e) => updatePageWidgetProps(idx, 'heading', e.target.value)}
                                  />
                                  <TextField
                                    select
                                    size="small"
                                    label="Taxonomy"
                                    value={widget?.props?.taxonomy || 'product_category'}
                                    onChange={(e) => updatePageWidgetProps(idx, 'taxonomy', e.target.value)}
                                  >
                                    <MenuItem value="product_category">Product Category</MenuItem>
                                    <MenuItem value="brand">Brand</MenuItem>
                                    <MenuItem value="theme">Theme</MenuItem>
                                    <MenuItem value="collection">Collection</MenuItem>
                                  </TextField>
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Desktop cols"
                                    value={widget?.props?.columns_desktop ?? 8}
                                    onChange={(e) => updatePageWidgetProps(idx, 'columns_desktop', Number(e.target.value || 8))}
                                  />
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Tablet cols"
                                    value={widget?.props?.columns_tablet ?? 4}
                                    onChange={(e) => updatePageWidgetProps(idx, 'columns_tablet', Number(e.target.value || 4))}
                                  />
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Mobile cols"
                                    value={widget?.props?.columns_mobile ?? 2}
                                    onChange={(e) => updatePageWidgetProps(idx, 'columns_mobile', Number(e.target.value || 2))}
                                  />
                                </Box>
                              )}

                              {widget.component === 'hero' && (
                                <Box sx={{ mt: 1, p: 1, borderRadius: 1.5, border: `1px solid ${BRAND.line}`, background: 'rgba(255,255,255,0.9)' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                                    <Typography variant="caption" sx={{ color: BRAND.slate, fontWeight: 800 }}>
                                      Hero slides ({Array.isArray(widget?.props?.slides) ? widget.props.slides.length : 0}/10)
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      sx={styles.outlineButton}
                                      onClick={() => addPageHeroSlide(idx)}
                                      disabled={Array.isArray(widget?.props?.slides) && widget.props.slides.length >= 10}
                                    >
                                      Add Slide
                                    </Button>
                                  </Box>
                                  <Box sx={{ display: 'grid', gap: 0.75 }}>
                                    {(Array.isArray(widget?.props?.slides) ? widget.props.slides : []).map((slide, slideIndex) => (
                                      <Box key={`page-hero-slide-${pageEditorSlot}-${idx}-${slideIndex}`} sx={{ p: 0.75, borderRadius: 1.25, border: `1px dashed ${BRAND.line}` }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                          <Typography variant="caption" sx={{ color: BRAND.ink, fontWeight: 700 }}>Slide {slideIndex + 1}</Typography>
                                          <Button size="small" color="error" onClick={() => removePageHeroSlide(idx, slideIndex)}>Remove</Button>
                                        </Box>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.75 }}>
                                          <TextField size="small" label="Background URL" value={slide?.background_url || ''} onChange={(e) => updatePageHeroSlide(idx, slideIndex, 'background_url', e.target.value)} />
                                          <TextField size="small" label="H1" value={slide?.h1 || ''} onChange={(e) => updatePageHeroSlide(idx, slideIndex, 'h1', e.target.value)} />
                                          <TextField size="small" label="Paragraph" value={slide?.paragraph || ''} onChange={(e) => updatePageHeroSlide(idx, slideIndex, 'paragraph', e.target.value)} />
                                          <TextField size="small" label="Button text" value={slide?.button_text || ''} onChange={(e) => updatePageHeroSlide(idx, slideIndex, 'button_text', e.target.value)} />
                                          <TextField size="small" label="Button URL" value={slide?.button_url || ''} onChange={(e) => updatePageHeroSlide(idx, slideIndex, 'button_url', e.target.value)} />
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ) : pageEditorViewMode === 'preview' ? (
                    <Box sx={{ border: `1px solid ${BRAND.line}`, borderRadius: 2, overflow: 'hidden', background: "#fff" }}>
                      <Box sx={{ px: 1.25, py: 0.9, borderBottom: `1px solid ${BRAND.line}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Box>
                          <Typography sx={{ fontWeight: 800, color: BRAND.ink }}>Template Styles Live Preview</Typography>
                          <Typography variant="caption" sx={{ color: BRAND.slate }}>Live updates while typing (debounced). Hover/click a block to grab selector.</Typography>
                        </Box>
                        <TextField select size="small" label="Slot" value={pageEditorSlot} onChange={(e) => setPageEditorSlot(e.target.value)} sx={{ minWidth: 170 }}>
                          {['home', 'catalog', 'about', 'contact'].map((slot) => (
                            <MenuItem key={`page-editor-preview-slot-${slot}`} value={slot}>{slot}</MenuItem>
                          ))}
                        </TextField>
                      </Box>
                      <Box sx={{ height: { xs: 360, md: 560 }, background: "#f8fafc", position: 'relative' }}>
                        <iframe
                          title="Page editor template styles live preview"
                          sandbox="allow-same-origin allow-scripts"
                          srcDoc={pageEditorTemplateStylesPreviewSrcDoc}
                          style={{ width: "100%", height: "100%", border: 0, display: "block" }}
                        />
                        <Box sx={{ position: 'absolute', right: 10, bottom: 10, background: 'rgba(15,23,42,0.9)', color: '#fff', borderRadius: 1.5, px: 1, py: 0.75, maxWidth: '75%', boxShadow: '0 6px 18px rgba(2,6,23,0.35)' }}>
                          <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>Selector</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{previewSelectedSelector || previewHoveredSelector || '.promoshowcase-slot .ps-widget--hero'}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1, borderTop: `1px solid ${BRAND.line}`, background: 'rgba(248,250,252,0.95)' }}>
                        <Typography variant="caption" sx={{ color: BRAND.slate, flex: 1 }}>Click block in preview, then insert selector into Custom CSS.</Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: BRAND.slate }}>
                        Payload preview for page sync apply request (debug helper for WordPress/PHP mapping).
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1.25,
                          borderRadius: 2,
                          border: `1px solid ${BRAND.line}`,
                          background: '#0f172a',
                          color: '#e2e8f0',
                          fontSize: 12,
                          lineHeight: 1.5,
                          overflow: 'auto',
                          maxHeight: { xs: 360, md: 620 },
                        }}
                      >
                        {JSON.stringify(pageEditorApplyPayloadPreview, null, 2)}
                      </Box>
                    </Box>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button variant="text" onClick={() => setPageEditorOpen(false)}>Cancel</Button>
                  <Button variant="contained" sx={styles.primaryButton} onClick={syncEditedPageLayout} disabled={pageEditorSyncing || !editingPage}>
                    {pageEditorSyncing ? 'Syncing...' : 'Sync this page'}
                  </Button>
                </DialogActions>
              </Dialog>

              <Dialog
                open={requestDesignerOpen}
                onClose={() => setRequestDesignerOpen(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Request PromoShowcase designer</DialogTitle>
                <DialogContent dividers>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    You are about to notify the following designers:
                  </Typography>
                  {designerTeamUsers.length ? (
                    <List dense>
                      {designerTeamUsers.map((user) => (
                        <ListItem key={user.id}>
                          <ListItemText
                            primary={`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email}
                            secondary={user.email}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No PromoShowcase designers have been configured in Company Settings.
                    </Typography>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setRequestDesignerOpen(false)} disabled={requestingDesigner}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirmRequestDesigner}
                    disabled={requestingDesigner || designerTeamUsers.length === 0}
                  >
                    {requestingDesigner ? "Sending..." : "Confirm request"}
                  </Button>
                </DialogActions>
              </Dialog>

              <PromoShowcaseProductPicker
                open={showPromoWorldPicker}
                onClose={() => setShowPromoWorldPicker(false)}
                server={SERVER}
                headers={headers}
                instanceUniqueId={instanceUniqueId}
                currentSelections={storeProductSummary.selections || []}
                onSaved={async () => { acknowledgeMembershipChange(); await loadPromoShowcaseProductSummary(); }}
              />

              {showProductPopup && (
                <InstanceProducts
                  updateItemsToOrder={rejectShowcaseReplacementSave}
                  onAddProduct={addShowcaseProduct}
                  onRemoveProduct={removeShowcaseProduct}
                  popupMode={showProductPopup}
                  closeProductListPopup={closeProductListPopup}
                  isPresentation={true}
                  order={showcaseProductOrder}
                  instanceUniqueId={instanceUniqueId}
                />
              )}
            <PromoShowcaseLaunchWizard
              open={launchWizardOpen}
              onClose={() => setLaunchWizardOpen(false)}
              settings={settings}
              templates={templates}
              selectedTemplateId={selectedTemplateId}
              templateForm={templateForm}
              storeProductSummary={storeProductSummary}
              launching={launchingShowcase}
              launchVerification={launchVerification}
              connecting={connecting}
              provisioning={provisioningShowcase}
              onProvision={handleProvisionShowcase}
              onReconnect={handleReconnect}
              onOpenSettings={() =>
                history.push(`/instance/${instanceUniqueId}/settings/global-settings/promoshowcase`)
              }
              onOpenProducts={() => {
                setLaunchWizardOpen(false);
                setActiveTab(1);
              }}
              onOpenCatalogues={() => {
                setLaunchWizardOpen(false);
                setActiveTab(1);
                setShowPromoWorldPicker(true);
              }}
              onUploadLogo={uploadShowcaseLogo}
              onLaunch={launchShowcaseFromWizard}
            />
            </Box>
          </div>
        </div>
      </div>
    </div>
  );
}
